import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  untrack,
  useContext,
  type Accessor,
  type JSX,
} from 'solid-js';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type ActionCodeSettings,
  type User,
} from 'firebase/auth';
import { collection, doc, getDoc, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore';
import { onValue, ref as databaseRef, serverTimestamp, set as databaseSet } from 'firebase/database';
import { auth, firebaseConfigured, firestore, functions, realtime, restFirestore } from '../lib/firebase';
import {
  WebBluetoothControlClient,
  type BluetoothConnectionState,
  type BluetoothSetupResult,
} from '../lib/bluetooth';
import { createDispatchClient } from '../lib/dispatch';
import { isFreshHeartbeat } from '../lib/format';
import { controllerHealthLive, type ControllerHealthLive } from '../lib/controllerHealth';
import {
  autoVJLibraryFromDocuments,
  parseEffectDocument,
  type EffectDocument,
  type AutoVJLibrary,
} from '../lib/autoVJ';
import { AUTO_VJ_PACKAGE_ID, type EffectPackageParameterValue } from '../lib/effectPackages';
import { parseEffectPreset, parseEffectPresets, type EffectPreset } from '../lib/effectPresets';
import { type EffectStack } from '../lib/effectStack';
import { applyControllerIntent } from '../lib/collaboration';
import { controllerIntentSlot, maskIntentParams, rigElementCounts, type ControllerIntent } from '../lib/controllerIntent';
import {
  isRoomLinkLost, RoomLink, RoomLinkMeter, roomLinkOffered,
  type CloudControllerReceipt, type RoomLinkCollab, type RoomLinkState,
} from '../lib/roomLink';
import {
  commandApplied,
  commandFailed,
  commandSent,
  type ControlFeedback,
} from '../lib/controlFeedback';
import { useControlFeedback } from './useControlFeedback';
import {
  adoptFullState,
  normalizeFullState,
  readStateSync,
  reduceStateDelta,
  type DeltaV1,
  type StateSyncChunk,
} from '../lib/deltaState';
import {
  authoredDomainPathsForRoute,
  authoredFixtureIndexOf,
  authoredFixturePath,
  authoredPanelBaselineComplete,
  beginAuthoredEpoch as beginAuthoredTreeEpoch,
  forgetAuthoredFixture,
  reduceAuthoredFeedNode,
  type AuthoredFeed,
  type AuthoredTreeModel,
  type AuthoredTreeReduction,
} from '../lib/authoredStateTree';
import {
  loadPublishedEffectLayout,
  resolveEffectLayoutSource,
  sha256Hex,
  type EffectLayoutStatus,
  type LoadedEffectLayout,
} from '../lib/effectLayout';
import {
  rememberController,
  rememberedControllerId,
  resolveControllerForRoom,
  selectionAfterHello,
  selectionAfterPairing,
  type SelectionOutcome,
} from '../lib/controllerSelection';
import { controllersAfterRename, type RenameDeviceResult } from '../lib/deviceRename';
import { initialLanAddress, isLocalConsoleOrigin } from '../lib/consoleOrigin';
import { clearPanelHistories } from '../lib/panelHistory';
import { createResyncBudget, RESYNC_READS_PER_MINUTE } from '../lib/resyncBudget';
import {
  buildPanelModel,
  SCHEMATIC_DRAWING_SCHEMAS,
  elementAddress,
  fallbackPanelLayout,
  fixtureIntensityKeys,
  placementTargets as placementTargetsOf,
  type PanelModel,
  type PlacementTargets,
  type SchematicLayout,
} from '../lib/panelElements';
import { can } from '../lib/permissions.generated';
import { isRoomInput } from '../lib/audioInputs';
import { roomIsListening, type MusicInputMirror } from '../lib/effectSync';
import {
  ACCOUNT_CREATE_FAILURE,
  PASSWORD_RESET_FAILURE,
  plainAccountError,
  plainSignInError,
  reportError,
  VERIFICATION_SEND_FAILURE,
} from '../lib/plainErrors';
import {
  capturePendingInvite,
  clearPendingInvite,
  pendingInviteId,
  venueToSelectAfterClaim,
  type ClaimedInvite,
} from '../lib/pendingInvite';
import { mergeSceneFavorites, updateSceneFavorite } from '../lib/sceneFavorites';
import { decodeSceneLevels, parseSceneRecords, sceneSummaryOf, type SceneRecord } from '../lib/sceneLevels';
import { scoresAfterDelete, type DeleteScoreResult } from '../lib/scoreDeletion';
import {
  createTakeoverConfirmation,
  type TakeoverPrompt,
  type TakeoverTarget,
} from '../lib/takeover';
import { LanClient, type LanConnectionState, type LanNotification } from '../lib/lan';
import { expandRigTopology, hydrateCloudRigLayouts } from '../lib/rig';
import { boundRigVersion, openBoundRoomRig, type RigOpenResult, type RoomRigReply } from '../lib/roomRig';
import { applyRecordSets, effectRecordSets, newNodeInstanceId, newRecordOpId, recordBase, recordRefusalReason, rigRecordId,
  type RecordSet, type RecordWriteReply, type RigRecord, type RigRecordKind } from '../lib/records';
import type {
  AssignableRole,
  CloudRig,
  ControllerActivity,
  ControllerMirror,
  ControllerMirrorAudio,
  ControllerMirrorPatch,
  ControllerSummary,
  InviteOutcome,
  LanHello,
  LanStatus,
  LiveState,
  RemoteLivePolicy,
  TransportState,
  RigSnapshot,
  RouteName,
  SceneSummary,
  ScoreSummary,
  RoomStatus,
  RoomSummary,
  VenueRole,
  VenueInvite,
  VenueMember,
  VenueSummary,
} from '../lib/types';
import type { ListAudioInputsResult } from '../wire-corpus/generated';
import { createOutputViewSession, type OutputViewStore } from './useOutputView';
import { createSettingsTreeViewStore, type SettingsTreeViewStore } from './settingsTreeView';

interface Toast {
  id: string;
  tone: 'info' | 'success' | 'error';
  message: string;
}

export type ControlTransport = 'cloud' | 'lan' | 'bluetooth' | 'none';

export interface ControlOwnership {
  kind: 'stream' | 'remote';
  source: 'STREAM' | 'REMOTE';
}

/**
 * What a room-confirmed mutation came back with: whether anything has confirmed it yet, and when the
 * cloud stops carrying it.
 *
 * Both halves reach the screen that staged the edit. `confirmed` decides what the header says; the
 * DEADLINE is what bounds the optimistic overlay, because `confirmed: true` can be the room moving for
 * somebody else's reason and is therefore not a statement about this operator's pixels.
 */
export interface RoomConfirmation {
  confirmed: boolean;
  /**
   * When this command stops being deliverable, **on this browser's own clock** — the relay's reported
   * remaining duration added to `Date.now()` at the moment it arrived.
   *
   * Local by construction, so the screen that arms an overlay on it never compares two clocks. Nothing
   * that reaches the console from the cloud is an absolute instant any more.
   */
  expiresAt: number;
}

interface ControllerStateSyncMode {
  deviceId: string;
  mode: 'tree' | 'legacy-1.7.19';
  root: string | null;
}

interface GestureSlot {
  name: string;
  params?: Record<string, unknown>;
  /**
   * This gesture's identity WITHIN this tab — `${command}:${targetKey}`, the key of `gestureSlots`.
   *
   * Kept unhashed and separate from `controlKey` so two different controls can never share one slot:
   * the hash below is a database key with a length and alphabet to satisfy, and it is a 32-bit digest,
   * whereas local slot identity has no such constraint and no reason to accept even a remote collision.
   */
  key: string;
  /**
   * WHICH CONTROL this is, as every party outside this tab names it.
   *
   * ONE string with three consumers, deliberately not three fields that could drift: the database key of
   * the intent slot, the `coalesceKey` of every command this gesture sends, and — through that — the key
   * the Pi files its per-control high-water under when it drops an intent older than the release it has
   * already applied. If the release's `coalesceKey` did not equal the intent's slot key, that guard
   * would never match, and H15 would look closed while staying open, which is worse than not having
   * built it.
   */
  controlKey: string;
  commits: Array<{
    params: Record<string, unknown>;
    onApplied?: (result: unknown) => void;
    onRejected?: (error: unknown) => void;
    onUnanswered?: (error: unknown) => void;
  }>;
  /**
   * True while this console has an un-deleted intent slot in the database for this gesture.
   *
   * A slot is deleted on the release, so this is set for the length of a drag. It is also what lets a
   * teardown clear a slot a drag that never released left behind — a lost pointer, a closed tab.
   */
  intentLive: boolean;
  /**
   * Whether this gesture has already reported that its samples are too large for an intent slot.
   *
   * Once per gesture, not once per sample: at ten samples a second an unconditional report would bury
   * the log it is supposed to make readable.
   */
  oversizeReported: boolean;
  inFlight: boolean;
  lastSentAt: number;
  timer?: number;
}

export interface VenueContextValue {
  configured: boolean;
  localOrigin: boolean;
  authReady: Accessor<boolean>;
  user: Accessor<User | null>;
  /** True once this browser may enter the console — see the accessor's own note on unverified accounts. */
  signedIn: Accessor<boolean>;
  /** True while a signed-in account is still waiting for its address to be proven. */
  awaitingVerification: Accessor<boolean>;
  authBusy: Accessor<boolean>;
  authError: Accessor<string>;
  venues: Accessor<VenueSummary[]>;
  rooms: Accessor<RoomSummary[]>;
  selectedVenue: Accessor<VenueSummary | undefined>;
  selectedRoom: Accessor<RoomSummary | undefined>;
  selectedRigId: Accessor<string>;
  recordRigSelected: Accessor<boolean>;
  rigUnassigned: Accessor<boolean>;
  piLineSelected: Accessor<boolean>;
  microRecordWrites: Accessor<boolean>;
  boundDevice: (deviceId: string) => Record<string, any> | undefined;
  selectedController: Accessor<ControllerSummary | undefined>;
  selectedDeviceId: Accessor<string>;
  role: Accessor<VenueRole>;
  /** True for a Nuance staff operator — the only audience for internal diagnostics. */
  staff: Accessor<boolean>;
  canControl: Accessor<boolean>;
  controlAvailable: Accessor<boolean>;
  /** The route commands use right now: cloud first, then the venue network, then Bluetooth. */
  controlTransport: Accessor<ControlTransport>;
  controlsEnabled: Accessor<boolean>;
  cloudReachable: Accessor<boolean>;
  /** The arbiter category currently reaching the wire. */
  activeSource: Accessor<string>;
  /** The daemon's operator-facing name for that source, with a rollout-safe local fallback. */
  activeSourceLabel: Accessor<string>;
  controlOwnership: Accessor<ControlOwnership | undefined>;
  takeoverConfirmation: Accessor<TakeoverPrompt | undefined>;
  /** The single category-handoff rule used by Scenes, Scores and Effects. */
  confirmTakeover: (target: TakeoverTarget) => Promise<boolean>;
  resolveTakeoverConfirmation: (confirmed: boolean) => void;
  activity: Accessor<ControllerActivity | undefined>;
  controlFeedback: Accessor<ControlFeedback | undefined>;
  cloudBusy: Accessor<boolean>;
  cloudError: Accessor<string>;
  clearCloudError: () => void;
  roomStatus: Accessor<RoomStatus | undefined>;
  cloudRig: Accessor<CloudRig | undefined>;
  rigRecordState: (recordId: string) => RigRecord | undefined;
  rigAppliedRevision: (recordId: string) => number;
  effectRecord: (packageId: string) => { recordId: string; record: RigRecord } | undefined;
  writeEffectRecord: (params: Record<string, unknown>) => Promise<RecordWriteReply>;
  writeRigRecord: (kind: RigRecordKind, segments: string[], edit: {
    op: 'set' | 'replace' | 'delete'; set?: RecordSet[]; value?: Record<string, unknown>;
  }) => Promise<RecordWriteReply>;
  /** Advances after an observed active-rig change has been reloaded from the platform. */
  rigReloadRevision: Accessor<number>;
  cloudScores: Accessor<ScoreSummary[]>;
  roomDoc: Accessor<Record<string, any> | undefined>;
  /** This room's library of named effect looks, live. Every member reads it; manager+ writes it. */
  effectPresets: Accessor<EffectPreset[]>;
  /** UI projection of Auto VJ's set and active documents. */
  autoVJLibrary: Accessor<AutoVJLibrary>;
  /** One filtered collection of generic, independently revisioned documents owned by Auto VJ. */
  effectDocuments: Accessor<EffectDocument[]>;
  /** Which nodes of each tree-shaped card this viewer has open and selected, for the life of the page (B85). */
  settingsTreeView: SettingsTreeViewStore;
  deviceDoc: Accessor<Record<string, any> | undefined>;
  controllerHealthLive: Accessor<ControllerHealthLive | undefined>;
  remoteSession: Accessor<Record<string, any> | undefined>;
  /** This console's one live view, owned here so moving between screens never ends a stream. */
  outputView: OutputViewStore;
  /**
   * The session link to the open room's controller (`room-link/1`, CONTRACT-console B116), while the console
   * holds one: opened with the room on a controller that publishes the capability, closed with it.
   */
  roomLink: Accessor<RoomLink | undefined>;
  /** Whether that link can carry a command right now; the command funnel asks it once per command (B117). */
  roomLinkState: Accessor<RoomLinkState>;
  mirror: Accessor<ControllerMirror | undefined>;
  bluetoothClient: WebBluetoothControlClient;
  bluetoothState: Accessor<BluetoothConnectionState>;
  bluetoothReason: Accessor<string>;
  bluetoothIdentity: Accessor<BluetoothSetupResult | undefined>;
  lanClient: LanClient;
  lanAddress: Accessor<string>;
  lanState: Accessor<LanConnectionState>;
  lanReason: Accessor<string>;
  lanHello: Accessor<LanHello | undefined>;
  lanStatus: Accessor<LanStatus | undefined>;
  rig: Accessor<RigSnapshot | undefined>;
  /**
   * The `/3` drawing's placement → target table, always from the CLOUD projection of the room's active rig
   * version, whichever link supplies `rig` (CONTRACT-console S41). Undefined while that projection is not loaded.
   */
  placementTargets: Accessor<PlacementTargets | undefined>;
  /** One shared, published room path for every operator-facing run count and label. */
  roomLayout: Accessor<SchematicLayout | undefined>;
  roomModel: Accessor<PanelModel | undefined>;
  roomLayoutLoading: Accessor<boolean>;
  roomLayoutWarning: Accessor<string>;
  /**
   * The room's 3-D effect geometry — the ONE copy every effect panel reads. Undefined unless
   * `effectLayoutStatus()` is `'ready'`.
   */
  effectLayout: Accessor<LoadedEffectLayout | undefined>;
  effectLayoutStatus: Accessor<EffectLayoutStatus>;
  /** The raw reason behind a non-`ready` status. Staff-only: it names internals on purpose. */
  effectLayoutDetail: Accessor<string>;
  controllerState: Accessor<LiveState | undefined>;
  /**
   * Whether the controller has reported the room's panel levels IN FULL.
   *
   * An authored tree publishes one node per fixture and they arrive seconds apart, so this is true only
   * once every fixture the tree counts has arrived in the current run. A whole-state source (a direct
   * link, the legacy mirror) reports every fixture at once, so for it any reported fixture is the room.
   */
  panelBaselineComplete: Accessor<boolean>;
  /** The daemon's transport, from whichever source reported it most recently. */
  transport: Accessor<TransportState | undefined>;
  scores: Accessor<ScoreSummary[]>;
  scoreAssignments: Accessor<RoomStatus['assignments']>;
  scenes: Accessor<SceneSummary[]>;
  /**
   * The room's saved scenes as WHOLE records — what the controller recalls, not a list row.
   *
   * The cloud listener fills this for every scene in the room at once; a direct link fills it one scene
   * at a time through {@link loadSceneRecord}. Either way it is the only thing the scene preview decodes.
   */
  sceneRecords: Accessor<SceneRecord[]>;
  /**
   * Read one whole scene from the controller, for the room drawing to preview.
   *
   * Fetched on demand for the scene being drawn, never fanned out over the library, and memoised per
   * scene for as long as the room is open so a refusal cannot become a retry loop.
   */
  loadSceneRecord: (sceneId: string) => Promise<SceneRecord | undefined>;
  audioInputs: Accessor<ListAudioInputsResult>;
  /**
   * The room's committed music input (`set_music_input`), or undefined when the room has none. It is read
   * from the cloud mirror and from `get_status` alike, so a direct console — and a hosted one before the
   * mirror's first delivery — gives the same answer as a warm hosted one. Every surface that asks "does
   * this room have music" reads THIS, never a capture that happens to be running.
   */
  roomMusicInput: Accessor<MusicInputMirror | undefined>;
  /**
   * Whether the room is listening to that input. It is the room's own durable switch, not a guess from
   * telemetry: an input can be selected with the room not listening, and the room can be listening with
   * nothing arriving. Every surface that asks "can this effect follow the music" reads THIS.
   */
  roomMusicListening: Accessor<boolean>;
  /** The room input chosen on Music; shared so Audio reactive never invents a device on its own. */
  selectedAudioInputIdentity: Accessor<string>;
  selectAudioInput: (identity: string) => void;
  audioStatus: Accessor<Record<string, any> | undefined>;
  actionBusy: Accessor<string>;
  toasts: Accessor<Toast[]>;
  route: Accessor<RouteName>;
  setRoute: (route: RouteName) => void;
  signInEmail: (email: string, password: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  createAccount: (name: string, email: string, password: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  clearAuthError: () => void;
  signOut: () => Promise<void>;
  selectVenue: (venueId: string) => Promise<void>;
  selectRoom: (roomId: string, rigId?: string) => Promise<void>;
  selectController: (deviceId: string) => void;
  refreshCloud: () => Promise<void>;
  /** Re-read the room status and its version-bound artifacts without polling. */
  reloadRoomRig: () => Promise<void>;
  refreshLan: () => Promise<void>;
  refreshController: () => Promise<void>;
  connectLan: (address?: string) => Promise<void>;
  pairLan: (pin: string) => Promise<void>;
  disconnectLan: () => void;
  connectBluetooth: (setupCode: string) => Promise<void>;
  disconnectBluetooth: () => Promise<void>;
  controllerCall: <T = any>(name: string, params?: Record<string, unknown>) => Promise<T>;
  controllerRead: <T = any>(name: string, params?: Record<string, unknown>) => Promise<T>;
  sendPanelEdit: (params: Record<string, unknown>) => Promise<RoomConfirmation>;
  sendPanelGesture: (
    params: Record<string, unknown>, coalesceKey: string, final?: boolean,
    onApplied?: (result: unknown) => void, onRejected?: (error: unknown) => void,
    onUnanswered?: (error: unknown) => void,
  ) => void;
  sendBlackout: (on: boolean) => Promise<unknown>;
  controllerGesture: (
    name: string,
    params: Record<string, unknown>,
    coalesceKey: string,
    final?: boolean,
    onApplied?: (result: unknown) => void,
    onRejected?: (error: unknown) => void,
    onUnanswered?: (error: unknown) => void,
  ) => void;
  cloudCall: <T = any>(name: string, params?: Record<string, unknown>) => Promise<T>;
  renameDevice: (deviceId: string, displayName: string) => Promise<RenameDeviceResult>;
  deleteScore: (scoreId: string) => Promise<DeleteScoreResult>;
  runAction: <T>(key: string, work: () => Promise<T>, success?: string | ((result: T) => string)) => Promise<T | undefined>;
  setMaxIntensity: (value: number) => Promise<void>;
  setRemoteLivePolicy: (enabled: boolean) => Promise<void>;
  /** The venue's team, owner first. Manager+ only; the cloud refuses anyone below it. */
  listVenueMembers: () => Promise<VenueMember[]>;
  /** The addresses invited to this venue that nobody has taken up yet. Manager+ only, like the roster. */
  listVenueInvites: () => Promise<VenueInvite[]>;
  /**
   * Invite someone by email. `accepted` means they already had an account and are a member as of now;
   * `pending` means the invite is waiting for them to sign in, and they are on the team's list as such.
   */
  inviteVenueMember: (email: string, role: AssignableRole) => Promise<InviteOutcome>;
  cancelVenueInvite: (inviteId: string) => Promise<void>;
  /** Save the values on screen as a named look for this room. Manager+; the cloud refuses anyone below. */
  saveEffectPreset: (
    packageId: string,
    name: string,
    values: Record<string, EffectPackageParameterValue>,
    /**
     * The rest of what the card is showing, for the one effect whose look is more than knob positions:
     * audio reactive's whole layer stack. A package card passes neither.
     */
    audio?: { stack?: EffectStack },
  ) => Promise<EffectPreset>;
  /**
   * Read back one named look, operator+.
   *
   * It RETURNS the look; it does not send it. Where it goes is the card's decision and always has
   * been: `EffectPackagePanel` and `EffectsScreen` apply them through the same `set_effect_settings`
   * path as a slider drag, including when the effect is stopped.
   */
  applyEffectPreset: (presetId: string) => Promise<EffectPreset>;
  renameEffectPreset: (presetId: string, name: string) => Promise<EffectPreset>;
  deleteEffectPreset: (presetId: string) => Promise<void>;
  setMemberRole: (uid: string, role: AssignableRole) => Promise<void>;
  removeVenueMember: (uid: string) => Promise<void>;
  endRemoteLive: () => Promise<void>;
  takeControl: () => Promise<void>;
  dismissToast: (id: string) => void;
}

/**
 * Exported so a journey test can mount a real screen against a stated room, which is the only way to
 * assert what an operator actually sees. Application code reads it through `useVenue`.
 */
export const VenueContext = createContext<VenueContextValue>();
const dispatch = createDispatchClient(functions);
export const validRoutes: RouteName[] = ['connect', 'scenes', 'scores', 'effects', 'music', 'status', 'settings'];

/** How often the console re-reads a new account's Auth record while it waits for the address to be proven. */
const VERIFICATION_POLL_MS = 3_000;

/**
 * Where Firebase's own verification and password-reset emails send someone after the link is used.
 *
 * Firebase serves the action handler itself (`<project>.firebaseapp.com/__/auth/action`, set project-wide
 * as `notification.sendEmail.callbackUri`); this only names the `continueUrl` it hands back afterwards,
 * and its origin has to be in Auth → authorized domains or the send is refused. The console's own origin
 * is that place, so the link ends where the person already is.
 *
 * `dynamicLinkDomain` is deliberately absent — Firebase Dynamic Links shut down on 2025-08-25 and the
 * field is deprecated; its successor `linkDomain` only applies to opening a link in a native app from a
 * custom Hosting domain, which is not this flow.
 */
function emailActionSettings(): ActionCodeSettings {
  return { url: window.location.origin, handleCodeInApp: false };
}

const localOrigin = isLocalConsoleOrigin(location, import.meta.env.PROD);

function routeFromHash(): RouteName {
  const raw = location.hash.replace(/^#\/?/, '').split(/[/?]/)[0];
  if (raw === 'room' || raw === 'panel') return 'scenes';
  const route = raw as RouteName;
  return validRoutes.includes(route) ? route : 'connect';
}

function cloudFixtureRig(value?: CloudRig): RigSnapshot | undefined {
  const version = value?.rigVersion;
  if (!version) return undefined;
  return hydrateCloudRigLayouts({
    rigVersionId: value?.activeRigVersionId ?? undefined,
    fixtures: (version.devices ?? []) as RigSnapshot['fixtures'],
    groups: version.groups,
  }, version.layouts);
}

function scoreForAssignment(score: ScoreSummary, status?: RoomStatus): boolean {
  return Boolean(
    status?.assignments.some((assignment) => assignment.scoreId === score.scoreId && assignment.enabled) ||
      score.assignedTo?.some((assignment) => assignment.roomId === status?.roomId),
  );
}

/** Is this score one the room is assigned right now, by the room's own live assignment rows? */
function assignedNow(score: ScoreSummary, assignments: RoomStatus['assignments']): boolean {
  return assignments.some((assignment) => assignment.scoreId === score.scoreId && assignment.enabled);
}

/**
 * What a set of assignment rows AMOUNTS TO, for deciding whether the room's scores changed.
 *
 * Only the three fields that can change what the operator sees — which scores, which version of each,
 * and whether it is on — and sorted, because a collection snapshot's document order is the database's
 * business and not news. Everything else on the row (`updatedAt`, the fixture mapping) moves for reasons
 * that are not a score arriving, and re-asking the room for its library on each of those would be a poll
 * with extra steps.
 */
function assignmentFingerprint(assignments: RoomStatus['assignments']): string {
  return assignments
    .map((assignment) => `${assignment.scoreId}:${assignment.scoreVersionId ?? ''}:${assignment.enabled ? 1 : 0}`)
    .sort()
    .join('|');
}

/**
 * Commands whose outcome the ROOM reports, so the relay's acknowledgement is not what an operator waits
 * on and not what decides whether their edit stands.
 *
 * `set_fixture_params` is the level write. The daemon applies it in about 17 ms and publishes the result
 * to its own authored tree under `controllerAuthored/{deviceId}/panel/fixtures/*`, which the Scenes
 * screen is already subscribed to (`desiredAuthoredPaths`). The cloud relay's ack is therefore a SECOND
 * account of something this console can see for itself — and on prod, 2026-09-17, a markedly slower one:
 * the Pi applied a commit at 15:25:36.733 and the callable did not finish until 15:25:51.074, 14.4 s
 * later. The console was awaiting that callable with an 8 s budget, read its expiry as "not applied",
 * and wiped the operator's level — for a change the room had already made.
 *
 * Adding a command to this set is a claim that this console observes its WHOLE effect on the authored
 * tree. Nothing else belongs here: a read has nowhere but the relay to get its answer.
 */
const ROOM_CONFIRMED_COMMANDS = new Set(['set_fixture_params']);

// These writes are confirmed by their per-document Firestore listeners. Their relay acknowledgement may
// carry a result for other clients, but this console neither requires nor interprets that body.
const OPTIONAL_MUTATION_RESULT_COMMANDS = new Set([
  'set_effect_document',
  'delete_effect_document',
]);

/**
 * How long the relay may wait for the room's answer before it gives up.
 *
 * For a READ this budget is the whole story: its expiry is a real failure, because there is no other
 * source for the answer.
 *
 * For a room-confirmed WRITE it governs nothing an operator sees, and it deliberately stays where it is
 * rather than being stretched. Stretching it cannot work — the callable's schema caps `waitTimeoutMs` at
 * 12 s (functions/src/commands/control.ts:305) and prod's slow ack took 14.4 s, so no LEGAL budget could
 * have covered it — and it is not free, because the same ceiling applies to the editing samples a scrub
 * sends every 100 ms, each of which holds a callable for its whole duration. What the budget still buys
 * is the fast case: a refusal, which the daemon issues in milliseconds, always arrives well inside it.
 */
const READ_RESULT_WAIT_MS = 8_000;

/**
 * How long this console keeps an optimistic overlay for a command the cloud never reported on.
 *
 * The relay stamps and clamps every command's real lifetime from SERVER time and returns it as a
 * DURATION (`CloudControllerReceipt.expiresInMs`), which is the deadline anything on screen keys on. The
 * console sends no deadline of its own and asks for nothing — a client-stated lifetime is a client clock
 * entering the system, and there is now no path by which one can.
 *
 * This is the fallback for the one case where no duration comes back at all: the relay gave up, so there
 * is nothing to adopt. It is the longest lifetime any command class has, because over-holding an overlay
 * for a change the room has probably made is the safe direction and under-holding it is the bug this
 * whole bound exists to fix.
 */
export const UNREPORTED_COMMAND_LIFETIME_MS = 30_000;

/**
 * FNV-1a over a string, in base 36 — a short, stable, filename-safe digest of arbitrary text.
 *
 * Used for two different keys that must both be derived rather than sanitised: the relay's coalescing
 * key, and the database key of an intent slot (which the rules constrain to `[A-Za-z0-9_-]{1,120}`,
 * while the gesture's own target strings contain colons and whatever a screen puts in a fingerprint).
 * Hashing is the right answer for both, because a hand-written character filter silently collides two
 * different targets onto one key the first time a screen invents a new target vocabulary.
 */
function fnv1a36(text: string): string {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

/**
 * What a failed relay call actually says — in the only two terms that differ in consequence.
 *
 * - `refused` — **a verdict about the request.** The room's own No (`failed-precondition` carrying
 *   `details.controllerCode`), and equally the relay's own No: a full command queue, an expired command,
 *   an unauthenticated caller. In every one of these the command did NOT reach the room, so the
 *   operator's edit comes off the screen and they are told.
 * - `unreported` — `deadline-exceeded`. The relay ran out of budget waiting for an ack. It has told us
 *   about the cloud and **nothing whatever about the room**; on prod that hop took 9–18 s for commands
 *   the Pi had applied in ~17 ms.
 *
 * Only `refused` may revert an edit or accuse the room of anything. `unreported` is a statement about
 * the CLOUD, and the command may well have landed — which is why it is not allowed to take back a change
 * the room actually made. What bounds an overlay it leaves standing is the command's `expiresAt`, not
 * this verdict.
 *
 * There used to be a third, `patch-lost` (`data-loss`): the room proved a mutation with a revision while
 * the cloud failed to carry the canonical patch that went with it. The Pi is the sole writer of the
 * authored tree now and the ack carries no patch at all, so there is nothing left for the cloud to lose
 * and the relay no longer raises that code. Reading a code the relay cannot send is not defence, it is a
 * branch no test can reach honestly.
 *
 * The two are told apart by CODE and never by message prose (`functions/src/registry/errors.ts`;
 * `docs/CONTRACT-controller-command-relay.md` B8). The absence of `details.controllerCode` was never a
 * sufficient discriminator on its own: a queue-backlog refusal carries none either, so a console keying
 * on its absence would read "the queue is full" as "the cloud was merely slow" and quietly drop it.
 */
export type RelayVerdict = 'refused' | 'unreported';

export function relayVerdict(error: unknown): RelayVerdict {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  if (code === 'functions/deadline-exceeded' || code === 'deadline-exceeded') return 'unreported';
  return 'refused';
}

export function VenueProvider(props: { children: JSX.Element }): JSX.Element {
  const [authReady, setAuthReady] = createSignal(false);
  const [user, setUser] = createSignal<User | null>(null);
  /**
   * Whether the signed-in account's address has been proven, as its own signal rather than read off
   * `user()`. A `User` is a mutable object Firebase updates in place, so `reload()` flipping
   * `emailVerified` changes nothing any screen is watching; this signal is what actually re-renders.
   */
  const [emailVerified, setEmailVerified] = createSignal(false);
  const [authBusy, setAuthBusy] = createSignal(false);
  const [authError, setAuthError] = createSignal('');
  /** Held while `createAccount` is naming a brand-new account — see `finishSignIn`. */
  let creatingAccount = false;
  // An `/invite/accept?inviteId=…` link is remembered and the address bar is put back on the console
  // root, so the hash router sees the URL it expects and a reload does not re-enter a path no screen
  // serves. The invite itself is consumed after a verified sign-in, by `claimInvites`.
  capturePendingInvite();
  const [staff, setStaff] = createSignal(false);
  // Which STAFF tier, if any. `admin` is the super admin (reaches every venue, changes anything);
  // `support` reads every venue and changes nothing. They used to be one thing, which made the console
  // show a support operator every control the cloud would refuse.
  const [staffTier, setStaffTier] = createSignal<'admin' | 'support' | undefined>();
  const [venues, setVenues] = createSignal<VenueSummary[]>([]);
  const [rooms, setRooms] = createSignal<RoomSummary[]>([]);
  const [roomsLoadedVenueId, setRoomsLoadedVenueId] = createSignal('');
  const [boundDevices, setBoundDevices] = createSignal<Record<string, Record<string, any>>>({});
  const [selectedVenueId, setSelectedVenueId] = createSignal(localStorage.getItem('nuance-venue:venue') ?? '');
  const [selectedRoomId, setSelectedRoomId] = createSignal(localStorage.getItem('nuance-venue:room') ?? '');
  const [selectedRigId, setSelectedRigId] = createSignal(localStorage.getItem(`nuance-venue:rig:${selectedRoomId()}`) ?? '');
  const [selectedDeviceId, setSelectedDeviceId] = createSignal(
    rememberedControllerId(selectedVenueId(), selectedRoomId()),
  );
  const [cloudBusy, setCloudBusy] = createSignal(false);
  const [cloudError, setCloudError] = createSignal('');
  const [cloudReachable, setCloudReachable] = createSignal(navigator.onLine);
  const [roomStatus, setRoomStatus] = createSignal<RoomStatus>();
  const [cloudRig, setCloudRig] = createSignal<CloudRig>();
  const [rigRecords, setRigRecords] = createSignal<Record<string, RigRecord>>({});
  const [rigApplied, setRigApplied] = createSignal<Record<string, number>>({});
  const [recordOverlays, setRecordOverlays] = createSignal<Record<string, {
    recordId: string; kind: RigRecordKind; segments: string[]; op: 'set' | 'replace' | 'delete'; set?: RecordSet[];
    value?: Record<string, unknown>; revision?: number; unitKeys?: string[]; applied?: boolean;
  }>>({});
  const recordWriteTails = new Map<string, Promise<void>>();
  const [rigReloadRevision, setRigReloadRevision] = createSignal(0);
  const [cloudScores, setCloudScores] = createSignal<ScoreSummary[]>([]);
  /**
   * The room's assignment rows as they stand RIGHT NOW, when something is watching them.
   *
   * `undefined` is "nobody is watching" — a cable console, or the moment before the listener's first
   * delivery — and is the only value that leaves the room's score list to the one-shot reads. An EMPTY
   * array is a real answer and a different fact: this room has no scores, so the list is empty.
   */
  const [liveAssignments, setLiveAssignments] = createSignal<RoomStatus['assignments']>();
  const [roomDoc, setRoomDoc] = createSignal<Record<string, any>>();
  const [effectPresets, setEffectPresets] = createSignal<EffectPreset[]>([]);
  const settingsTreeView = createSettingsTreeViewStore();
  const [effectDocuments, setEffectDocuments] = createSignal<EffectDocument[]>([]);
  const autoVJLibrary = createMemo<AutoVJLibrary>(() => (
    autoVJLibraryFromDocuments(effectDocuments())
  ));
  const [deviceDoc, setDeviceDoc] = createSignal<Record<string, any>>();
  const [healthLive, setHealthLive] = createSignal<ControllerHealthLive>();
  const [remoteSession, setRemoteSession] = createSignal<Record<string, any>>();
  const [mirror, setMirror] = createSignal<ControllerMirror>();
  /** The session link to the open room's controller (B116); see `openRoomLinkFor`. */
  const [roomLink, setRoomLink] = createSignal<RoomLink>();
  const [roomLinkState, setRoomLinkState] = createSignal<RoomLinkState>('closed');
  /** The per-command measures (S29), across links and the queue, for the life of the provider. */
  const roomLinkMeter = new RoomLinkMeter();

  /**
   * Tree-mode subscriptions arrive one producer-owned domain at a time. Keep that construction detail
   * here; the public mirror type remains the exact generated document and every field name is still
   * checked against it.
   */
  const mergeMirrorPatch = (
    current: ControllerMirror | undefined,
    patch: ControllerMirrorPatch,
  ): ControllerMirror => ({ ...(current ?? {}), ...patch }) as ControllerMirror;
  const [bluetoothState, setBluetoothState] = createSignal<BluetoothConnectionState>('idle');
  const [bluetoothReason, setBluetoothReason] = createSignal('');
  const [bluetoothIdentity, setBluetoothIdentity] = createSignal<BluetoothSetupResult>();
  const queryAddress = new URLSearchParams(location.search).get('lan');
  const defaultAddress = initialLanAddress(location, localOrigin, queryAddress, localStorage.getItem('nuance-venue:lan-address'));
  const [lanAddress, setLanAddress] = createSignal(defaultAddress);
  const [lanState, setLanState] = createSignal<LanConnectionState>('idle');
  const [lanReason, setLanReason] = createSignal('');
  const [lanHello, setLanHello] = createSignal<LanHello>();
  const [lanStatus, setLanStatus] = createSignal<LanStatus>();
  const [lanRig, setLanRig] = createSignal<RigSnapshot>();
  const [controllerState, setControllerState] = createSignal<LiveState>();
  /** `authoredPanelBaselineComplete` of the tree this console follows; undefined when it follows none. */
  const [authoredPanelComplete, setAuthoredPanelComplete] = createSignal<boolean>();
  /**
   * Transport lives in its OWN signal, not only inside `controllerState`.
   *
   * Every transport update used to be written as `setControllerState(current => current ? … : current)`,
   * which silently dropped the update whenever the cloud origin had no full state snapshot yet. That is
   * the measured bug where Play was delivered and executed by the Pi in ~0.3 s while the button kept
   * saying "stopped" and the operator kept pressing it. A command result, a playhead tick or a full
   * snapshot can each seed this on its own, so the button reflects the daemon as soon as anything says so.
   */
  const [transportState, setTransportState] = createSignal<TransportState>();

  const [lanScores, setLanScores] = createSignal<ScoreSummary[]>([]);
  const [scenes, setScenes] = createSignal<SceneSummary[]>([]);
  const [sceneRecords, setSceneRecords] = createSignal<SceneRecord[]>([]);
  const [audioInputs, setAudioInputs] = createSignal<ListAudioInputsResult>({ inputs: [] });
  const [selectedAudioInputIdentity, setSelectedAudioInputIdentity] = createSignal('');
  /** The operator's own pick while `set_music_input` is still in flight; '' once the room agrees. */
  const [pendingAudioInput, setPendingAudioInput] = createSignal('');
  const [audioStatus, setAudioStatus] = createSignal<Record<string, any>>();
  const [roomLayout, setRoomLayout] = createSignal<SchematicLayout>();
  const [roomLayoutLoading, setRoomLayoutLoading] = createSignal(false);
  const [roomLayoutWarning, setRoomLayoutWarning] = createSignal('');
  const [effectLayout, setEffectLayout] = createSignal<LoadedEffectLayout>();
  const [effectLayoutStatus, setEffectLayoutStatus] = createSignal<EffectLayoutStatus>('loading');
  const [effectLayoutDetail, setEffectLayoutDetail] = createSignal('');
  const [actionBusy, setActionBusy] = createSignal('');
  const {
    feedback: controlFeedback,
    show: showControlFeedback,
    clear: clearControlFeedback,
  } = useControlFeedback();
  const [takeoverConfirmation, setTakeoverConfirmation] = createSignal<TakeoverPrompt>();
  const [toasts, setToasts] = createSignal<Toast[]>([]);
  const [route, setRouteSignal] = createSignal<RouteName>(routeFromHash());
  const lanClient = new LanClient();
  const bluetoothClient = new WebBluetoothControlClient();
  const gestureSlots = new Map<string, GestureSlot>();
  const activeActionKeys = new Set<string>();
  let detailUnsubscribers: Unsubscribe[] = [];
  const authoredUnsubscribers = new Map<string, Unsubscribe>();
  let musicTelemetryUnsubscribe: Unsubscribe | undefined;
  let authoredDeviceId = '';
  let authoredModel: AuthoredTreeModel = { mirror: {}, revisions: {} };
  let cloudStateMode: ControllerStateSyncMode['mode'] | undefined;
  let stateSyncGeneration = 0;
  let stateSyncPendingGeneration: number | undefined;
  let legacyJoinDeviceId = '';
  /** One in-flight-or-settled `get_scene` per scene, for as long as this room is open. */
  const sceneRecordRequests = new Map<string, Promise<SceneRecord | undefined>>();
  /** `venueId:roomId` of the room whose live scene listener is attached, or '' when none is. */
  let sceneLibraryRoom = '';
  /** The (venue, room, device) the live subscriptions are currently attached to. */
  let subscribedIdentity = '';
  let firstRigReload: Promise<void> | undefined;
  let connectivityUnsubscribe: Unsubscribe | undefined;
  let controllerRefresh: Promise<void> | undefined;
  let controllerRefreshDevice = '';

  function watchCloudConnectivity(): void {
    if (connectivityUnsubscribe) return;
    connectivityUnsubscribe = onValue(
      databaseRef(realtime, '.info/connected'),
      (snapshot) => setCloudReachable(snapshot.val() === true),
      () => setCloudReachable(false),
    );
  }

  /**
   * The one place a live subscription's failure goes.
   *
   * A subscription that fails and is reset to its empty value is indistinguishable from a room that is
   * genuinely empty — and the failure that does this is not exotic. Firestore rules are proofs over the
   * QUERY, not filters over the rows: a read the rules cannot prove is refused whole, for every caller
   * the clause does not reach. Platform staff pass that clause on sight, so a listener a manager can
   * never read looks perfect to whoever wrote it. Discarding the error made the console say the room has
   * no controller.
   *
   * So the error is reported and the operator is told, in the plain sentence the surface already owns —
   * a refusal reads as “You do not have permission to do that.” A reachability failure keeps its
   * existing treatment instead: the top bar already says the internet is gone, and a second notice
   * saying it again is noise.
   */
  function subscriptionFailed(context: string, error: unknown): void {
    const plain = reportError(context, error);
    if (/network|unavailable|offline/i.test(plain.detail)) {
      setCloudReachable(false);
      return;
    }
    setCloudError(plain.message);
  }

  function selectAudioInput(identity: string): void {
    setPendingAudioInput(identity);
    setSelectedAudioInputIdentity(identity);
  }

  function resetAudioRoomState(): void {
    setPendingAudioInput('');
    setSelectedAudioInputIdentity('');
    setAudioInputs({ inputs: [] });
    setAudioStatus(undefined);
    setMirror(undefined);
  }

  const roomMusicInput = createMemo<MusicInputMirror | undefined>(() => {
    const committed = mirror()?.musicInput ?? lanStatus()?.musicInput;
    return committed?.selected ? committed : undefined;
  });
  const roomMusicListening = createMemo(() => roomIsListening(roomMusicInput()));

  // The picker shows the ROOM's committed input and nothing else. A capture that happens to be running
  // is not a choice anybody made: seeding from `audioMonitor`/`effect.audio` showed an input as picked
  // while `set_music_input` had never been sent, so the picker named an input while every effect read
  // the room as having none. The operator's own pick stands optimistically until the room reports it.
  createEffect(() => {
    const committed = String(roomMusicInput()?.id ?? '');
    if (pendingAudioInput()) {
      // Matched, not compared: the controller answers a synthetic pick with its canonical id, so an
      // equality test would leave the pick pending forever and the picker would stop following the room.
      if (isRoomInput(pendingAudioInput(), committed)) setPendingAudioInput('');
      return;
    }
    const entry = (audioInputs().inputs ?? [])
      .filter((input) => input.available !== false && input.kind !== 'file')
      .map((input) => String(input.identity ?? ''))
      .find((identity) => identity && isRoomInput(identity, committed));
    // An unlisted committed input is still the room's input, and the picker carries an entry for it
    // (`audioInputOptions`). Resolving to nothing here is how a room that IS listening showed a picker
    // sitting on its placeholder: controller 1.7.15 lists no inputs at all while its capture is running.
    setSelectedAudioInputIdentity(entry ?? committed);
  });
  let controlEpoch = 0;
  let lastMirrorCommandId = '';
  /**
   * Whether the room's standing `lastCommand` has already been handed to this subscription.
   *
   * A Realtime Database listener is given whatever is already at its path the moment it attaches, so the
   * FIRST delivery is the room's history rather than something that just happened — on prod it was an
   * effect-parameter tweak from minutes earlier, replayed verbatim to every console that signed in.
   * Counting it as a command issued NOW is what threw away the one `get_status` this page load asks for
   * and left the Status screen on its reduced notice for the life of the tab.
   */
  let mirrorCommandSeeded = false;
  /**
   * The same trap, one listener over: a Firestore collection listener's first snapshot is what is
   * already there, so the room's standing assignments are not a score that just arrived and must not
   * set off the re-read that one does. They are still ADOPTED on that first delivery — a standing value
   * is a perfectly good value — it is only the re-read that waits for a real change.
   */
  let assignmentsSeeded = false;
  /** What the room's assignments amounted to when this console last re-read its scores for them. */
  let readScoresForAssignments = '';
  let controllerHandshake: Promise<void> | undefined;
  let presenceDeviceId = '';
  let presenceTimer: number | undefined;
  let lastMirrorActivityId = '';
  let roomLayoutRequest = 0;
  let loadedRoomLayoutKey = '';
  let effectLayoutRequest = 0;
  let loadedEffectLayoutKey = '';
  let retriedEffectLayoutKey = '';
  let observedRoomRigVersion = '';
  let observedControllerRigVersion = '';
  let rigReload: Promise<void> | undefined;
  let announceRigReload = false;
  let takeoverResolver: ((confirmed: boolean) => void) | undefined;

  const selectedVenue = createMemo<VenueSummary | undefined>(() => venues().find((venue) => venue.venueId === selectedVenueId()) ?? (
    localOrigin && lanHello()?.venueId ? {
      venueId: lanHello()!.venueId!,
      displayName: 'Local venue',
      role: lanHello()?.role === 'owner' ? 'owner' : lanHello()?.role === 'manager' ? 'manager' : 'operator',
    } : undefined
  ));
  const selectedRoom = createMemo<RoomSummary | undefined>(() => rooms().find((room) => room.roomId === selectedRoomId()) ?? (
    localOrigin && lanHello()?.roomId ? {
      roomId: lanHello()!.roomId!,
      displayName: lanHello()?.roomName || 'Local room',
      rigs: [],
    } : undefined
  ));
  const selectedBinding = createMemo(() => selectedRoom()?.rigs?.find((entry) => entry.rigId === selectedRigId()));
  const rigUnassigned = createMemo(() => selectedBinding()?.binding?.state === 'unassigned');
  const recordRigSelected = createMemo(() => selectedBinding()?.binding?.state === 'bound');
  const boundDevice = (deviceId: string): Record<string, any> | undefined =>
    (deviceDoc()?.id === deviceId ? deviceDoc() : undefined) ?? boundDevices()[deviceId];
  const boundDeviceClass = createMemo(() => boundDevice(selectedBinding()?.controllerId ?? '')?.deviceClass);
  const piLineSelected = createMemo(() => recordRigSelected()
    && selectedBinding()?.controllerId === selectedDeviceId() && boundDeviceClass() === 'controller');
  const microRecordWrites = createMemo(() => recordRigSelected()
    && roomsLoadedVenueId() === selectedVenueId()
    && selectedBinding()?.controllerId === selectedDeviceId() && boundDeviceClass() === 'sculpture');
  const cloudBindingReady = createMemo(() => {
    if (!user() || !selectedVenueId() || roomsLoadedVenueId() !== selectedVenueId()) return false;
    const room = rooms().find((entry) => entry.roomId === selectedRoomId());
    if (!room || !Array.isArray(room.rigs)) return false;
    if (room.rigs.length === 0) return false;
    const binding = selectedBinding();
    return Boolean(binding?.binding?.state === 'bound' && binding.controllerId
      && binding.controllerId === selectedDeviceId() && (piLineSelected() || microRecordWrites()));
  });
  const directControllerReady = createMemo(() => {
    const deviceId = selectedDeviceId();
    // The handshake must identify the selected controller before its Pi commands can be sent.
    const proven = (lanState() === 'connected' && lanHello()?.authenticated === true
      && Boolean(lanHello()?.deviceId) && (!deviceId || lanHello()?.deviceId === deviceId))
      || (bluetoothState() === 'connected' && Boolean(bluetoothIdentity()?.deviceId)
        && bluetoothIdentity()?.deviceId === deviceId);
    if (!proven) return false;
    if (roomsLoadedVenueId() !== selectedVenueId() || !selectedVenueId()) return true;
    const room = rooms().find((entry) => entry.roomId === selectedRoomId());
    if (!room || !Array.isArray(room.rigs)) return false;
    if (room.rigs.length === 0) return false;
    return piLineSelected();
  });
  // A proven local controller stays usable when the account's cloud room read is unavailable.
  const boundClassReady = createMemo(() => cloudBindingReady() || directControllerReady());
  const piCloudReady = createMemo(() => cloudBindingReady() && Boolean(selectedBinding()) && piLineSelected());
  const piControllerReady = createMemo(() => directControllerReady() || piCloudReady());
  /**
   * The controller the header names — and it is always the one commands are addressed to.
   *
   * A direct connection stands in for the cloud's device list when there is none, but only for the
   * controller actually selected: a `hello` that names some other controller is not allowed to select
   * one, so it must not be allowed to name one either, or the header would credit a controller the
   * console is not driving.
   */
  const selectedController = createMemo<ControllerSummary | undefined>(() => {
    const deviceId = selectedDeviceId();
    const listed = roomStatus()?.devices.find((device) => device.deviceId === deviceId);
    if (listed) return listed;
    const binding = roomsLoadedVenueId() === selectedVenueId() ? selectedBinding() : undefined;
    if (binding?.binding?.state === 'bound' && binding.controllerId === deviceId) {
      const bound = boundDevice(deviceId);
      if (bound) return { deviceId, displayName: String(bound.displayName ?? bound.name ?? deviceId),
        status: String(bound.status ?? 'offline') };
    }
    const hello = lanHello();
    if (hello?.deviceId && (!deviceId || hello.deviceId === deviceId)) {
      return {
        deviceId: hello.deviceId,
        displayName: hello.deviceName,
        status: lanState() === 'connected' ? 'online' : 'offline',
      };
    }
    const identity = bluetoothIdentity();
    if (identity?.deviceId && (!deviceId || identity.deviceId === deviceId)) {
      return {
        deviceId: identity.deviceId,
        displayName: identity.deviceName,
        status: bluetoothState() === 'connected' ? 'online' : 'offline',
      };
    }
    return undefined;
  });
  /**
   * Whether this browser is through the door.
   *
   * An account with an unproven address is signed in as far as Firebase is concerned, which is not the
   * same thing as being allowed into the room: the platform will not hand it an invite, a claim or a
   * membership until the address is verified. The console holds it at the sign-in card so what it shows
   * and what the cloud will do agree. A console served by the controller over a cable has no account at
   * all and is admitted by the pairing it already did.
   */
  const signedIn = createMemo(() => localOrigin || (user() != null && emailVerified()));
  /** The one state a brand-new account sits in: created, signed in, waiting on the link in its inbox. */
  const awaitingVerification = createMemo(() => !localOrigin && user() != null && !emailVerified());

  const role = createMemo<VenueRole>(() => {
    if (staffTier() === 'admin') return 'staff';
    if (staffTier() === 'support') return 'support';
    if (user()) return selectedVenue()?.role ?? 'operator';
    // A direct LAN or Bluetooth connection proves a paired DEVICE, not a venue membership — the daemon
    // itself refuses a bare paired credential every manager-tier command. So the console claims exactly
    // what such a connection can actually do (run the room) instead of assuming manager and offering
    // controls the controller will refuse. Being in the room is its own authority for the setup surfaces,
    // and those gate on the direct connection rather than on this role.
    return 'operator';
  });
  const canControl = createMemo(() => !rigUnassigned()
    && (can(role(), 'scenes.manage') || (localOrigin && lanHello()?.authenticated === true)));
  /**
   * The cloud's own word on whether the selected controller is still there, or nothing if it has none.
   *
   * The same reading the Connect and Status screens already render — `online` AND a heartbeat inside
   * `isFreshHeartbeat`'s window — because the screen saying a controller is offline while the console
   * goes on addressing it is the two disagreeing about the same fact.
   *
   * Deliberately three-valued. `undefined` is "the mirror has not said", which is every console in the
   * moments after a room is selected and every controller too old to publish a heartbeat at all; those
   * reach the cloud exactly as they always did. It is also deliberately not on a timer: the answer moves
   * when the mirror does. A room that dies mid-session leaves its last delivery standing and that
   * dispatch still waits out the relay, which is the honest cost of not running a clock whose every tick
   * could refuse a command to a healthy room over one slow heartbeat write. What it does catch is the
   * case a person actually meets — opening the console on a room that is already gone, where `onValue`
   * delivers the stale heartbeat sitting at the path (H18) and the answer is known before anything is
   * sent.
   */
  const mirrorOnline = createMemo<boolean | undefined>(() => {
    const heartbeat = mirror()?.heartbeat;
    if (!heartbeat) return undefined;
    if (heartbeat.online !== true) return false;
    // A beat that names no time cannot be aged, and a missing timestamp is not evidence the controller
    // is gone — a 1.7.19-shaped mirror publishes exactly that. The cloud's word is then all there is.
    if (!heartbeat.lastSeenAt) return undefined;
    return isFreshHeartbeat(heartbeat.lastSeenAt);
  });
  /**
   * Which wire this console's commands take — and a connection the operator MADE outranks the cloud.
   *
   * The old order asked `cloudReachable()` first, which is whether THIS BROWSER can reach Firebase and
   * says nothing whatever about the controller. On the prod Pi on 2026-09-18 that inverted the whole
   * feature: the Mac had internet, the Pi had none, the operator pressed "Connect over Bluetooth", the
   * console told him he was connected — and "set an effect" went to the cloud and timed out against a
   * controller that was never going to hear it. The Pi's own log proves the absence: a healthy BLE
   * session at 03:47:58Z and not one command over it. Bluetooth exists for exactly that outage — the
   * common one is the CONTROLLER losing its network, not the laptop — so the console may not quietly
   * route around the link a person walked into the room to establish.
   *
   * A direct connection is never incidental: the cable console pairs on its own origin, and the only
   * automatic venue-network reconnect there is fires solely while the cloud is unreachable. So "a direct
   * link is up" and "the operator chose this route" are the same fact, and it wins.
   *
   * The cloud is what is left, and it is refused outright for a controller the mirror says is gone: an
   * operator is told to choose an online controller in the same breath rather than after an 18-second
   * relay deadline. NOTE the limit of that clause today — `runHeartbeat` only ever writes `online: true`
   * and there is no disconnect writer, so a dead Pi's heartbeat goes STALE rather than false and this
   * will not fire for it. It is a floor the controller has to grow into (an `onDisconnect` offline
   * write); what carries the fix above is the rule that an explicit connection outranks the cloud.
   */
  const controlTransport = createMemo<ControlTransport>(() => {
    const direct = directTransport();
    if (direct && boundClassReady()) return direct;
    if (cloudBindingReady() && selectedDeviceId() && cloudReachable() && mirrorOnline() !== false) return 'cloud';
    return 'none';
  });
  /**
   * The tree's own completeness decides only while the cloud carries the room. A direct link answers
   * `get_state` with the whole room at once, and its console may follow a tree that never arrives at all
   * (a controller on the venue network with no internet), so there any reported fixture is the room.
   */
  const panelBaselineComplete = createMemo(() => {
    const tree = controlTransport() === 'cloud' ? authoredPanelComplete() : undefined;
    return tree ?? Boolean(controllerState()?.fixtures?.length);
  });
  const controlAvailable = createMemo(() => canControl()
    && (microRecordWrites() ? cloudBindingReady() && cloudReachable()
      : piControllerReady() && controlTransport() !== 'none'));
  /** Prefer the dedicated signal; fall back to whatever a snapshot happens to carry. */
  const transport = createMemo<TransportState | undefined>(() => transportState() ?? controllerState()?.transport ?? (lanStatus()?.transport as TransportState | undefined));

  const activeSource = createMemo(() => {
    const direct = controlTransport() === 'lan' || controlTransport() === 'bluetooth';
    return String(
      (direct
        ? controllerState()?.activeSource ?? mirror()?.activeSource
        : mirror()?.activeSource ?? controllerState()?.activeSource)
        ?? lanStatus()?.mode
        ?? 'panel',
    ).trim().toLowerCase() || 'panel';
  });

  const activeSourceLabel = createMemo(() => {
    const source = activeSource();
    // A controller may still be running a composition it was given before the console dropped that
    // page. The operator has no such word any more, so this one source is named in theirs and the
    // daemon's own published label — "Composition: …" — is deliberately not used for it.
    if (source === 'composition') return 'a saved look';

    const direct = controlTransport() === 'lan' || controlTransport() === 'bluetooth';
    const published = direct
      ? controllerState()?.activeSourceLabel ?? mirror()?.activeSourceLabel
      : mirror()?.activeSourceLabel ?? controllerState()?.activeSourceLabel;
    if (typeof published === 'string' && published.trim()) return published.trim();

    if (source === 'panel' || source === 'idle' || source === 'held') return 'Scenes';
    if (source === 'score') {
      const name = String(transport()?.scoreName ?? '').trim();
      return name ? `Score: ${name}` : 'Score';
    }
    if (source === 'effect') {
      const effect = mirror()?.effect?.running
        ? mirror()?.effect
        : controllerState()?.effects?.find((item) => item.active !== false);
      const kind = String(effect?.kind ?? '').toLowerCase();
      if (kind === 'audio_reactive') return 'Effect: Audio reactive';
      // Every other room effect is a package, and it carries its own name — so the prompt names whatever
      // is running without the console holding a table of effects it would have to keep in step.
      const named = String((effect as { package?: { name?: string } } | undefined)?.package?.name ?? '').trim();
      return named ? `Effect: ${named}` : 'Effect';
    }
    if (source === 'stream' || source === 'remote' || source === 'remote_live' || source === 'remote-live') {
      return 'Nuance stream';
    }
    return 'the current look';
  });

  function requestTakeoverConfirmation(prompt: TakeoverPrompt): Promise<boolean> {
    takeoverResolver?.(false);
    setTakeoverConfirmation(prompt);
    return new Promise<boolean>((resolve) => {
      takeoverResolver = resolve;
    });
  }

  function resolveTakeoverConfirmation(confirmed: boolean): void {
    const resolve = takeoverResolver;
    takeoverResolver = undefined;
    setTakeoverConfirmation(undefined);
    resolve?.(confirmed);
  }

  const confirmTakeover = createTakeoverConfirmation(
    () => ({ source: activeSource(), label: activeSourceLabel() }),
    requestTakeoverConfirmation,
  );

  /**
   * The ARBITER'S SOURCE rule, and only that.
   *
   * There is no control holder any more: ordinary control is last-write-wins, so nothing here reports
   * "another operator has it". What DOES still own the room exclusively is the arbiter's active source
   * — a LAN stream from Nuance, or a remote-live session — and while one of those is driving, panel and
   * score changes do not reach the wire until a device explicitly takes over.
  */
  const controlOwnership = createMemo<ControlOwnership | undefined>(() => {
    const source = activeSource();
    if (!['stream', 'remote', 'remote_live', 'remote-live'].includes(source)) return undefined;
    const remote = controllerState()?.remoteLive ?? lanStatus()?.remoteLive ?? remoteSession();
    const remoteState = String(remote?.state ?? '').toUpperCase();
    const label = activeSourceLabel();
    if (source !== 'stream' || /^Remote session\b/i.test(label) || remote?.active === true || ['AUTHORIZED', 'ACTIVE', 'DEGRADED_HOLD', 'PAUSED_HOLD'].includes(remoteState)) {
      return { kind: 'remote', source: 'REMOTE' };
    }
    return { kind: 'stream', source: 'STREAM' };
  });
  // Panel edits are intentionally enabled while another category owns the wire: the daemon stages them
  // until an explicit takeover. Category-changing actions use confirmTakeover above.
  const controlsEnabled = createMemo(() => controlAvailable());
  const activity = createMemo(() => mirror()?.activity);
  const cloudProjection = createMemo(() => cloudFixtureRig(cloudRig()));
  // The direct link supplies the rig's live shape, but its compact `get_rig` carries no placement uids. The
  // drawing and its join table both come from `cloudRig`, so they always describe the same rig version.
  const rig = createMemo(() => lanRig() ?? cloudProjection());
  const placementTargets = createMemo(() => placementTargetsOf(cloudProjection()));
  function rigRecordState(recordId: string): RigRecord | undefined {
    const saved = rigRecords()[recordId];
    const overlays = Object.values(recordOverlays()).filter((entry) => entry.recordId === recordId
      && (entry.revision === undefined || (saved?.revision ?? 0) < entry.revision
        || (!entry.applied && Boolean(selectedDeviceId()) && (rigApplied()[recordId] ?? 0) < entry.revision)));
    if (!saved && !overlays.length) return undefined;
    let state = saved?.state ?? {};
    for (const overlay of overlays) {
      if (overlay.op === 'delete') return undefined;
      state = overlay.op === 'replace' ? overlay.value ?? {} : applyRecordSets(state, overlay.set ?? []);
    }
    const units = { ...saved?.units };
    for (const overlay of overlays) {
      if (overlay.revision === undefined) continue;
      for (const key of overlay.unitKeys ?? []) units[key] = { revision: overlay.revision };
    }
    return { ...(saved ?? { kind: overlays[0].kind, segments: overlays[0].segments, revision: 0, units: {} }),
      revision: Math.max(saved?.revision ?? 0, ...overlays.map((overlay) => overlay.revision ?? 0)),
      units, state };
  }
  function rigAppliedRevision(recordId: string): number {
    return rigApplied()[recordId] ?? 0;
  }
  const microSceneRecords = createMemo<SceneRecord[]>(() => {
    const ids = new Set([...Object.keys(rigRecords()),
      ...Object.values(recordOverlays()).map((entry) => entry.recordId)]);
    // Micro scene records have no createdAt; while edits are hidden, their write time is creation order.
    const entries = [...ids].flatMap((recordId, order) => {
      const record = rigRecordState(recordId);
      if (!record || record.deletedAt || record.kind !== 'scene' || !Array.isArray(record.state.fixtures)) return [];
      const sceneId = record.segments[0];
      if (!sceneId) return [];
      return [{ record, sceneId, order, createdOrder: record.updatedAt ?? '' }];
    });
    entries.sort((a, b) => (a.createdOrder || '\uffff').localeCompare(b.createdOrder || '\uffff') || a.order - b.order);
    return entries.map(({ record, sceneId }, index) => ({ sceneId,
      name: typeof record.state.name === 'string' && record.state.name.trim()
        ? record.state.name.trim() : `Scene ${index + 1}`,
      fixtures: record.state.fixtures, revision: record.revision }));
  });
  const visibleSceneRecords = createMemo(() => microRecordWrites() ? microSceneRecords() : sceneRecords().flatMap((scene) => {
    const recordId = rigRecordId('scene', [scene.sceneId]);
    if (rigRecords()[recordId]?.deletedAt || Object.values(recordOverlays()).some((entry) =>
      entry.recordId === recordId && entry.op === 'delete')) return [];
    const fixtures = rigRecordState(recordId)?.state.fixtures;
    return [{ ...scene, ...(Array.isArray(fixtures) ? { fixtures } : {}) }];
  }));
  const visibleScenes = createMemo(() => microRecordWrites() ? microSceneRecords().map(sceneSummaryOf)
    : (recordRigSelected() && !piLineSelected() ? [] : scenes().filter((scene) => {
    const id = scene.sceneId || scene.id;
    if (!id) return true;
    const recordId = rigRecordId('scene', [id]);
    return !rigRecords()[recordId]?.deletedAt && !Object.values(recordOverlays()).some((entry) =>
      entry.recordId === recordId && entry.op === 'delete');
  })));
  function effectRecord(packageId: string): { recordId: string; record: RigRecord } | undefined {
    const ids = new Set([...Object.entries(rigRecords()).filter(([, record]) => record.kind === 'nodeInstance')
      .map(([recordId]) => recordId), ...Object.values(recordOverlays()).filter((entry) => entry.kind === 'nodeInstance')
      .map((entry) => entry.recordId)]);
    for (const recordId of ids) {
      const record = rigRecordState(recordId);
      if (record && !record.deletedAt && record.state.definition?.definitionId === packageId) return { recordId, record };
    }
    return undefined;
  }
  async function writeEffectRecord(params: Record<string, unknown>): Promise<RecordWriteReply> {
    const packageId = String(params.packageId ?? '');
    const raw = params.set;
    if (!packageId || !Array.isArray(raw)) throw new Error('Invalid effect settings edit.');
    const set = effectRecordSets(raw as RecordSet[]);
    const existing = effectRecord(packageId);
    if (existing) return writeRigRecord('nodeInstance', existing.record.segments, { op: 'set', set });
    const card = (deviceDoc()?.effectCatalog as Array<Record<string, any>> | undefined)
      ?.find((item) => item.packageId === packageId && typeof item.version === 'string');
    if (!card) throw new Error('The controller has no installed card for this effect.');
    const bindings = Object.fromEntries(Object.entries(card.parameters ?? {}).flatMap(([id, spec]) =>
      spec && typeof spec === 'object' && 'default' in spec ? [[id, spec.default]] : []));
    const value = applyRecordSets({ definition: { definitionId: packageId, definitionVersion: card.version },
      enabled: true, bindings }, set);
    return writeRigRecord('nodeInstance', [await newNodeInstanceId(selectedRigId(), packageId)], { op: 'replace', value });
  }
  async function writePanelRecords(params: Record<string, unknown>): Promise<RecordWriteReply[]> {
    const values = params.values;
    const elements = params.elements;
    if (!values || typeof values !== 'object' || Array.isArray(values)
        || !elements || typeof elements !== 'object' || Array.isArray(elements)) {
      throw new Error('Change not applied.');
    }
    const fixtureElements = elements as Record<string, unknown>;
    const edits = Object.entries(fixtureElements).map(([fixtureId, selected]) => {
      const fixture = rig()?.fixtures.find((entry) => entry.deviceId === fixtureId);
      const count = fixture?.elements?.length ?? fixture?.elementCount ?? 0;
      if (!Array.isArray(selected) || selected.length !== count
          || selected.some((index) => !Number.isInteger(index) || index < 0 || index >= count)) {
        throw new Error('Change not applied.');
      }
      const channels = Object.entries(values as Record<string, unknown>);
      const intensityKeys = fixtureIntensityKeys(fixture);
      if (!channels.length || channels.some(([key, value]) => !intensityKeys.includes(key)
          || typeof value !== 'number' || value < 0 || value > 1)
          || channels.some(([, value]) => value !== channels[0]?.[1])) {
        throw new Error('Change not applied.');
      }
      const set = [{ path: 'values/intensity', value: channels[0]![1] }];
      return { fixtureId, set };
    });
    return Promise.all(edits.map(({ fixtureId, set }) => writeRigRecord('panel', [fixtureId], { op: 'set', set })));
  }
  async function writeRigRecord(kind: RigRecordKind, segments: string[], edit: {
    op: 'set' | 'replace' | 'delete'; set?: RecordSet[]; value?: Record<string, unknown>;
  }): Promise<RecordWriteReply> {
    if (!microRecordWrites() || !controlAvailable()) throw new Error('Change not applied.');
    const venueId = selectedVenueId();
    const roomId = selectedRoomId();
    const rigId = selectedRigId();
    if (!venueId || !roomId || !rigId) throw new Error('Choose a room first.');
    const recordId = rigRecordId(kind, segments);
    let opId = newRecordOpId();
    const address = `${venueId}:${roomId}:${rigId}:${recordId}`;
    setRecordOverlays((current) => ({ ...current, [opId]: { recordId, kind, segments, ...edit } }));
    const write = async (): Promise<RecordWriteReply> => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        let unitRefusalReason: string | undefined;
        try {
          const base = recordBase(recordId, kind, rigRecordState(recordId), edit.op, edit.set);
          const reply = await cloudCall<RecordWriteReply>('writeRigRecord', {
            venueId, roomId, rigId, recordId, opId, origin: 'cloud', base, ...edit,
          });
          const refused = reply.units.filter((unit) => unit.outcome === 'refused');
          if (refused.length) {
            unitRefusalReason = refused[0]?.reason ?? 'unknown';
            throw Object.assign(new Error('Record units refused.'), { details: { reason: refused[0]?.reason } });
          }
          if (selectedVenueId() === venueId && selectedRoomId() === roomId && selectedRigId() === rigId) {
            setRecordOverlays((current) => current[opId] ? { ...current,
              [opId]: { ...current[opId], revision: reply.revision,
                unitKeys: reply.units.filter((unit) => unit.outcome === 'applied').map((unit) => unit.unitKey),
                applied: reply.applied !== undefined && reply.applied >= reply.revision } } : current);
          }
          return reply;
        } catch (error) {
          const reason = recordRefusalReason(error);
          if (reason === 'binding-changed' && attempt === 0) {
            console.info('[nuance-venue] rig record retry:', reason);
            const previousOpId = opId;
            opId = newRecordOpId();
            setRecordOverlays((current) => {
              const next = { ...current, [opId]: { recordId, kind, segments, ...edit } };
              delete next[previousOpId];
              return next;
            });
            continue;
          }
          setRecordOverlays((current) => {
            const next = { ...current };
            delete next[opId];
            return next;
          });
          if (reason || unitRefusalReason) {
            console.error('[nuance-venue] rig record refusal:', reason ?? unitRefusalReason);
            throw new Error('Change not applied.');
          }
          throw error;
        }
      }
      throw new Error('Change not applied.');
    };
    const pending = (recordWriteTails.get(address) ?? Promise.resolve()).then(write);
    const tail = pending.then(() => undefined, () => undefined);
    recordWriteTails.set(address, tail);
    return pending.finally(() => {
      if (recordWriteTails.get(address) === tail) recordWriteTails.delete(address);
    });
  }
  createEffect(() => {
    const saved = rigRecords();
    const applied = rigApplied();
    setRecordOverlays((current) => {
      const next = { ...current };
      for (const [opId, entry] of Object.entries(next)) {
        if (entry.revision !== undefined && (saved[entry.recordId]?.revision ?? 0) >= entry.revision
            && (entry.applied || !selectedDeviceId() || (applied[entry.recordId] ?? 0) >= entry.revision)) delete next[opId];
      }
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  });
  const roomModel = createMemo<PanelModel | undefined>(() => {
    const value = roomLayout();
    return value?.elements?.length ? buildPanelModel(value, rig(), placementTargets()) : undefined;
  });

  /**
   * The schematic is shared application state, not Scenes-screen state. Connect, Status, Effects and the
   * room drawing must all resolve the same `continuousRuns` chains or their summaries disagree.
   * The artifact's checksum is derived from the bytes that arrived; no fixture digest is pinned here.
   */
  createEffect(() => {
    const currentRig = rig();
    const published = cloudRig()?.schematicLayout ?? currentRig?.schematicLayout;
    const url = published?.url;
    const rawFallback = fallbackPanelLayout(currentRig);
    const fallback = rawFallback && published?.layoutDigest
      ? { ...rawFallback, layoutDigest: published.layoutDigest }
      : rawFallback;
    if (!url) {
      roomLayoutRequest += 1;
      loadedRoomLayoutKey = '';
      setRoomLayout(fallback);
      setRoomLayoutWarning('');
      setRoomLayoutLoading(false);
      return;
    }

    const layoutKey = `${cloudRig()?.activeRigVersionId ?? ''}:${published.sha256 ?? published.layoutDigest ?? url}`;
    if (loadedRoomLayoutKey === layoutKey) return;
    const request = ++roomLayoutRequest;
    setRoomLayout(undefined);
    setRoomLayoutWarning('');
    setRoomLayoutLoading(true);
    void fetch(url)
      .then(async (response) => {
        if (!response.ok) throw new Error(`room drawing download returned ${response.status}`);
        const bytes = await response.arrayBuffer();
        const expected = String(published.sha256 ?? '').replace(/^sha256:/, '').toLowerCase();
        if (expected && await sha256Hex(bytes) !== expected) throw new Error('room drawing checksum mismatch');
        return JSON.parse(new TextDecoder().decode(bytes)) as SchematicLayout;
      })
      .then((value) => {
        if (request !== roomLayoutRequest) return;
        if (!SCHEMATIC_DRAWING_SCHEMAS.has(value.schema)) {
          loadedRoomLayoutKey = layoutKey;
          setRoomLayout(undefined);
          setRoomLayoutWarning(`The detailed room drawing is unavailable. (${cloudRig()?.activeRigVersionId ?? currentRig?.activeRigVersionId ?? '—'} · ${value.schema})`);
          return;
        }
        if (!value.elements?.length) {
          throw new Error('room drawing has an unsupported shape');
        }
        loadedRoomLayoutKey = layoutKey;
        setRoomLayout({
          ...value,
          layoutDigest: value.layoutDigest ?? published.layoutDigest ?? published.sha256,
        });
      })
      .catch((error) => {
        if (request !== roomLayoutRequest) return;
        reportError('room drawing download', error, 'The detailed room drawing is unavailable.');
        setRoomLayout(fallback);
        if (fallback) setRoomLayoutWarning('Showing the controller’s own geometry while the detailed room drawing is unavailable.');
      })
      .finally(() => {
        if (request === roomLayoutRequest) setRoomLayoutLoading(false);
      });
  });

  /**
   * The room's effect geometry, loaded ONCE for the whole console.
   *
   * This used to live inside `EffectsScreen`, and that placement was the bug. Screen-local state has no
   * memory, so every re-read of the room rig — a rig-version edge, a room re-select, a sign-in refresh —
   * re-entered a window in which the signal was empty, and an empty signal was the same value as "this
   * room has no effect geometry". An operator who had started Audio reactive a minute earlier opened
   * Doorway and was told the room's rig had published nothing. Meanwhile Sweep and Edges kept working,
   * because they read the schematic from up here, where it is keyed, cached and never cleared by a reload.
   *
   * So this is the schematic's discipline applied to the same class of document:
   *
   * - **Keyed by identity, not by object.** The key is the rig version plus the bytes' own checksum, so a
   *   re-minted signed URL or a second read of the same rig is a no-op, not a re-download and not a gap.
   * - **Revalidated on rig-version edges.** `reloadRoomRigInternal` installs a fresh `cloudRig` on every
   *   observed edge; a genuinely new version changes the key and is re-downloaded, and nothing else is.
   * - **One retry per key.** The artifact URL is a short-lived signed READ URL, so a refused download is
   *   most often an expired one. A single rig re-read re-mints it; the guard stops that becoming a loop.
   * - **Four states, never three.** `loading` is not `unpublished` — see `EffectLayoutStatus`.
   */
  createEffect(() => {
    const rig = cloudRig();
    const settle = (status: EffectLayoutStatus, detail: string): void => {
      effectLayoutRequest += 1;
      loadedEffectLayoutKey = '';
      setEffectLayout(undefined);
      setEffectLayoutStatus(status);
      setEffectLayoutDetail(detail);
    };
    const source = resolveEffectLayoutSource(rig, !localOrigin);
    if (!('artifact' in source)) {
      settle(source.status, source.detail);
      return;
    }
    const { artifact, key } = source;
    if (loadedEffectLayoutKey === key) return;
    const request = ++effectLayoutRequest;
    setEffectLayout(undefined);
    setEffectLayoutStatus('loading');
    setEffectLayoutDetail('');
    void loadPublishedEffectLayout(artifact).then((result) => {
      if (request !== effectLayoutRequest) return;
      if ('problem' in result) {
        setEffectLayoutStatus('unavailable');
        setEffectLayoutDetail(result.problem);
        console.warn(`[nuance-venue] effect layout ${key} unusable: ${result.problem}`);
        if (retriedEffectLayoutKey !== key) {
          retriedEffectLayoutKey = key;
          void reloadRoomRigInternal(false).catch((error) => reportError('effect layout retry', error));
        }
        return;
      }
      loadedEffectLayoutKey = key;
      retriedEffectLayoutKey = '';
      setEffectLayout({ layout: result.layout, elementCount: artifact.elementCount });
      setEffectLayoutStatus('ready');
      setEffectLayoutDetail('');
    });
  });

  /**
   * The scores this room has.
   *
   * Two accounts of them, and they are not equals. The controller's own answer to `get_scores` is the
   * richer one — it is the room's library, whoever pushed it — so it wins whenever there is one; the
   * signed-in operator's own library stands in until then, narrowed to this room. Both are ANSWERS,
   * true when they were asked and not afterwards, which is the whole of the reload the operator used to
   * have to do: a score pushed from Nuance a minute ago was in neither of them. So when the room's
   * assignment rows are being watched, they have the last word on WHICH scores are in the room — a
   * score unassigned or deleted since is gone from this list the moment its row goes, without waiting
   * for a round trip, and a score added arrives with the re-read those same rows set off.
   */
  const scores = createMemo(() => {
    const assignments = liveAssignments();
    if (lanScores().length) {
      return assignments ? lanScores().filter((score) => assignedNow(score, assignments)) : lanScores();
    }
    return cloudScores().filter((score) => (assignments
      ? assignedNow(score, assignments)
      : scoreForAssignment(score, roomStatus())));
  });
  const scoreAssignments = createMemo(() => liveAssignments() ?? roomStatus()?.assignments ?? []);

  function toast(message: string, tone: Toast['tone'] = 'info'): void {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { id, message, tone }].slice(-4));
    globalThis.setTimeout(() => dismissToast(id), 5_000);
  }

  function dismissToast(id: string): void {
    setToasts((current) => current.filter((item) => item.id !== id));
  }

  /**
   * `success` may be a function of the result, for an action whose outcome decides what to say — inviting
   * an address that already has an account adds them now, while an unknown one is left waiting, and the
   * person who pressed the button needs to know which happened.
   */
  async function runAction<T>(
    key: string,
    work: () => Promise<T>,
    success?: string | ((result: T) => string),
  ): Promise<T | undefined> {
    if (activeActionKeys.has(key)) return undefined;
    activeActionKeys.add(key);
    setActionBusy(key);
    try {
      const result = await work();
      const message = typeof success === 'function' ? success(result) : success;
      if (message) toast(message, 'success');
      return result;
    } catch (error) {
      toast(reportError('action', error).message, 'error');
      return undefined;
    } finally {
      activeActionKeys.delete(key);
      setActionBusy((current) => current === key ? ([...activeActionKeys].at(-1) ?? '') : current);
    }
  }

  async function cloudCall<T = any>(name: string, params: Record<string, unknown> = {}): Promise<T> {
    // Carries the code, not just the words: `plainErrors` keys the sign-in sentence on the `unauthenticated`
    // vocabulary, never on a message that happens to contain "sign in".
    if (!user()) throw Object.assign(new Error('Sign in to use cloud commands.'), { code: 'unauthenticated' });
    try {
      const result = await dispatch.call<T>(name, params);
      setCloudReachable(true);
      return result;
    } catch (error) {
      if (/network|unavailable|failed to fetch|offline/i.test(String(error))) setCloudReachable(false);
      throw error;
    }
  }

  /** Reflect the authoritative command response immediately while the device-document listener catches up. */
  async function renameDevice(deviceId: string, displayName: string): Promise<RenameDeviceResult> {
    const venueId = selectedVenueId();
    if (!venueId) throw new Error('Choose a venue before renaming a controller.');
    const result = await cloudCall<RenameDeviceResult>('renameDevice', { venueId, deviceId, displayName });
    if (result.deviceId !== deviceId) throw new Error('The platform returned a different controller after renaming.');
    setRoomStatus((current) => current ? {
      ...current,
      devices: controllersAfterRename(current.devices, result),
    } : current);
    setDeviceDoc((current) => current && (current.id === result.deviceId || current.deviceId === result.deviceId)
      ? { ...current, displayName: result.displayName }
      : current);
    setMirror((current) => selectedDeviceId() === result.deviceId && current
      ? { ...current, displayName: result.displayName }
      : current);
    return result;
  }

  /**
   * Remove a score from both inventories only after deleteScore returns its authoritative identity. The
   * controller's assignments nudge owns transport clearing; this update owns the immediate list refresh.
   */
  async function deleteScore(scoreId: string): Promise<DeleteScoreResult> {
    const result = await cloudCall<DeleteScoreResult>('deleteScore', { scoreId });
    if (result.scoreId !== scoreId) throw new Error('The platform returned a different score after deletion.');
    setCloudScores((current) => scoresAfterDelete(current, result));
    setLanScores((current) => scoresAfterDelete(current, result));
    setRoomStatus((current) => current ? {
      ...current,
      assignments: current.assignments.filter((assignment) => assignment.scoreId !== result.scoreId),
    } : current);
    setLiveAssignments((current) => current?.filter((assignment) => assignment.scoreId !== result.scoreId));
    return result;
  }

  function commandCoalesceKey(name: string, params: Record<string, unknown>): string {
    const target = JSON.stringify({
      fixtures: params.fixtures,
      elements: params.elements,
      groups: params.groups,
      effectId: params.effectId,
    });
    let hash = 2166136261;
    for (let index = 0; index < target.length; index += 1) {
      hash ^= target.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `${name}:${(hash >>> 0).toString(36)}`;
  }

  /** True while the room's own authored tree is this console's account of a command's outcome. */
  function roomConfirms(name: string): boolean {
    return cloudStateMode === 'tree' && ROOM_CONFIRMED_COMMANDS.has(name);
  }

  /**
   * The room's own clock: the authored revision it has reached, and the last command the cloud recorded
   * against it.
   *
   * `stateRevision` advances whenever an authored node is installed (`authoredStateTree.ts:253`), which
   * on Scenes includes every `panel/fixtures/NNN` the daemon publishes after a level write. `lastCommand`
   * is the mirror child the relay stamps on a successful ack. Either moving means the room has moved on.
   */
  function roomClock(): { revision: number; commandId: string } {
    return {
      revision: Number(controllerState()?.stateRevision ?? -1),
      commandId: String(mirror()?.lastCommand?.id ?? ''),
    };
  }

  let roomConfirmationWaiters: Array<{ revision: number; commandId: string; settle: () => void }> = [];

  /**
   * Settle everyone waiting on the room as soon as the room's clock passes where they started.
   *
   * The revision is compared as an ORDERING, never as an equality against a number read off a different
   * clock: it only ever counts up, so "greater than where I started" is the honest test. The command id
   * is an identity and is compared as one.
   *
   * A waiter can be settled by movement that was not its own — another operator's edit, a scene recall.
   * That is deliberate and harmless: what this signal drives is the header's account of the command, and
   * ANY movement of the room corrects the screen anyway, because the authored tree is the authority. It
   * never decides whether an edit stands; only an explicit refusal does that.
   */
  createEffect(() => {
    const now = roomClock();
    if (!roomConfirmationWaiters.length) return;
    const moved = (waiter: { revision: number; commandId: string }): boolean => (
      now.revision > waiter.revision || (now.commandId !== '' && now.commandId !== waiter.commandId)
    );
    const settled = roomConfirmationWaiters.filter(moved);
    if (!settled.length) return;
    roomConfirmationWaiters = roomConfirmationWaiters.filter((waiter) => !moved(waiter));
    for (const waiter of settled) waiter.settle();
  });

  /**
   * A promise that resolves when the room moves past where it is now, plus the way to stop waiting.
   *
   * `abandon` matters: a waiter whose command has already been answered another way must leave the list,
   * or a console left open on Scenes accumulates one dead waiter per commit.
   */
  function awaitRoomConfirmation(): { confirmed: Promise<void>; abandon: () => void } {
    const { revision, commandId } = roomClock();
    let waiter!: { revision: number; commandId: string; settle: () => void };
    const confirmed = new Promise<void>((resolve) => {
      waiter = { revision, commandId, settle: resolve };
      roomConfirmationWaiters = [...roomConfirmationWaiters, waiter];
    });
    return {
      confirmed,
      abandon: () => {
        roomConfirmationWaiters = roomConfirmationWaiters.filter((entry) => entry !== waiter);
      },
    };
  }

  /**
   * Send a mutation the room reports on, and wait for the ROOM rather than for the relay.
   *
   * Four outcomes, and the difference between them is the whole fix:
   *
   * - The room's authored tree moves → the command landed. Resolves `true`, typically in the time it
   *   takes the daemon to publish a node, not in the time it takes a cloud callable to finish.
   * - The relay answers without the room having moved → resolves `true`; the relay's own ack is the
   *   other way of learning the room did it.
   * - The relay GIVES UP (`deadline-exceeded`) → resolves `false`. This is "nobody has confirmed it
   *   yet", NOT "it did not happen", and no caller may revert anything on it. A relay that runs out of
   *   budget has told us about the cloud and nothing about the room.
   * - Anything else THROWS. The room's own refusal, obviously — but also a relay-side refusal such as a
   *   full command queue, or an unauthenticated caller. Those are not slowness: the command demonstrably
   *   never reached the room, so leaving the operator's level standing would be the mirror image of the
   *   bug this fixes — a screen showing a level the room does not have. The race above is what makes
   *   this safe to be strict about: if the command DID land, the room settles first and we never get
   *   here, so an ambiguous failure whose command actually worked resolves as success rather than as a
   *   revert.
   *
   * The relay call is still made with `waitForResult`, and is still adopted when it arrives: its ack is
   * the only channel that carries a refusal, and it is the only writer of `lastCommand`. What changed is
   * that nothing an operator sees is gated on it any more.
   *
   * It also reports the command's DEADLINE, and that is not decoration. "Confirmed" here can be settled
   * by the room moving for a reason that is not this command — another operator's edit, a scene recall —
   * so a caller that stakes an optimistic overlay on this cannot read `true` as "the room took MY
   * value". `expiresAt` is what bounds the overlay either way: after it, nothing in the cloud can still
   * deliver this command, so anything still drawn and unconfirmed is never going to be confirmed.
   */
  async function sendRoomConfirmed(
    name: string,
    params: Record<string, unknown>,
    coalesceKey: string,
  ): Promise<RoomConfirmation> {
    const room = awaitRoomConfirmation();
    // The deadline is carried on the LOCAL clock from here on. The relay reports how much longer it
    // will carry the command, and that duration is added to this console's own `Date.now()` the moment
    // it arrives, so the overlay's lifetime is the relay's and the clock reading it is the only one in
    // play. When the relay never answers there is no duration to adopt and the fallback applies.
    let expiresAt = Date.now() + UNREPORTED_COMMAND_LIFETIME_MS;
    const relay = sendCloudControllerReceipt(name, params, true, coalesceKey, 'committed').then(
      (receipt) => {
        if (Number.isFinite(receipt.expiresInMs)) expiresAt = Date.now() + Number(receipt.expiresInMs);
        applyCloudCommandAck(name, params, receipt);
        return undefined as unknown;
      },
      (error: unknown) => error,
    );
    try {
      const failure = await Promise.race([room.confirmed.then(() => undefined as unknown), relay]);
      if (failure === undefined) {
        // Either the room moved or the relay answered. A relay still running is left running: its ack
        // corrects a room this console has already believed, and it cannot report a refusal of something
        // the room has demonstrably done. Its lateness never reverts anything.
        void relay.catch(() => undefined);
        return { confirmed: true, expiresAt };
      }
      // The relay ran out of budget. It has told us about the cloud and nothing about the room, so:
      // claim nothing, revert nothing, and let the room's own listeners settle it — within the deadline
      // above, after which an unconfirmed overlay comes off.
      if (relayVerdict(failure) !== 'refused') return { confirmed: false, expiresAt };
      throw failure;
    } finally {
      room.abandon();
    }
  }

  /**
   * The ONE funnel for every cloud-class command — and the one place its path is chosen (D12, B117).
   *
   * With the room's link open the command is spoken down the link and answered on it; otherwise it takes the
   * cloud queue exactly as before. The choice is made once, here, synchronously, and a command handed to the
   * link is never ALSO queued (H50): if the link dies with the answer outstanding, a mutation resolves exactly
   * like a relay timeout (the room's own listeners settle it), and only a read, which changes nothing, is asked
   * again on the queue. Callers never see which path ran, except as the receipt's `path`.
   */
  async function sendCloudControllerReceipt<T>(
    name: string,
    params: Record<string, unknown>,
    waitForResult: boolean,
    coalesceKey = commandCoalesceKey(name, params),
    interaction?: 'editing' | 'committed',
    announceOnly = false,
  ): Promise<CloudControllerReceipt<T>> {
    if (!piCloudReady()) throw new Error('Change not applied.');
    const deviceId = selectedDeviceId();
    if (!deviceId) throw new Error('Choose a controller first.');
    const link = roomLink();
    if (link && link.state() === 'open') {
      return sendLinkCommand<T>(link, deviceId, name, params, waitForResult, coalesceKey, interaction, announceOnly);
    }
    return sendQueueCommand<T>(deviceId, name, params, waitForResult, coalesceKey, interaction, announceOnly);
  }

  /** The catalog's own word for a command: a read (`query`) may be asked twice; anything else may not. */
  function commandIsQuery(name: string): boolean {
    return deviceDoc()?.commandCatalog?.[name]?.kind === 'query';
  }

  function commandClassOf(name: string): 'discrete' | 'gesture' {
    return deviceDoc()?.commandCatalog?.[name]?.class === 'gesture' ? 'gesture' : 'discrete';
  }

  /**
   * One command on the open link. A link answer is one round trip on a line that is already open, so the link
   * always waits for its result, whatever the caller asked of the relay; an announcement has no result.
   */
  async function sendLinkCommand<T>(
    link: RoomLink,
    deviceId: string,
    name: string,
    params: Record<string, unknown>,
    waitForResult: boolean,
    coalesceKey: string,
    interaction: 'editing' | 'committed' | undefined,
    announceOnly: boolean,
  ): Promise<CloudControllerReceipt<T>> {
    if (announceOnly) {
      link.announce({ command: name, coalesceKey, interaction: interaction ?? 'committed', paramsJson: JSON.stringify(params) });
      return { deviceId, status: 'announced', path: 'link' } as CloudControllerReceipt<T>;
    }
    const sentAt = performance.now();
    try {
      const receipt = await link.send<T>({ name, params, coalesceKey, interaction: interaction ?? null, commandClass: commandClassOf(name) });
      roomLinkMeter.commandAnswered({ cmdId: receipt.cmdId, name, path: 'link', sentAt, revision: receipt.authoredRevision });
      return receipt;
    } catch (error) {
      if (isRoomLinkLost(error) && commandIsQuery(name)) {
        return sendQueueCommand<T>(deviceId, name, params, waitForResult, coalesceKey, interaction, false);
      }
      throw error;
    }
  }

  async function sendQueueCommand<T>(
    deviceId: string,
    name: string,
    params: Record<string, unknown>,
    waitForResult: boolean,
    coalesceKey: string,
    interaction: 'editing' | 'committed' | undefined,
    announceOnly: boolean,
  ): Promise<CloudControllerReceipt<T>> {
    const sentAt = performance.now();
    const receipt = await cloudCall<CloudControllerReceipt<T>>('sendControllerCommand', {
      deviceId,
      command: name,
      params,
      coalesceKey,
      waitForResult,
      waitTimeoutMs: READ_RESULT_WAIT_MS,
      // No deadline is sent. The relay stamps and clamps the command's lifetime from SERVER time and
      // returns it as a duration; a deadline stated here would be this browser's clock entering the
      // system, and a machine a few minutes slow would have every command refused as already expired.
      ...(interaction ? { interaction } : {}),
      announceOnly,
    });
    if (
      waitForResult && receipt.result === undefined && receipt.authoredRevision === undefined
      && !OPTIONAL_MUTATION_RESULT_COMMANDS.has(name)
      && receipt.status !== 'announced'
    ) {
      // The relay accepted the command but handed back no result while we were waiting for one. That
      // means the deployed platform predates the ack-wait in functions/src/commands/control.ts, and every
      // read (state, status, scores, scenes) would silently return nothing — a console that can write but
      // never see. Say so loudly rather than rendering an empty room.
      throw new Error(`The controller relay returned no result for ${name} (status ${receipt.status ?? 'unknown'}).`);
    }
    if (waitForResult && !announceOnly) {
      roomLinkMeter.commandAnswered({ cmdId: receipt.cmdId, name, path: 'queue', sentAt, revision: receipt.authoredRevision });
    }
    return { ...receipt, path: 'queue' };
  }

  async function sendCloudController<T>(
    name: string,
    params: Record<string, unknown>,
    waitForResult: boolean,
    coalesceKey = commandCoalesceKey(name, params),
    interaction?: 'editing' | 'committed',
    announceOnly = false,
  ): Promise<T | undefined> {
    const receipt = await sendCloudControllerReceipt<T>(
      name, params, waitForResult, coalesceKey, interaction, announceOnly,
    );
    return receipt.result;
  }

  function announceCloudActivity(name: string, params: Record<string, unknown>, coalesceKey: string, interaction: 'editing' | 'committed'): void {
    if (!piCloudReady() || !selectedDeviceId() || !cloudReachable()) return;
    void sendCloudController(name, params, false, coalesceKey, interaction, true).catch(() => undefined);
  }

  /** True while this room's own live scene listener is the authority for which scenes exist. */
  function roomOwnsSceneLibrary(): boolean {
    return Boolean(sceneLibraryRoom) && sceneLibraryRoom === `${selectedVenueId()}:${selectedRoomId()}`;
  }

  /**
   * The ONE place a list of scenes becomes this console's scene library.
   *
   * Favorites are shared controller state (B5) and arrive on the mirror rather than in the list, so every
   * list has to be merged with them. Three paths deliver a list — the room's own collection listener, a
   * `list_scenes` result, and the two direct-link refreshes — and the direct ones used to call `setScenes`
   * raw, which silently dropped the merge: a room reached over the cable showed no favorites at all and
   * the Favorites group simply never appeared. There is one reducer now and all of them go through it.
   *
   * It also decides between the two, because they are not two libraries. The room's collection IS the
   * library; the daemon converges its own copy to that collection within about a second of a platform
   * nudge, so a `list_scenes` answer is the SAME library seen through a slower window. So while this
   * room's listener is attached, the listener says which scenes exist and a `list_scenes` result is
   * dropped rather than adopted — preferring the slower window is exactly how a daemon that is stale, or
   * has not yet been taught the collection, blanks a list of scenes the room genuinely has. With no
   * listener attached — a cable, venue-network or Bluetooth console, or a cloud one before it attaches —
   * `list_scenes` is the only source there is, and it is adopted as it always was.
   *
   * A FAVORITE is not a library: `set_scene_favorite`'s echo and the mirror's `favoriteSceneIds` move a
   * flag on whatever library is current and never come through here, so both cases keep them.
   */
  function adoptScenes(listed: readonly SceneSummary[], source: 'room' | 'controller'): void {
    if (source === 'controller' && roomOwnsSceneLibrary()) return;
    const favorites = mirror()?.scenes?.favoriteSceneIds;
    setScenes(Array.isArray(favorites) ? mergeSceneFavorites(listed, favorites) : [...listed]);
  }

  /**
   * One whole scene, read from the controller and folded in beside the cloud listener's records.
   *
   * A console on a direct link has no Firestore listener, so the library it holds is `list_scenes` — rows
   * with a name and an id and no look in them. Asking for the whole record of EVERY listed scene would
   * turn opening the page into one round trip per scene, so it is read for the one scene being drawn and
   * memoised: a controller that refuses must refuse once, not once per render.
   *
   * With no route to ask on there is nothing to remember — that is not an answer, and banking it would
   * leave the scene unreadable for the rest of the room's life once a route appeared.
   */
  async function loadSceneRecord(sceneId: string): Promise<SceneRecord | undefined> {
    const id = sceneId.trim();
    if (!id) return undefined;
    const existing = sceneRecordRequests.get(id);
    if (existing) return existing;
    const transport = controlTransport();
    if (transport === 'none') return undefined;
    const params = { scope: 'room', sceneId: id };
    const request = (async () => {
      const answer = transport === 'lan'
        ? await lanClient.call<unknown>('get_scene', params)
        : transport === 'bluetooth'
          ? await bluetoothClient.call<unknown>('get_scene', params)
          : await sendCloudController<unknown>('get_scene', params, true);
      // The answer is one record, and `parseSceneRecords` is the only thing that decides whether it is a
      // readable one — the same judgement the cloud listener's rows get.
      const [record] = parseSceneRecords([answer && typeof answer === 'object' ? { sceneId: id, ...answer } : answer]);
      if (!record) return undefined;
      setSceneRecords((current) => [...current.filter((entry) => entry.sceneId !== record.sceneId), record]);
      return record;
    })().catch((error) => {
      reportError(`scene read (${id})`, error);
      return undefined;
    });
    sceneRecordRequests.set(id, request);
    return request;
  }

  /**
   * Keep the non-reactive tree reducer on the same promoted value as the UI signal. Without this, a
   * cloud acknowledgement can move the screen to revision N and the next unrelated device-authored node
   * will reduce from the pre-command value retained here, rolling the acknowledged field backward.
   */
  function retainAuthoredState(next: LiveState | undefined): LiveState | undefined {
    if (cloudStateMode !== 'tree' || !next) return next;
    const projected: ControllerMirrorPatch = {
      activeSource: next.activeSource,
      activeSourceLabel: next.activeSourceLabel,
      blackout: next.blackout,
      outputRateHz: next.outputRateHz,
      transport: next.transport ?? null,
      stream: (next.stream as ControllerMirror['stream']) ?? null,
      remoteLive: (next.remoteLive as ControllerMirror['remoteLive']) ?? null,
      ...(next.scene ? {
        scenes: {
          ...(authoredModel.mirror.scenes ?? {}),
          recalledSceneId: next.scene.recalledSceneId ?? next.scene.sceneId ?? '',
          favoriteSceneIds: authoredModel.mirror.scenes?.favoriteSceneIds ?? [],
        },
      } : {}),
    };
    authoredModel = {
      ...authoredModel,
      state: next,
      mirror: { ...authoredModel.mirror, ...projected },
    };
    setMirror((current) => mergeMirrorPatch(current, projected));
    return next;
  }

  function promoteControllerState(
    update: (current: LiveState | undefined) => LiveState | undefined,
  ): LiveState | undefined {
    let promoted: LiveState | undefined;
    setControllerState((current) => {
      promoted = update(current);
      return promoted;
    });
    return retainAuthoredState(promoted);
  }

  /**
   * Put a controller's answer on screen, field by field.
   *
   * `requestedAtEpoch` is the control epoch the READ was issued at, and it defaults to now — an answer
   * that IS a command's own receipt cannot have been superseded by that command. A slow read can be, and
   * the test belongs HERE rather than around the whole answer: `get_status` carries the room's transport
   * (ordering-sensitive, and a late snapshot really can roll a concurrent `transport` command back) in
   * the same object as its health telemetry (version, daemon, session, engine, outputBinding, runtime,
   * update — nothing any command orders against). Discarding the snapshot whole to protect the one field
   * is what left the Status screen empty: `lanStatus` has exactly one cloud writer, and losing it once
   * was permanent.
   */
  function applyControllerResult(name: string, result: unknown, requestedAtEpoch: number = controlEpoch): void {
    if (!result || typeof result !== 'object') return;
    const value = result as Record<string, any>;
    if (name === 'get_status') {
      setLanStatus(value as LanStatus);
      if (requestedAtEpoch === controlEpoch) {
        if (value.transport) setTransportState(value.transport as TransportState);
        else setTransportState(undefined);
      }
    }
    else if (name === 'get_rig') setLanRig(expandRigTopology(value as RigSnapshot));
    else if (name === 'get_state') installFullState(normalizeFullState(value as LiveState, rig()));
    else if (name === 'get_scores') setLanScores(value.scores ?? []);
    else if (name === 'list_scenes') adoptScenes((value.scenes ?? []) as SceneSummary[], 'controller');
    else if (name === 'set_scene_favorite' && typeof value.sceneId === 'string' && typeof value.favorite === 'boolean') {
      setScenes((current) => updateSceneFavorite(current, value.sceneId, value.favorite));
      if (cloudStateMode === 'tree') {
        const current = authoredModel.favoriteSceneIds ?? authoredModel.mirror.scenes?.favoriteSceneIds ?? [];
        const favoriteSceneIds = value.favorite
          ? [...new Set([...current, value.sceneId])]
          : current.filter((sceneId) => sceneId !== value.sceneId);
        const scenesMirror: NonNullable<ControllerMirror['scenes']> = {
          recalledSceneId: authoredModel.mirror.scenes?.recalledSceneId ?? '',
          favoriteSceneIds,
        };
        authoredModel = {
          ...authoredModel,
          favoriteSceneIds,
          mirror: { ...authoredModel.mirror, scenes: scenesMirror },
        };
        setMirror((currentMirror) => mergeMirrorPatch(currentMirror, { scenes: scenesMirror }));
      }
    }
    else if (name === 'list_audio_inputs') setAudioInputs(value as ListAudioInputsResult);
    else if (name === 'get_audio_reactive_status') setAudioStatus(value);
    else if (name === 'start_audio_monitor') {
      const monitor = (value.audioMonitor ?? value.monitor ?? value) as ControllerMirrorAudio;
      setLanStatus((current) => ({ ...(current ?? {}), audioMonitor: monitor }));
    }
    else if (name === 'stop_audio_monitor') {
      setLanStatus((current) => ({ ...(current ?? {}), audioMonitor: null }));
    }
    else if (name === 'transport' && typeof value.state === 'string') {
      setTransportState(value as TransportState);
      promoteControllerState((current) => current ? { ...current, transport: value as TransportState } : current);
    } else if (name === 'blackout' && typeof value.blackout === 'boolean') {
      promoteControllerState((current) => current ? { ...current, blackout: value.blackout } : current);
    } else if (name === 'set_mode' && typeof value.activeSource === 'string') {
      // Taking the room back also ends whatever was driving it. Clearing the stale stream/remote-live
      // overlay here is what makes the "Nuance is live" banner disappear the instant the arbiter agrees,
      // instead of lingering until some later read happens to refresh the whole snapshot.
      const takenOver = value.activeSource !== 'stream' && value.activeSource !== 'remote';
      promoteControllerState((current) => current ? {
        ...current,
        activeSource: value.activeSource,
        activeSourceLabel: value.activeSourceLabel ?? (value.activeSource === 'panel' ? 'Scenes' : current.activeSourceLabel),
        ...(takenOver ? { stream: undefined, remoteLive: undefined } : {}),
      } : current);
    }
    else if (name === 'set_output_rate' && Number.isFinite(Number(value.outputRateHz))) {
      const outputRateHz = Number(value.outputRateHz);
      promoteControllerState((current) => current ? { ...current, outputRateHz } : current);
      setLanStatus((current) => current ? {
        ...current,
        engine: { ...(current.engine ?? {}), targetRateHz: outputRateHz },
      } : current);
    } else if (name === 'set_max_intensity' && Number.isFinite(Number(value.maxIntensity))) {
      const maxIntensity = Number(value.maxIntensity);
      if (cloudStateMode === 'tree') {
        authoredModel = {
          ...authoredModel,
          mirror: { ...authoredModel.mirror, maxIntensity },
        };
        setMirror((current) => mergeMirrorPatch(current, { maxIntensity }));
      }
      setLanStatus((current) => current ? {
        ...current,
        maxIntensity,
        engine: { ...(current.engine ?? {}), maxIntensity },
      } : current);
    }
    else if (name === 'set_fixture_params' && Array.isArray(value.fixtures) && value.fixtures.every((fixture) => typeof fixture?.deviceId === 'string')) {
      // Expand-first only: a 1.7.19 controller returns this touched-fixture result to the originating
      // console. A 1.7.20 mutation returns authoredRevision instead and the local intent is promoted below.
      const updates = new Map(value.fixtures.map((fixture) => [fixture.deviceId, fixture]));
      promoteControllerState((current) => current ? {
        ...current,
        fixtures: current.fixtures.map((fixture) => updates.get(fixture.deviceId) ?? fixture),
      } : current);
    }
    else if (value.projection === 'delta-v1' && controllerState() && rig()) {
      const reduced = reduceStateDelta(controllerState()!, value as DeltaV1, rig()!);
      if (reduced.kind === 'applied' || reduced.kind === 'duplicate') promoteControllerState(() => reduced.state);
    }
  }

  /**
   * Put a freshly read whole state on screen.
   *
   * One rule wherever a full state arrives: never repaint from an answer older than what we already hold.
   * A cloud read is a slow round-trip and deltas keep landing while it is in flight, so its answer can be
   * superseded before it arrives, and installing it would bounce the room backwards.
   */
  function installFullState(next: LiveState): void {
    promoteControllerState((current) => adoptFullState(current, next));
    setTransportState(next.transport ?? undefined);
  }

  /** Expand-window baseline for catalog-selected legacy mode; retiring get_state.cloudRelay removes it. */
  async function legacyCloudStateRequest(params: Record<string, unknown>): Promise<StateSyncChunk | LiveState> {
    const answer = await sendCloudController<StateSyncChunk | LiveState>('get_state', params, true);
    if (!answer) throw new Error('The controller returned no state.');
    return answer;
  }

  /** The only cloud `get_state`: one join read for a deployed 1.7.19 controller. */
  async function readLegacyCloudState(force = false): Promise<void> {
    if (!piCloudReady()) return;
    if (cloudStateMode !== 'legacy-1.7.19' || !user() || !selectedDeviceId() || !cloudReachable()) return;
    const deviceId = selectedDeviceId();
    if (!force && legacyJoinDeviceId === deviceId) return;
    legacyJoinDeviceId = deviceId;
    const requestedAtEpoch = controlEpoch;
    if (!rig()) await reloadRoomRigInternal(false).catch(() => undefined);
    const currentRig = rig();
    if (!currentRig) throw new Error('The controller returned state before its rig was available.');
    const legacy = await readStateSync(legacyCloudStateRequest, currentRig);
    if (selectedDeviceId() === deviceId && requestedAtEpoch === controlEpoch) installFullState(legacy);
  }

  /**
   * The ceiling on resync-driven whole-room reads. Held for the life of the provider, so switching rooms
   * inside one session does not hand a device a fresh budget it has not earned.
   */
  const resyncBudget = createResyncBudget();

  /**
   * Buy a compatibility refresh, if this legacy device can still afford one.
   *
   * The ONLY caller is the mirror's `resync` branch, which is the one path where the console asks for a
   * whole room in response to something the ROOM did rather than something a person did. That is what makes
   * it unbounded when it goes wrong: a divergence the console cannot reconcile turns every published delta
   * into a large cloud read, at the mirror's own rate, for as long as the tab is open. The budget remains
   * defence in depth only for 1.7.19. The authored tree has complete per-node values and no delta gaps.
   */
  function requestResync(reason: string): void {
    const deviceId = selectedDeviceId();
    if (!deviceId || cloudStateMode !== 'legacy-1.7.19') return;
    const verdict = resyncBudget.request(deviceId, Date.now());
    if (verdict === 'allowed') {
      void readLegacyCloudState(true).catch(() => undefined);
      return;
    }
    if (verdict === 'exhausted') {
      // Once per exhausted minute, never at the mirror's rate — a warning on every refused delta would be
      // the same runaway in a different ledger. Nothing reaches the operator: the room simply stops getting
      // fresher until a delta applies, and the deltas keep arriving either way.
      console.warn('[nuance-venue] resync budget exhausted', JSON.stringify({
        deviceId,
        readsPerMinute: RESYNC_READS_PER_MINUTE,
        reason,
      }));
    }
  }

  /**
   * The explicit connection the operator has, if any — venue network ahead of Bluetooth.
   *
   * One definition, read from two places: `controlTransport` chooses the route with it, and both
   * dispatch paths retry over it. They used to hold two copies of the same order and drifted apart in
   * the one way that mattered — the memo's copy sat behind the cloud, the fallback's in front of it.
   */
  function directTransport(): Exclude<ControlTransport, 'cloud' | 'none'> | undefined {
    if (lanState() === 'connected' && lanHello()?.authenticated === true) return 'lan';
    if (bluetoothState() === 'connected') return 'bluetooth';
    return undefined;
  }

  async function callDirect<T>(transport: 'lan' | 'bluetooth', name: string, params: Record<string, unknown>): Promise<T> {
    const client = transport === 'lan' ? lanClient : bluetoothClient;
    const result = await client.call<T>(name, params);
    applyControllerResult(name, result);
    announceCloudActivity(name, params, commandCoalesceKey(name, params), 'committed');
    if (['transport', 'blackout', 'start_audio_reactive', 'start_effect_package', 'stop_effect'].includes(name)) {
      void (transport === 'lan' ? refreshLan() : refreshBluetooth());
    }
    return result;
  }

  function optimisticControllerResult(name: string, params: Record<string, unknown>): Record<string, unknown> | undefined {
    if (name === 'blackout' && typeof params.on === 'boolean') return { blackout: params.on };
    if (name === 'set_mode' && typeof params.source === 'string') {
      return { activeSource: params.source, ...(params.source === 'panel' ? { activeSourceLabel: 'Scenes' } : {}) };
    }
    if (name === 'set_output_rate' && Number.isFinite(params.hz)) return { outputRateHz: Number(params.hz) };
    if (name === 'set_max_intensity' && Number.isFinite(params.value)) return { maxIntensity: Number(params.value) };
    if (name === 'set_scene_favorite' && typeof params.sceneId === 'string' && typeof params.favorite === 'boolean') {
      return { sceneId: params.sceneId, favorite: params.favorite };
    }
    return undefined;
  }

  /**
   * A recalled scene is already in the room's Firestore listener. Apply that same record to the staged
   * panel projection while promoting the command ack, instead of asking the Pi to serialize the room we
   * just told it to select. The cloud snapshot remains the recovery authority; this is only the author's
   * zero-byte promotion path.
   */
  function applySceneRecallIntent(current: LiveState, sceneId: string): LiveState {
    const currentRig = rig();
    const currentModel = roomModel();
    const record = sceneRecords().find((candidate) => candidate.sceneId === sceneId);
    const decoded = decodeSceneLevels(record, currentModel, currentRig);
    const scene = { ...(current.scene ?? {}), sceneId, recalledSceneId: sceneId };
    if (decoded.kind !== 'valid' || !currentRig || !currentModel) {
      return { ...current, activeSource: 'panel', activeSourceLabel: 'Scenes', scene };
    }

    const sceneLevel = (deviceId: string, index: number): number | undefined => {
      const addressed = currentModel.byAddress.get(elementAddress(deviceId, index));
      return addressed ? decoded.levels.get(addressed.key) : undefined;
    };
    const descriptors = new Map(currentRig.fixtures.map((fixture) => [fixture.deviceId, fixture]));
    const fixtures = current.fixtures.map((fixture) => {
      const keys = fixtureIntensityKeys(descriptors.get(fixture.deviceId));
      if (!keys.length) return fixture;
      if (!fixture.elements?.length) {
        const level = sceneLevel(fixture.deviceId, 0);
        return level == null
          ? fixture
          : { ...fixture, values: { ...fixture.values, ...Object.fromEntries(keys.map((key) => [key, level])) } };
      }
      return {
        ...fixture,
        elements: fixture.elements.map((element) => {
          const level = sceneLevel(fixture.deviceId, element.index);
          return level == null
            ? element
            : { ...element, values: { ...element.values, ...Object.fromEntries(keys.map((key) => [key, level])) } };
        }),
      };
    });
    return { ...current, activeSource: 'panel', activeSourceLabel: 'Scenes', scene, fixtures };
  }

  /**
   * Promote this console's already-known command intent onto the authored clock named by the Pi's ack.
   *
   * The ack is `{status, authoredRevision}` and carries no authored patch: the Pi is the sole writer of
   * `controllerAuthored/{deviceId}` and publishes what it did on its own command path, so the authored
   * listeners are the ONLY adoption path for canonical state. What this function still does is promote
   * the caller's own intent onto the revision the room reported, so the screen does not sit on the
   * pre-command value for the ≈200 ms it takes the leaf to arrive.
   */
  function applyCloudCommandAck<T>(
    name: string,
    params: Record<string, unknown>,
    receipt: CloudControllerReceipt<T>,
  ): T {
    const revision = receipt.authoredRevision;
    if (receipt.result !== undefined && receipt.result !== null) {
      applyControllerResult(name, receipt.result);
    } else {
      const optimistic = optimisticControllerResult(name, params);
      if (optimistic) applyControllerResult(name, optimistic);
    }

    if (Number.isSafeInteger(revision) && Number(revision) >= 0) {
      let promotedTransport: TransportState | null | undefined;
      promoteControllerState((current) => {
        const activity: ControllerActivity = {
          id: receipt.cmdId,
          command: name,
          phase: 'committed',
          intentJson: JSON.stringify(params),
        };
        let promoted = applyControllerIntent(current, activity);
        if (promoted && name === 'recall_scene' && typeof params.sceneId === 'string') {
          promoted = applySceneRecallIntent(promoted, params.sceneId);
        }
        if (!promoted) return current;
        promotedTransport = promoted.transport;
        const nextRevision = Math.max(promoted.stateRevision ?? 0, Number(revision));
        return {
          ...promoted,
          rigRevision: promoted.rigRevision ?? rig()?.rigVersionId,
          stateRevision: nextRevision,
        };
      });
      if (promotedTransport !== undefined) setTransportState(promotedTransport ?? undefined);
    }

    // 1.7.20 mutation acknowledgements intentionally contain no legacy result body. Existing callers use
    // a truthy return only as successful completion; state is reconciled from the Pi's canonical leaves.
    return (receipt.result ?? {}) as T;
  }

  /**
   * One controller command, and one account of what happened to it in the header's feedback slot.
   *
   * Every transport shares that account. The venue-network and Bluetooth branch used to return before
   * any feedback was raised at all, so a controller refusing a command over a cable left the header
   * blank; the same refusal over the cloud said so. There is one shape now — sent, then applied or
   * refused — whichever wire carried it, including the direct link the cloud falls back to.
   */
  async function controllerCall<T = any>(name: string, params: Record<string, unknown> = {}): Promise<T> {
    if (!canControl()) throw new Error("Your venue role doesn't allow controls.");
    if (!piControllerReady()) throw new Error('Change not applied.');
    const selectedTransport = controlTransport();
    if (selectedTransport === 'none') throw new Error('Choose an online controller first.');

    controlEpoch += 1;
    showControlFeedback(commandSent(name));
    try {
      if (selectedTransport === 'lan' || selectedTransport === 'bluetooth') {
        const result = await callDirect<T>(selectedTransport, name, params);
        showControlFeedback(commandApplied(name));
        return result;
      }
      if (roomConfirms(name)) {
        const outcome = await sendRoomConfirmed(name, params, commandCoalesceKey(name, params));
        if (outcome.confirmed) showControlFeedback(commandApplied(name));
        else clearControlFeedback();
        // A room-confirmed mutation's acknowledgement carries no result body: the state it changed is
        // reconciled from the authored leaves the daemon publishes. What it DOES carry back is the
        // command's deadline, so the screen that staged the edit can bound its own overlay.
        return outcome as T;
      }
      const receipt = await sendCloudControllerReceipt<T>(
        name, params, true, commandCoalesceKey(name, params), 'committed',
      );
      const result = applyCloudCommandAck(name, params, receipt);
      showControlFeedback(commandApplied(name));
      return result as T;
    } catch (error) {
      // A cloud dispatch that failed is retried over whatever explicit link is up NOW. The gate used to
      // be `!cloudReachable()` — the browser's own view of Firebase — which is the same mistake the
      // transport memo made: a console with working internet could never be rescued by the Bluetooth
      // link it was sitting on, which is precisely the case the link exists for. Because an explicit
      // connection now outranks the cloud, reaching here at all means the link came up DURING the
      // dispatch: nine to eighteen seconds is long enough for an operator to give up waiting and press
      // Connect over Bluetooth, and their command should ride the wire they just made rather than die
      // with the relay deadline that sent them looking for it.
      const fallback = selectedTransport === 'cloud' && directControllerReady() ? directTransport() : undefined;
      if (fallback) {
        try {
          const result = await callDirect<T>(fallback, name, params);
          showControlFeedback(commandApplied(name));
          return result;
        } catch (fallbackError) {
          showControlFeedback(commandFailed(name, fallbackError));
          throw fallbackError;
        }
      }
      showControlFeedback(commandFailed(name, error));
      throw error;
    }
  }

  /** A guarded read changes no command feedback or mutation epoch. */
  async function controllerRead<T = any>(name: string, params: Record<string, unknown> = {}): Promise<T> {
    if (!piControllerReady()) throw new Error('Change not applied.');
    const selectedTransport = controlTransport();
    if (selectedTransport === 'none') throw new Error('Choose an online controller first.');
    if (selectedTransport === 'lan' || selectedTransport === 'bluetooth') {
      const client = selectedTransport === 'lan' ? lanClient : bluetoothClient;
      const result = await client.call<T>(name, params);
      applyControllerResult(name, result);
      return result;
    }
    const result = await sendCloudController<T>(name, params, true);
    applyControllerResult(name, result);
    return result as T;
  }

  async function sendRecordControl<T>(name: string, write: () => Promise<T>): Promise<T> {
    if (!microRecordWrites() || !controlAvailable()) throw new Error('Change not applied.');
    controlEpoch += 1;
    showControlFeedback(commandSent(name));
    try {
      const result = await write();
      showControlFeedback(commandApplied(name));
      return result;
    } catch (error) {
      showControlFeedback(commandFailed(name, error));
      throw error;
    }
  }

  async function sendPanelEdit(params: Record<string, unknown>): Promise<RoomConfirmation> {
    if (!microRecordWrites()) return controllerCall<RoomConfirmation>('set_fixture_params', params);
    await sendRecordControl('set_fixture_params', () => writePanelRecords(params));
    return { confirmed: true, expiresAt: Date.now() };
  }

  async function sendBlackout(on: boolean): Promise<unknown> {
    if (!microRecordWrites()) return controllerCall('blackout', { on });
    return sendRecordControl('blackout', () => writeRigRecord('output', [], {
      op: 'set', set: [{ path: 'blackout', value: on }],
    }));
  }

  async function loadRooms(venueId: string): Promise<void> {
    setRoomsLoadedVenueId('');
    const result = await cloudCall<{ rooms: RoomSummary[] }>('listRooms', { venueId });
    if (venueId !== selectedVenueId()) return;
    setRooms(result.rooms);
    const ids = [...new Set(result.rooms.flatMap((room) => (room.rigs ?? []).map((rig) => rig.controllerId).filter((id): id is string => Boolean(id))))];
    const documents = await Promise.all(ids.map(async (deviceId) => [deviceId,
      (await getDoc(doc(firestore, `devices/${deviceId}`))).data()] as const));
    if (venueId !== selectedVenueId()) return;
    setBoundDevices(Object.fromEntries(documents.filter((entry): entry is readonly [string, Record<string, any>] => Boolean(entry[1]))));
    const preferred = result.rooms.find((room) => room.roomId === selectedRoomId()) ?? result.rooms[0];
    if (preferred) {
      setSelectedRoomId(preferred.roomId);
      const rigId = localStorage.getItem(`nuance-venue:rig:${preferred.roomId}`);
      setSelectedRigId(preferred.rigs?.find((rig) => rig.rigId === rigId)?.rigId ?? preferred.rigs?.[0]?.rigId ?? '');
      setRoomsLoadedVenueId(venueId);
      localStorage.setItem('nuance-venue:room', preferred.roomId);
      await loadRoomDetails(venueId, preferred.roomId);
    } else {
      setSelectedRoomId('');
      setRoomStatus(undefined);
    }
  }

  function activeRoomRigVersion(value: Record<string, any> | RoomStatus | CloudRig | undefined): string {
    if (!value) return '';
    const bound = boundRigVersion(value as RoomRigReply, selectedRigId());
    if (bound) return bound;
    const nested = 'activeRig' in value && value.activeRig && typeof value.activeRig === 'object'
      ? value.activeRig.rigVersionId
      : undefined;
    const candidate = nested ?? ('activeRigVersionId' in value ? value.activeRigVersionId : undefined);
    return typeof candidate === 'string' ? candidate.trim() : '';
  }

  async function readBoundRoomRig(venueId: string, roomId: string): Promise<CloudRig> {
    const result = await cloudCall<RoomRigReply | CloudRig>('getRoomRig', { venueId, roomId });
    // Older journey fixtures modelled the retired single-rig reply. Production always returns rigs[].
    if (!('rigs' in result) || !Array.isArray(result.rigs)) return result as CloudRig;
    const chosen = result.rigs.find((rig) => rig.rigId === selectedRigId()) ?? result.rigs[0];
    if (!chosen) return { activeRigVersionId: null, rigVersion: null };
    // An older room's getRoomRig may finish after the picker changed rooms. It must never retarget
    // the new room's record writes to that older room's rig.
    if (roomId === selectedRoomId() && chosen.rigId !== selectedRigId()) setSelectedRigId(chosen.rigId);
    return openBoundRoomRig(chosen,
      (rigId, versionId) => cloudCall<RigOpenResult>('getRigVersion', { venueId, rigId, versionId }),
      async (rigId, versionId) => {
        const version = await getDoc(doc(firestore, `rigs/${rigId}/versions/${versionId}`));
        return version.exists() ? version.data() : undefined;
      });
  }

  function mirrorRigVersion(value: ControllerMirrorPatch | undefined): string {
    const candidate = value?.rig?.versionId;
    return typeof candidate === 'string' ? candidate.trim() : '';
  }

  /**
   * One event-triggered rig refresh shared by the Firestore active-version edge, the controller's mirror
   * edge, and Effects' last-moment stale-map guard. The caller can ask for the operator notice, but
   * concurrent edges still collapse into one pair of platform reads.
   */
  async function reloadRoomRigInternal(announce: boolean): Promise<void> {
    const venueId = selectedVenueId();
    const roomId = selectedRoomId();
    if (!user() || !venueId || !roomId) return;
    announceRigReload ||= announce;
    if (rigReload) return rigReload;
    rigReload = (async () => {
      const [statusResult, rigResult] = await Promise.all([
        cloudCall<RoomStatus>('getRoomStatus', { venueId, roomId }),
        readBoundRoomRig(venueId, roomId),
      ]);
      if (venueId !== selectedVenueId() || roomId !== selectedRoomId()) return;
      setRoomStatus(statusResult);
      setCloudRig(rigResult);
      observedRoomRigVersion = activeRoomRigVersion(statusResult) || activeRoomRigVersion(rigResult);
      if (cloudStateMode === 'tree' && authoredDeviceId) {
        authoredUnsubscribers.forEach((unsubscribe) => unsubscribe());
        authoredUnsubscribers.clear();
        authoredModel = { mirror: {}, revisions: {} };
        noteAuthoredBaseline();
        await reconcileAuthoredSubscriptions();
      }
      if (announceRigReload) setRigReloadRevision((revision) => revision + 1);
    })();
    try {
      await rigReload;
    } finally {
      rigReload = undefined;
      announceRigReload = false;
    }
  }

  /**
   * The caller-facing reload, and it announces itself.
   *
   * Its only caller is Effects' last-moment guard, which discovers the room has changed under it because
   * a start was refused. That is exactly the moment an operator needs "Room updated" — so the notice is
   * raised here rather than by a second sentence beside the Start button saying the same thing.
   */
  async function reloadRoomRig(): Promise<void> {
    await reloadRoomRigInternal(true);
  }

  function observeRoomRigVersion(next: string): void {
    if (!next) return;
    if (!observedRoomRigVersion) {
      observedRoomRigVersion = next;
      return;
    }
    if (next === observedRoomRigVersion) return;
    observedRoomRigVersion = next;
    void reloadRoomRigInternal(true).catch((error) => reportError('room rig reload', error));
  }

  function observeControllerRigVersion(next: string): void {
    if (!next) return;
    if (!observedControllerRigVersion) {
      observedControllerRigVersion = next;
      return;
    }
    if (next === observedControllerRigVersion) return;
    observedControllerRigVersion = next;
    void reloadRoomRigInternal(true).catch((error) => reportError('room rig reload', error));
  }

  async function loadRoomDetails(venueId: string, roomId: string): Promise<void> {
    const [statusResult, rigResult, scoreResult] = await Promise.all([
      cloudCall<RoomStatus>('getRoomStatus', { venueId, roomId }),
      readBoundRoomRig(venueId, roomId),
      cloudCall<{ scores: ScoreSummary[] }>('listMyScores'),
    ]);
    // An older room request can finish after an operator has already chosen another room.
    // Ignore that stale response so it cannot silently retarget subsequent controller commands.
    if (venueId !== selectedVenueId() || roomId !== selectedRoomId()) return;
    setRoomStatus(statusResult);
    setCloudRig(rigResult);
    observedRoomRigVersion = activeRoomRigVersion(statusResult) || activeRoomRigVersion(rigResult);
    setCloudScores(scoreResult.scores);
    const boundController = (statusResult.rigs ?? []).find((entry) => entry.rigId === selectedRigId())?.controllerId;
    const preferred = boundController ?? resolveControllerForRoom(statusResult.devices, venueId, roomId);
    selectController(preferred);
    subscribeDetails(venueId, roomId, preferred);
  }

  async function refreshCloud(): Promise<void> {
    if (!user()) return;
    setCloudBusy(true);
    setCloudError('');
    try {
      const token = await user()!.getIdTokenResult();
      setStaff(token.claims.staff != null);
      setStaffTier(token.claims.staff === 'admin' ? 'admin' : token.claims.staff != null ? 'support' : undefined);
      const result = await cloudCall<{ venues: VenueSummary[] }>('listMyVenues');
      setVenues(result.venues);
      const preferred = result.venues.find((venue) => venue.venueId === selectedVenueId()) ?? result.venues[0];
      if (preferred) {
        setSelectedVenueId(preferred.venueId);
        localStorage.setItem('nuance-venue:venue', preferred.venueId);
        await loadRooms(preferred.venueId);
      } else {
        setRooms([]);
        setRoomStatus(undefined);
      }
    } catch (error) {
      const message = reportError('cloud', error).message;
      setCloudError(message);
    } finally {
      setCloudBusy(false);
    }
  }

  function clearSubscriptions(): void {
    detailUnsubscribers.forEach((unsubscribe) => unsubscribe());
    detailUnsubscribers = [];
    setHealthLive(undefined);
    authoredUnsubscribers.forEach((unsubscribe) => unsubscribe());
    authoredUnsubscribers.clear();
    musicTelemetryUnsubscribe?.();
    musicTelemetryUnsubscribe = undefined;
    authoredDeviceId = '';
    authoredModel = { mirror: {}, revisions: {} };
    cloudStateMode = undefined;
    noteAuthoredBaseline();
    stateSyncPendingGeneration = undefined;
    legacyJoinDeviceId = '';
    stateSyncGeneration += 1;
    subscribedIdentity = '';
    // Per-subscription state: the next room's listeners attach fresh, and their first delivery is that
    // room's history, not a command anybody issued.
    lastMirrorCommandId = '';
    mirrorCommandSeeded = false;
    assignmentsSeeded = false;
    readScoresForAssignments = '';
    // The next room's assignments are nobody's yet. Leaving this room's behind would narrow the next
    // room's scores to a set of rows that were never about it.
    setLiveAssignments(undefined);
    observedControllerRigVersion = '';
    // A scene record belongs to the room it was read for, and so does a refusal to read one. With the
    // listener gone, a controller's `list_scenes` is the only library there is again.
    sceneRecordRequests.clear();
    sceneLibraryRoom = '';
    setEffectDocuments([]);
  }

  /**
   * The room's command mirror, and the one delivery of it that is not an event.
   *
   * Attaching to `controllerState/{deviceId}/lastCommand` is answered immediately with whatever is
   * already there, so the first thing a freshly-loaded console hears is the last command the room ran —
   * possibly minutes old, possibly somebody else's. That delivery is SEEDED: it records which command
   * the room is standing on and nothing else. Ticking `controlEpoch` for it would invalidate the reads
   * this very page load has in flight, and replaying its `resultJson` would apply a stale command's
   * answer to state that has only just loaded (a retained settings command may be harmless; a retained
   * `transport` or `set_mode` would not be).
   */
  function handleControllerCommand(command: ControllerMirror['lastCommand']): void {
    const seeding = !mirrorCommandSeeded;
    mirrorCommandSeeded = true;
    const commandRevision = command?.id
      ? `${command.id}:${command.at ?? ''}:${command.actor?.uid ?? ''}:${command.authoredRevision ?? ''}:${command.resultJson ?? ''}`
      : '';
    if (!command || !commandRevision || commandRevision === lastMirrorCommandId) return;
    lastMirrorCommandId = commandRevision;
    if (seeding) return;
    controlEpoch += 1;
    if (command.resultJson) {
      try {
        applyControllerResult(command.command, JSON.parse(command.resultJson));
      } catch {
        // A malformed optional 1.7.19 echo is ignored; the pushed state remains authoritative.
      }
    }
    if (Number.isSafeInteger(command.authoredRevision)) {
      promoteControllerState((current) => current ? {
        ...current,
        rigRevision: current.rigRevision ?? rig()?.rigVersionId,
        stateRevision: Math.max(current.stateRevision ?? 0, Number(command.authoredRevision)),
      } : current);
    }
  }

  function handleControllerActivity(collaborative: ControllerActivity | undefined): void {
    const activityRevision = collaborative?.id
      ? `${collaborative.id}:${collaborative.at ?? ''}:${collaborative.actor?.uid ?? ''}:${collaborative.phase}`
      : '';
    if (!collaborative || !activityRevision || activityRevision === lastMirrorActivityId) return;
    lastMirrorActivityId = activityRevision;
    const activityAge = Date.now() - Date.parse(collaborative.at ?? '');
    if (activityAge >= 0 && activityAge <= 5_000 && ['editing', 'committed', 'pending'].includes(collaborative.phase)) {
      const promoted = promoteControllerState((current) => applyControllerIntent(current, collaborative));
      if (promoted?.transport !== undefined) setTransportState(promoted.transport ?? undefined);
      controlEpoch += 1;
    }
    if (collaborative.phase === 'denied') requestResync('A collaborative command was denied.');
  }

  function applyLegacyMirror(next: ControllerMirror | undefined): void {
    if (!next) return;
    const previous = mirror();
    setMirror(next);
    handleControllerCommand(next.lastCommand);
    handleControllerActivity(next.activity);
    applyMirrorProjection(next, previous);
    observeControllerRigVersion(mirrorRigVersion(next));
  }

  function installAuthoredReduction(path: string, reduced: AuthoredTreeReduction): void {
    if (reduced.kind === 'invalid') {
      subscriptionFailed(`authored node subscription (${path})`, new Error(
        reduced.error ?? `Invalid authored node at ${path}.`,
      ));
      return;
    }
    // A duplicate changes nothing a person sees; its model differs only in which revision that feed last
    // delivered, which the next restart check reads (`reduceAuthoredFeedNode`).
    authoredModel = reduced.model;
    if (reduced.kind === 'duplicate') return;
    // Per-path revisions are independent. A music node at revision 7 can legitimately arrive after core
    // revision 12, so the old whole-snapshot revision gate must not discard it. The node reducer already
    // rejected stale writes on this exact path; install its combined model directly.
    if (authoredModel.state) promoteControllerState(() => authoredModel.state);
    // After the state it describes: a baseline must never read complete ahead of the fixture that made it so.
    noteAuthoredBaseline();
    setMirror((current) => mergeMirrorPatch(current, authoredModel.mirror));
    if (path === 'transport') setTransportState(authoredModel.state?.transport ?? undefined);
    if (path === 'scenes' && authoredModel.favoriteSceneIds) {
      setScenes((current) => mergeSceneFavorites(current, authoredModel.favoriteSceneIds!));
    }
    if (path === 'core') {
      observeControllerRigVersion(mirrorRigVersion(authoredModel.mirror));
      const maxIntensity = authoredModel.mirror.maxIntensity;
      if (typeof maxIntensity === 'number') {
        setLanStatus((current) => current ? {
          ...current, maxIntensity, engine: { ...(current.engine ?? {}), maxIntensity },
        } : current);
      }
    }
    if (path === 'panel/meta') void reconcileAuthoredSubscriptions();
  }

  /** Adopt the room's new run from scratch; see `beginAuthoredTreeEpoch` for what is kept. */
  function beginAuthoredEpoch(fromRevision?: number): void {
    installAuthoredFixtureLoss(beginAuthoredTreeEpoch(authoredModel, fromRevision, rig()));
  }

  /**
   * Install a model that reports fewer fixtures than the one it replaces, and put that on screen now.
   *
   * No node is necessarily about to follow — a deleted tree or fixture node arrives alone — so the room's
   * state is promoted here rather than left for the next reduction: until then Scenes would go on drawing
   * the forgotten fixtures at levels nobody is reporting.
   */
  function installAuthoredFixtureLoss(next: AuthoredTreeModel): void {
    authoredModel = next;
    if (authoredModel.state) promoteControllerState(() => authoredModel.state);
    noteAuthoredBaseline();
  }

  /** Publish whether the followed tree's panel baseline is whole (`panelBaselineComplete`). */
  function noteAuthoredBaseline(): void {
    setAuthoredPanelComplete(cloudStateMode === 'tree' ? authoredPanelBaselineComplete(authoredModel) : undefined);
  }

  /**
   * One authored node into the ONE reducer, whichever feed delivered it (B118): the cloud listener or a
   * session of the room's link. The per-path revision gate makes the second arrival of a revision — and any
   * arrival below what the other feed already delivered — a `duplicate`, so a person sees one change once.
   * Only a feed going backwards against itself, an epoch change or the tree's deletion starts a new run.
   */
  function applyAuthoredNode(path: string, value: unknown, feed: AuthoredFeed): void {
    const reduced = reduceAuthoredFeedNode(authoredModel, feed, path, value, rig());
    if (reduced.restarted) installAuthoredFixtureLoss(reduced.restarted);
    roomLinkMeter.nodeArrived(feed.kind, (value as { revision?: unknown } | null)?.revision, reduced.kind === 'duplicate');
    installAuthoredReduction(path, reduced);
  }

  /**
   * Open the session link to the room's controller (D1, B116). Its nodes and collaboration feed the same
   * reducers the cloud listeners feed; its state is what the command funnel reads at send time.
   */
  function openRoomLinkFor(venueId: string, roomId: string, deviceId: string, capability: string): void {
    const link = new RoomLink({
      db: firestore,
      restDb: restFirestore,
      call: <T,>(name: string, params: Record<string, unknown>) => cloudCall<T>(name, params),
      deviceId,
      venueId,
      roomId,
      capability,
      // Relay-only from the first byte (Rule 2); the grant's TURN servers are configured before gathering starts.
      peerFactory: () => new RTCPeerConnection({ iceTransportPolicy: 'relay' }),
    });
    link.onState = (state) => { if (roomLink() === link) setRoomLinkState(state); };
    link.onNode = (path, node, session) => {
      if (roomLink() !== link || cloudStateMode !== 'tree' || authoredDeviceId !== deviceId) return;
      applyAuthoredNode(path, node, { kind: 'link', session });
    };
    link.onCollab = (collab) => { if (roomLink() === link) applyLinkCollab(collab); };
    setRoomLink(link);
    setRoomLinkState('closed');
    subscribeRoomLink();
    link.open();
  }

  function closeRoomLink(): void {
    const link = roomLink();
    if (!link) return;
    setRoomLink(undefined);
    setRoomLinkState('closed');
    link.close('client_closed');
  }

  /**
   * Tell the link which of the room's domains this console follows (S20): the same domains the cloud listeners
   * follow for this route (`authoredDomainPathsForRoute`), and every fixture on Scenes. Only once this console
   * follows the controller's authored tree; the link resends it to every link it opens.
   */
  function subscribeRoomLink(): void {
    const link = roomLink();
    if (!link || cloudStateMode !== 'tree' || !authoredDeviceId || authoredDeviceId !== selectedDeviceId()) return;
    link.subscribe(authoredDomainPathsForRoute(route()), route() === 'scenes');
  }

  /** S19: the link's copy of `activity` and `lastCommand`, merged exactly as their cloud listeners merge them. */
  function applyLinkCollab(collab: RoomLinkCollab): void {
    if (collab.activity) {
      setMirror((current) => mergeMirrorPatch(current, { activity: collab.activity as ControllerMirror['activity'] }));
    }
    if (collab.lastCommand) {
      const lastCommand = collab.lastCommand as ControllerMirror['lastCommand'];
      setMirror((current) => mergeMirrorPatch(current, { lastCommand }));
      handleControllerCommand(lastCommand);
    }
  }

  function desiredAuthoredPaths(): Set<string> {
    const desired = new Set(authoredDomainPathsForRoute(route()));
    if (route() === 'scenes' && Number.isSafeInteger(authoredModel.fixtureCount)) {
      for (let index = 0; index < (authoredModel.fixtureCount ?? 0); index += 1) {
        desired.add(authoredFixturePath(index));
      }
    }
    return desired;
  }

  async function reconcileAuthoredSubscriptions(): Promise<void> {
    if (cloudStateMode !== 'tree' || !authoredDeviceId) return;
    const generation = stateSyncGeneration;
    const deviceId = authoredDeviceId;
    const needsFixtureRig = route() === 'scenes' && Number.isSafeInteger(authoredModel.fixtureCount);
    if (needsFixtureRig && !rig()) await reloadRoomRigInternal(false);
    if (generation !== stateSyncGeneration || cloudStateMode !== 'tree' || authoredDeviceId !== deviceId) return;
    const desired = desiredAuthoredPaths();
    for (const [path, unsubscribe] of authoredUnsubscribers) {
      if (desired.has(path)) continue;
      unsubscribe();
      authoredUnsubscribers.delete(path);
    }
    for (const path of desired) {
      if (authoredUnsubscribers.has(path)) continue;
      const unsubscribe = onValue(
        databaseRef(realtime, `controllerAuthored/${authoredDeviceId}/${path}`),
        (snapshot) => {
          if (snapshot.exists()) {
            applyAuthoredNode(path, snapshot.val(), { kind: 'cloud' });
            return;
          }
          // The tree is GONE, not merely quiet. The daemon deletes `controllerAuthored/{deviceId}`
          // outright when the last watching lease lapses, and the run that republishes afterwards may
          // number its leaves from 1 again. Treating that delete as "nothing arrived" is what let a
          // console keep a frozen room and reject every leaf of the next run as stale.
          //
          // Only `meta` is read as the tree's presence: it is the node the daemon always publishes while
          // the tree exists, whereas the optional nodes (`music`, `sessions`) are legitimately absent on
          // a room that is not doing those things, and resetting on those would throw away a live run's
          // revisions for no reason.
          if (path === 'meta') {
            beginAuthoredEpoch();
            return;
          }
          // One fixture's node is gone while the tree stays: that fixture's level is no longer known. It
          // goes back to unknown, exactly as before its node first arrived, and the room is not whole.
          const fixtureIndex = authoredFixtureIndexOf(path);
          if (fixtureIndex !== undefined) installAuthoredFixtureLoss(forgetAuthoredFixture(authoredModel, fixtureIndex, rig()));
        },
        (error) => subscriptionFailed(`authored node subscription (${path})`, error),
      );
      authoredUnsubscribers.set(path, unsubscribe);
    }
    // The room's tempo and input levels live on a leased node the controller writes only while watched.
    // `useMusicTelemetry` holds the lease that makes the Pi publish here at all.
    if (route() === 'music' && !musicTelemetryUnsubscribe) {
      musicTelemetryUnsubscribe = onValue(
        databaseRef(realtime, `controllerState/${authoredDeviceId}/musicTelemetry`),
        (snapshot) => setMirror((current) => mergeMirrorPatch(current, { musicTelemetry: snapshot.val() })),
        (error) => subscriptionFailed('music telemetry subscription', error),
      );
    } else if (route() !== 'music' && musicTelemetryUnsubscribe) {
      musicTelemetryUnsubscribe();
      musicTelemetryUnsubscribe = undefined;
      setMirror((current) => current ? { ...current, musicTelemetry: null } : current);
    }
    subscribeRoomLink();
  }

  function subscribeTreeCollaboration(deviceId: string): void {
    const watch = <K extends 'heartbeat' | 'activity' | 'lastCommand'>(key: K, apply: (value: ControllerMirror[K]) => void) => {
      detailUnsubscribers.push(onValue(
        databaseRef(realtime, `controllerState/${deviceId}/${key}`),
        (snapshot) => apply((snapshot.val() as ControllerMirror[K] | null) ?? undefined),
        (error) => subscriptionFailed(`controller state subscription (${key})`, error),
      ));
    };
    watch('heartbeat', (value) => setMirror((current) => mergeMirrorPatch(current, { heartbeat: value })));
    // The activity child says WHO is working the room and is rendered as that. It is deliberately NOT
    // applied to the room model in tree mode.
    //
    // It used to be: any activity under five seconds old had its `intentJson` folded straight into
    // `controllerState`, which made a peer console a THIRD writer of the room — one with no revision, so
    // nothing could order it against the authored leaves and nothing ever took it back. A peer that
    // showed `transport: playing` from another operator's REFUSED play went on showing it until the
    // transport node next moved for a real reason, and `requestResync` (its only correction) returns
    // immediately in tree mode. It was also a frontend re-implementation of command semantics, which is
    // the backend's job.
    //
    // Nothing is lost by dropping it. The authored leaf for the same change arrives about a second
    // later and is the truth; drag samples are keep-last intent slots now and never produce relayed
    // activity at all, so the preview it powered has no source left to draw from. Live code with a dead
    // source is worse than either keeping it fed or deleting it. The legacy 1.7.19 mirror path keeps the
    // mechanism — it has no authored tree to be corrected by.
    watch('activity', (value) => setMirror((current) => mergeMirrorPatch(current, { activity: value })));
    watch('lastCommand', (value) => {
      setMirror((current) => mergeMirrorPatch(current, { lastCommand: value }));
      handleControllerCommand(value);
    });
  }

  async function configureCloudStateSync(deviceId: string): Promise<void> {
    const generation = stateSyncGeneration;
    const mode = await cloudCall<ControllerStateSyncMode>('getControllerStateSyncMode', { deviceId });
    if (generation !== stateSyncGeneration || selectedDeviceId() !== deviceId || !cloudBindingReady()) return;
    cloudStateMode = mode.mode;
    if (mode.mode === 'tree') {
      authoredDeviceId = deviceId;
      authoredModel = { mirror: {}, revisions: {} };
      noteAuthoredBaseline();
      subscribeTreeCollaboration(deviceId);
      await reconcileAuthoredSubscriptions();
      return;
    }
    detailUnsubscribers.push(onValue(
      databaseRef(realtime, `controllerState/${deviceId}`),
      (snapshot) => applyLegacyMirror((snapshot.val() as ControllerMirror | null) ?? undefined),
      (error) => {
        setMirror(undefined);
        subscriptionFailed('controller state subscription', error);
      },
    ));
    await readLegacyCloudState();
  }

  function ensureCloudStateSync(deviceId: string): void {
    if (!deviceId || !cloudBindingReady() || cloudStateMode || stateSyncPendingGeneration === stateSyncGeneration) return;
    const generation = stateSyncGeneration;
    stateSyncPendingGeneration = generation;
    void configureCloudStateSync(deviceId)
      .catch((error) => reportError('controller state sync', error))
      .finally(() => {
        if (stateSyncPendingGeneration === generation) stateSyncPendingGeneration = undefined;
      });
  }

  /**
   * Attach the live document subscriptions for one (venue, room, device).
   *
   * Idempotent on purpose. Two call sites reach here — the room-details load and the selection effect —
   * and tearing four Firestore listeners down and building them straight back up on every tick made the
   * SDK trip its own internal assertion (`ID: ca9`), which then killed every later snapshot callback: the
   * device was never selected, so no controller state was ever fetched. A repeating 15-second cloud poll
   * used to hide that by eventually re-running the load. There is no poll any more, so the subscription
   * has to be correct rather than merely retried.
   */
  /**
   * The room's assignment rows just arrived, or moved.
   *
   * Two separate things happen with them, and only one of them is about CHANGE. The rows are adopted
   * every time, first delivery included, because they are the freshest statement of which scores the
   * room has and the score list narrows to them straight away — that is what makes a deleted or
   * unassigned score leave the page with no round trip at all. The RE-READ is what waits for a real
   * change: the rows carry no titles and no durations (they are `{scoreId, scoreVersionId, enabled}`),
   * so a score that has just arrived needs the account that names it — the controller's own library,
   * and the operator's — asked for again. Asking on the first delivery would re-read on every sign-in
   * for a room that has been sitting unchanged since yesterday.
   */
  function observeRoomAssignments(assignments: RoomStatus['assignments']): void {
    setLiveAssignments(assignments);
    const fingerprint = assignmentFingerprint(assignments);
    const seeding = !assignmentsSeeded;
    assignmentsSeeded = true;
    if (seeding || fingerprint === readScoresForAssignments) {
      readScoresForAssignments = fingerprint;
      return;
    }
    readScoresForAssignments = fingerprint;
    void reloadRoomScores().catch((error) => reportError('score refresh', error));
  }

  /**
   * Ask again for the scores the room has, because the room's assignments moved under us.
   *
   * Both accounts are re-read, because which one the page is drawing depends on whether the controller
   * has answered yet, and an operator watching a push land must not have to know which. The reads are
   * routed exactly as every other controller read is — the open cable first, then Bluetooth, then the
   * relay — and a room with no controller reached still gets the operator's own library back.
   */
  async function reloadRoomScores(): Promise<void> {
    const readController = async (): Promise<void> => {
      const direct = directTransport();
      if (direct) {
        const client = direct === 'lan' ? lanClient : bluetoothClient;
        applyControllerResult('get_scores', await client.call<{ scores: ScoreSummary[] }>('get_scores'));
        return;
      }
      if (!user() || !selectedDeviceId()) return;
      applyControllerResult('get_scores', await sendCloudController<{ scores: ScoreSummary[] }>('get_scores', {}, true));
    };
    const readLibrary = async (): Promise<void> => {
      if (!user()) return;
      const result = await cloudCall<{ scores: ScoreSummary[] }>('listMyScores');
      setCloudScores(result.scores);
    };
    const outcomes = await Promise.allSettled([readController(), readLibrary()]);
    const failure = outcomes.find((outcome) => outcome.status === 'rejected');
    if (failure) throw (failure as PromiseRejectedResult).reason;
  }

  function subscribeDetails(venueId: string, roomId: string, deviceId: string): void {
    const rigId = selectedRigId();
    const identity = `${venueId}:${roomId}:${rigId}:${deviceId}:${roomStatus()?.remoteLiveLocks?.[rigId]?.sessionId ?? ''}`;
    if (identity === subscribedIdentity && detailUnsubscribers.length) {
      // The first attach may have preceded binding resolution; start its mirror once that arrives.
      ensureCloudStateSync(deviceId);
      return;
    }
    clearSubscriptions();
    subscribedIdentity = identity;
    // A fresh attach starts with no failure: the notice belongs to the room whose listener failed, and
    // the next room must not inherit it.
    setCloudError('');
    detailUnsubscribers.push(
      onSnapshot(
        doc(firestore, `venues/${venueId}/rooms/${roomId}`),
        (snapshot) => {
          const data = snapshot.exists() ? snapshot.data() : undefined;
          setRoomDoc(data);
          observeRoomRigVersion(activeRoomRigVersion(data));
          if (selectedVenueId() === venueId && selectedRoomId() === roomId
            && !selectedRoom()?.rigs?.length && Array.isArray(data?.rigs) && data.rigs.length
            && !firstRigReload) {
            // The picker read a new, empty room; its first rig now supplies the binding and device class.
            firstRigReload = loadRooms(venueId)
              .catch((error) => { reportError('room rig refresh', error); })
              .finally(() => { firstRigReload = undefined; });
          }
        },
        (error) => {
          setRoomDoc(undefined);
          subscriptionFailed('room subscription', error);
        },
      ),
    );
    if (rigId) detailUnsubscribers.push(onSnapshot(
      collection(firestore, `venues/${venueId}/rooms/${roomId}/rigs/${rigId}/records`),
      (snapshot) => setRigRecords(Object.fromEntries(snapshot.docs.map((entry) =>
        [entry.id, entry.data() as RigRecord]))),
      (error) => subscriptionFailed('rig record subscription', error),
    ));
    setRigApplied({});
    if (rigId && deviceId) detailUnsubscribers.push(onValue(
      databaseRef(realtime, `controllerState/${deviceId}/rigs/${rigId}/applied`),
      (snapshot) => setRigApplied((snapshot.val() as Record<string, number> | null) ?? {}),
      (error) => subscriptionFailed('rig applied subscription', error),
    ));
    // The room's named looks, live for the same reason the room document is: two managers can be on the
    // same room, and a preset one of them just saved has to appear on the other's screen without a reload.
    // A listener failure empties the library rather than freezing a stale one — an operator who applies a
    // preset that no longer exists gets a refusal from the cloud, which is worse than not offering it.
    setEffectPresets([]);
    detailUnsubscribers.push(
      onSnapshot(
        collection(firestore, `venues/${venueId}/rooms/${roomId}/effectPresets`),
        (snapshot) => setEffectPresets(parseEffectPresets(
          snapshot.docs.map((entry) => ({ presetId: entry.id, ...entry.data() })),
        )),
        (error) => {
          setEffectPresets([]);
          subscriptionFailed('effect preset subscription', error);
        },
      ),
    );
    // Effect-owned documents follow the room, not the Pi. This is one generic collection listener,
    // constrained to Auto VJ's effectId so the rules can prove the read and another effect's documents
    // never become part of this card's state. Command results are never a second read path.
    setEffectDocuments([]);
    detailUnsubscribers.push(
      onSnapshot(
        query(
          collection(firestore, `venues/${venueId}/rooms/${roomId}/effectDocuments`),
          where('effectId', '==', AUTO_VJ_PACKAGE_ID),
        ),
        (snapshot) => setEffectDocuments(snapshot.docs.flatMap((entry) => {
          const parsed = parseEffectDocument(entry.data());
          return parsed?.effectId === AUTO_VJ_PACKAGE_ID ? [parsed] : [];
        })),
        (error) => {
          setEffectDocuments([]);
          subscriptionFailed('effect document subscription', error);
        },
      ),
    );
    // The room's scenes, live, for the reason the room now HAS scenes in the cloud at all: a scene
    // follows the room rather than the SD card of whichever controller happened to save it. The library
    // therefore appears with no controller reached and while the Pi is offline, and a scene another
    // operator just saved lands on this screen without a reload. The records are whole — the same ones
    // the controller recalls — so the drawing can preview one. A listener failure empties the library
    // rather than freezing a stale one, exactly as the named looks above do.
    setSceneRecords([]);
    sceneLibraryRoom = `${venueId}:${roomId}`;
    adoptScenes([], 'room');
    detailUnsubscribers.push(
      onSnapshot(
        collection(firestore, `venues/${venueId}/rooms/${roomId}/scenes`),
        (snapshot) => {
          const records = parseSceneRecords(snapshot.docs.map((entry) => ({ sceneId: entry.id, ...entry.data() })));
          setSceneRecords(records);
          adoptScenes(records.map(sceneSummaryOf), 'room');
        },
        (error) => {
          setSceneRecords([]);
          adoptScenes([], 'room');
          subscriptionFailed('scene subscription', error);
        },
      ),
    );
    // The room's scores, live, and for the same reason its scenes are: a score pushed from Nuance while
    // this page is open belongs on this page, and an operator should not have to reload to find out
    // what the room has. This is the ONLY live signal there is for it — the controller publishes
    // nothing when a score arrives (its mirror says what is STAGED and nothing about the library), and
    // the event it does get, `controllerSync`, is readable by the Pi alone.
    //
    // Both equalities are required, and not as an optimisation: the rules prove a read on
    // `isMember(resource.data.venueId)`, and a query that does not constrain `venueId` AND `roomId` is
    // denied whole rather than filtered — the same trap the room's controllers are subject to.
    //
    // A listener failure leaves the one-shot reads in charge rather than emptying the room: unlike a
    // scene, a score the operator can no longer see is one they cannot start, and `getRoomStatus` gave
    // an independent answer to the same question when the room was opened.
    detailUnsubscribers.push(
      onSnapshot(
        query(
          collection(firestore, 'assignments'),
          where('venueId', '==', venueId),
          where('roomId', '==', roomId),
        ),
        (snapshot) => observeRoomAssignments(snapshot.docs.map((entry) => {
          const data = entry.data();
          return {
            scoreId: String(data.scoreId ?? ''),
            scoreVersionId: String(data.scoreVersionId ?? ''),
            enabled: data.enabled !== false,
            rigId: typeof data.rigId === 'string' ? data.rigId : undefined,
            oscAddressSources: data.oscAddressSources as Record<string, 'score' | 'rig'> | undefined,
          };
        })),
        (error) => {
          setLiveAssignments(undefined);
          assignmentsSeeded = false;
          readScoresForAssignments = '';
          subscriptionFailed('score subscription', error);
        },
      ),
    );
    // No controller selected is a real state (a room with more than one live controller and no
    // remembered choice). `doc(firestore, 'devices/')` is not a reference — it throws — so the room's
    // own listener above must not be taken down with it.
    setDeviceDoc(undefined);
    if (deviceId) {
      detailUnsubscribers.push(onValue(
        databaseRef(realtime, `controllerState/${deviceId}/health`),
        (snapshot) => setHealthLive(controllerHealthLive(snapshot.val(), deviceId)),
        (error) => {
          setHealthLive(undefined);
          subscriptionFailed('controller health subscription', error);
        },
      ));
      detailUnsubscribers.push(
        onSnapshot(
          doc(firestore, `devices/${deviceId}`),
          (snapshot) => {
            setDeviceDoc(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : undefined);
          },
          (error) => {
            setDeviceDoc(undefined);
            subscriptionFailed('controller subscription', error);
          },
        ),
      );
    }
    const lock = roomStatus()?.remoteLiveLocks?.[rigId]?.sessionId;
    if (lock) {
      detailUnsubscribers.push(
        onSnapshot(
          doc(firestore, `venues/${venueId}/rooms/${roomId}/remoteLiveSessions/${lock}`),
          (snapshot) => setRemoteSession(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : undefined),
          (error) => {
            setRemoteSession(undefined);
            subscriptionFailed('remote live subscription', error);
          },
        ),
      );
    }
    ensureCloudStateSync(deviceId);
  }

  async function selectVenue(venueId: string): Promise<void> {
    if (venueId === selectedVenueId()) return;
    clearPanelHistories();
    clearSubscriptions();
    setRigRecords({});
    setRecordOverlays({});
    observedRoomRigVersion = '';
    resetAudioRoomState();
    setSelectedVenueId(venueId);
    localStorage.setItem('nuance-venue:venue', venueId);
    setCloudBusy(true);
    try {
      await loadRooms(venueId);
    } catch (error) {
      setCloudError(reportError('cloud', error).message);
    } finally {
      setCloudBusy(false);
    }
  }

  async function selectRoom(roomId: string, rigId?: string): Promise<void> {
    const venueId = selectedVenueId();
    const room = rooms().find((entry) => entry.roomId === roomId);
    const nextRigId = room?.rigs?.find((entry) => entry.rigId === rigId)?.rigId
      ?? (roomId === selectedRoomId() ? selectedRigId() : room?.rigs?.[0]?.rigId ?? '');
    if (!venueId || (roomId === selectedRoomId() && nextRigId === selectedRigId())) return;
    clearPanelHistories();
    clearSubscriptions();
    observedRoomRigVersion = '';
    resetAudioRoomState();
    setSelectedRoomId(roomId);
    setSelectedRigId(nextRigId);
    setRigRecords({});
    setRecordOverlays({});
    localStorage.setItem('nuance-venue:room', roomId);
    if (nextRigId) localStorage.setItem(`nuance-venue:rig:${roomId}`, nextRigId);
    setCloudBusy(true);
    try {
      await loadRoomDetails(venueId, roomId);
    } catch (error) {
      setCloudError(reportError('cloud', error).message);
    } finally {
      setCloudBusy(false);
    }
  }

  /**
   * Drive this room from `deviceId`, or — with an empty id — from no controller at all.
   *
   * "No controller" is a real state, not an error: a room with several live controllers and no
   * remembered choice sits in it until someone picks one on the Connect screen. Nothing may keep
   * drawing the controller that was selected a moment ago, so the room's snapshot goes with the
   * selection; otherwise the console would show one controller's room while addressing another's.
   */
  function selectController(deviceId: string): void {
    if (deviceId !== selectedDeviceId()) {
      resetAudioRoomState();
      if (!deviceId) setControllerState(undefined);
    }
    setSelectedDeviceId(deviceId);
    rememberController(selectedVenueId(), selectedRoomId(), deviceId);
    const currentDevice = roomStatus()?.devices.find((device) => device.deviceId === deviceId);
    if (selectedVenueId() && selectedRoomId()) subscribeDetails(selectedVenueId(), selectedRoomId(), currentDevice?.deviceId ?? deviceId);
    // Status polling may populate the two headline snapshots before the initial controller load.
    // Still fetch scores, scenes and audio inputs whenever a controller is selected.
    if (user() && !lanClient.connected && !bluetoothClient.connected) {
      void establishControllerState();
    }
  }

  /** The console's own answer to "which room, which controller", for the selection rule to decide on. */
  function consoleSelection(): { venueId: string; roomId: string; deviceId: string } {
    return { venueId: selectedVenueId(), roomId: selectedRoomId(), deviceId: selectedDeviceId() };
  }

  /**
   * Apply what the selection rule decided about a direct connection: the room first, so nothing reads a
   * controller against the room it is not in, then the controller, then storage.
   */
  function applyDirectSelection(outcome: SelectionOutcome): void {
    if (outcome.venueId !== selectedVenueId()) setSelectedVenueId(outcome.venueId);
    if (outcome.roomId !== selectedRoomId()) setSelectedRoomId(outcome.roomId);
    if (outcome.deviceId !== selectedDeviceId()) {
      resetAudioRoomState();
      setSelectedDeviceId(outcome.deviceId);
    }
    if (outcome.remember) rememberController(outcome.remember.venueId, outcome.remember.roomId, outcome.deviceId);
  }

  function handleNotification(notification: LanNotification, source: 'lan' | 'bluetooth'): void {
    // Direct links may stay connected as warm fallbacks. Only the preferred link is allowed to replace
    // the cloud mirror or the higher-priority direct feed in application state.
    if (controlTransport() !== source) return;
    if (notification.name === 'state_changed' && notification.data) {
      const data = notification.data as LiveState | DeltaV1;
      if ('projection' in data && data.projection === 'delta-v1') {
        const current = controllerState();
        const currentRig = rig();
        if (!current || !currentRig) {
          void refreshLan();
          return;
        }
        const result = reduceStateDelta(current, data as DeltaV1, currentRig);
        if (result.kind === 'applied' || result.kind === 'duplicate') setControllerState(result.state);
        else if (result.kind === 'resync') void refreshLan();
      } else {
        setControllerState((current) => adoptFullState(current, normalizeFullState(data as LiveState, rig())));
      }
    } else if (notification.name === 'playhead' && notification.data) {
      const next: TransportState = {
        ...(transportState() ?? {
          state: notification.data.playing ? 'playing' : 'stopped',
          positionSeconds: 0,
          durationSeconds: 0,
          loop: false,
          snapshotAt: 0,
        }),
        positionSeconds: notification.data.position,
        durationSeconds: notification.data.duration,
        state: notification.data.playing ? 'playing' : (transportState()?.state ?? 'stopped'),
      };
      setTransportState(next);
      setControllerState((current) => current ? { ...current, transport: next } : current);
    } else if (notification.name === 'stream_changed' && notification.data) {
      // The active⇄idle edge of a LAN stream. Only this edge is pushed, so the endpoint health behind
      // the Status disclosure is fetched on the edge and on request — never on a clock.
      const data = notification.data as { active?: boolean; fixtures?: number; sender?: string };
      setControllerState((current) => current ? {
        ...current,
        stream: data.active ? { active: true, fixtureCount: data.fixtures, sender: data.sender } : undefined,
      } : current);
      if (data.active) void (source === 'bluetooth' ? refreshBluetooth() : refreshLan());
    } else if (notification.name === 'audio_reactive_status') {
      setAudioStatus(notification.data);
    } else if (notification.name === 'rig_changed') {
      void (source === 'bluetooth' ? refreshBluetooth() : refreshLan());
      if (user()) void reloadRoomRigInternal(true).catch((error) => reportError('room rig reload', error));
    } else if (notification.name === 'status_changed') {
      void (source === 'bluetooth' ? refreshBluetooth() : refreshLan());
    }
  }

  async function refreshLan(): Promise<void> {
    if (!lanClient.connected) return;
    const entries = await Promise.allSettled([
      lanClient.call<LanStatus>('get_status'),
      lanClient.call<RigSnapshot>('get_rig', { projection: 'compact-v1' }, 20_000),
      lanClient.call<{ scores: ScoreSummary[] }>('get_scores'),
      lanClient.call<{ scenes: SceneSummary[] }>('list_scenes', { scope: 'room' }),
      lanClient.call<ListAudioInputsResult>('list_audio_inputs'),
    ]);
    if (entries[0].status === 'fulfilled') setLanStatus(entries[0].value as LanStatus);
    const refreshedRig = entries[1].status === 'fulfilled'
      ? expandRigTopology(entries[1].value as RigSnapshot)
      : rig();
    if (entries[1].status === 'fulfilled') setLanRig(refreshedRig);
    try {
      setControllerState(await readStateSync(
        (params) => lanClient.call<StateSyncChunk | LiveState>('get_state', params, 20_000),
        refreshedRig,
      ));
    } catch (error) {
      setLanReason(reportError('lan', error).message);
    }
    if (entries[2].status === 'fulfilled') setLanScores((entries[2].value as { scores: ScoreSummary[] }).scores ?? []);
    if (entries[3].status === 'fulfilled') adoptScenes((entries[3].value as { scenes: SceneSummary[] }).scenes ?? [], 'controller');
    if (entries[4].status === 'fulfilled') setAudioInputs(entries[4].value);
  }

  async function refreshBluetooth(): Promise<void> {
    if (!bluetoothClient.connected) return;
    const entries = await Promise.allSettled([
      bluetoothClient.call<LanStatus>('get_status'),
      bluetoothClient.call<RigSnapshot>('get_rig', { projection: 'compact-v1' }, 20_000),
      bluetoothClient.call<{ scores: ScoreSummary[] }>('get_scores'),
      bluetoothClient.call<{ scenes: SceneSummary[] }>('list_scenes', { scope: 'room' }),
      bluetoothClient.call<ListAudioInputsResult>('list_audio_inputs'),
    ]);
    if (entries[0].status === 'fulfilled') setLanStatus(entries[0].value);
    const refreshedRig = entries[1].status === 'fulfilled' ? expandRigTopology(entries[1].value) : rig();
    if (entries[1].status === 'fulfilled') setLanRig(refreshedRig);
    if (refreshedRig) {
      try {
        setControllerState(await readStateSync(
          (params) => bluetoothClient.call<StateSyncChunk | LiveState>('get_state', params, 20_000),
          refreshedRig,
        ));
        await bluetoothClient.call('subscribe', { name: 'state_changed', projection: 'delta-v1' });
      } catch (error) {
        setBluetoothReason(reportError('bluetooth', error).message);
      }
    }
    if (entries[2].status === 'fulfilled') setLanScores(entries[2].value.scores ?? []);
    if (entries[3].status === 'fulfilled') adoptScenes(entries[3].value.scenes ?? [], 'controller');
    if (entries[4].status === 'fulfilled') setAudioInputs(entries[4].value);
  }

  /**
   * Ask the controller for its state until it answers, then stop.
   *
   * A single cloud round-trip to a Pi rendering 2,904 elements can exceed the relay's wait window, and
   * with no timer behind it a lost first answer used to leave the console permanently blank. This is a
   * bounded handshake — a handful of attempts with backoff, ending the moment live state arrives — not a
   * recurring read: once the room is on screen nothing here asks again.
   */
  /**
   * Fold the Pi's own published projection into live state.
   *
   * The cloud projection is authored/configured state: staged panel levels and semantic headlines. Rendered
   * effect frames never enter it; those belong only to the explicitly leased two-minute output preview.
   */
  function applyMirrorProjection(next: ControllerMirror | undefined, previous?: ControllerMirror): void {
    if (!next) return;
    if (next.transport !== undefined) {
      setTransportState((current) => next.transport == null
        ? undefined
        : {
          ...(current ?? {}),
          ...next.transport,
          ...(current?.positionSeconds !== undefined
            ? { positionSeconds: current.positionSeconds }
            : {}),
        } as TransportState);
    }
    if (Array.isArray(next.scenes?.favoriteSceneIds)) {
      setScenes((current) => mergeSceneFavorites(current, next.scenes!.favoriteSceneIds!));
    } else if (next.scenes === null && Array.isArray(previous?.scenes?.favoriteSceneIds)) {
      // The last favorite was removed. New controllers encode the now-empty scene headline as null;
      // only clear on an edge from the versioned array so an older controller cannot erase list data.
      setScenes((current) => mergeSceneFavorites(current, []));
    }
    setControllerState((current) => {
      if (!current) return current;
      const merged = { ...current };
      if (next.activeSource) {
        if (!next.activeSourceLabel && next.activeSource !== current.activeSource) merged.activeSourceLabel = undefined;
        merged.activeSource = next.activeSource;
      }
      if (next.activeSourceLabel) merged.activeSourceLabel = next.activeSourceLabel;
      if (typeof next.blackout === 'boolean') merged.blackout = next.blackout;
      if (typeof next.outputRateHz === 'number') merged.outputRateHz = next.outputRateHz;
      if (next.transport !== undefined) {
        merged.transport = next.transport == null
          ? null
          : {
            ...(current.transport ?? {}),
            ...next.transport,
            ...(current.transport?.positionSeconds !== undefined
              ? { positionSeconds: current.transport.positionSeconds }
              : {}),
          } as LiveState['transport'];
      }
      if (next.stream !== undefined) merged.stream = next.stream ?? undefined;
      if (next.remoteLive !== undefined) merged.remoteLive = next.remoteLive ?? undefined;
      // RTDB removes a null effect child, so a score or panel takeover can arrive with no effect
      // member. The active source then closes the previous full-state effect fallback.
      if (next.effect !== undefined || (next.activeSource && next.activeSource !== 'effect')) {
        const previousEffect = current.effects?.find((candidate) => (
          (candidate.id ?? candidate.effectId) === next.effect?.effectId
        ));
        merged.effects = next.effect?.running
          ? [{
            ...previousEffect,
            id: next.effect.effectId ?? '',
            kind: next.effect.kind ?? '',
            devices: next.effect.devices ?? 0,
            ...(next.effect.preset !== undefined ? { preset: next.effect.preset } : {}),
            ...(next.effect.health !== undefined ? { health: next.effect.health } : {}),
            ...(next.effect.stack !== undefined ? { stack: next.effect.stack } : {}),
            ...(next.effect.sync !== undefined ? { sync: next.effect.sync } : {}),
            ...(next.effect.package !== undefined ? {
              package: next.effect.package == null
                ? next.effect.package
                : { ...(previousEffect?.package as Record<string, unknown> | undefined), ...next.effect.package },
            } : {}),
          }]
          : [];
      }
      // A direct link already receives these deltas over its own socket, so only the hosted origin applies
      // the mirror's copy — applying both would make a simultaneously paired console reduce each change
      // twice and resync needlessly.
      if (controlTransport() !== 'cloud') return merged;
      const panel = next.panel;
      const currentRig = rig();
      if (!panel?.delta || !currentRig) return merged;
      const reduced = reduceStateDelta(merged, panel.delta as DeltaV1, currentRig);
      if (reduced.kind === 'applied') {
        // Following the room again, so whatever the device spent recovering is forgiven.
        resyncBudget.clear(selectedDeviceId() ?? '');
        return reduced.state;
      }
      // `duplicate` (a revision that does not move forward) and `awaiting-delta` (the controller elided the
      // data because the payload would have been too big) both mean: keep what we hold and wait. Neither is
      // a gap, and neither may buy a whole room — `awaiting-delta` used to fall into the branch below.
      if (reduced.kind !== 'resync') return reduced.state;
      // This branch exists only for the 1.7.19 root mirror. Its compatibility read may enter the old
      // command relay, but requestResync imposes a hard ceiling. The 1.7.20 authored tree has no deltas or
      // cross-node gaps and cannot reach this branch.
      requestResync(reduced.reason);
      return merged;
    });
    if (typeof next.maxIntensity === 'number') {
      const maxIntensity = next.maxIntensity;
      setLanStatus((current) => current ? { ...current, maxIntensity, engine: { ...(current.engine ?? {}), maxIntensity } } : current);
    }
  }

  /**
   * Hold the presence lease that lets the Pi publish at all.
   *
   * The mirror is presence-gated because Realtime Database bills for delivered bytes, so an unwatched
   * controller must cost nothing. The lease is short and self-expiring — a refresh every 30 s while a room
   * is open, released when the room closes or the console signs out — which is why a crashed tab cannot
   * leave a Pi publishing forever. This is a heartbeat, not a read: it never asks the controller anything.
   */
  function watchControllerState(deviceId: string): void {
    if (!cloudBindingReady()) return;
    if (presenceDeviceId === deviceId) return;
    releaseControllerState();
    presenceDeviceId = deviceId;
    const refresh = () => {
      if (presenceDeviceId !== deviceId || !user() || !cloudBindingReady()) return;
      void cloudCall('subscribeControllerState', { deviceId }).catch((error) => {
        reportError('controller state subscription', error);
      });
    };
    refresh();
    presenceTimer = globalThis.setInterval(refresh, 30_000) as unknown as number;
  }

  function releaseControllerState(): void {
    if (presenceTimer != null) globalThis.clearInterval(presenceTimer);
    presenceTimer = undefined;
    const previous = presenceDeviceId;
    presenceDeviceId = '';
    if (previous && user()) {
      void cloudCall('subscribeControllerState', { deviceId: previous, release: true }).catch(() => undefined);
    }
  }

  async function establishControllerState(): Promise<void> {
    if (!piControllerReady()) return;
    if (controllerHandshake) return controllerHandshake;
    controllerHandshake = (async () => {
      for (const [attempt, delay] of [0, 800, 1_600, 3_200, 6_000].entries()) {
        if (!user() || !selectedDeviceId() || controlTransport() !== 'cloud') return;
        if (delay) await new Promise((resolve) => globalThis.setTimeout(resolve, delay));
        await refreshController().catch(() => undefined);
        // Both halves of what this handshake fetches, because they arrive on different channels and
        // either can be missing on its own: `controllerState` comes from the authored leaves (so it is
        // usually already there before the first read returns) and `lanStatus` from `get_status` alone.
        // Testing `controllerState` alone declared victory on a leaf this loop never fetched, so a
        // `get_status` that really had been superseded was dropped and never asked for again.
        if (controllerState() && lanStatus()) return;
        if (attempt === 4) console.error('[nuance-venue] the controller never returned its live state');
      }
    })();
    try {
      await controllerHandshake;
    } finally {
      controllerHandshake = undefined;
    }
  }

  async function refreshController(): Promise<void> {
    if (!piControllerReady()) return;
    if (controlTransport() === 'lan') {
      await refreshLan();
      return;
    }
    if (controlTransport() === 'bluetooth') {
      await refreshBluetooth();
      return;
    }
    if (controlTransport() !== 'cloud' || !user() || !selectedDeviceId()) return;
    const deviceId = selectedDeviceId();
    if (controllerRefresh && controllerRefreshDevice === deviceId) return controllerRefresh;
    controllerRefreshDevice = deviceId;
    const requestedAtEpoch = controlEpoch;
    controllerRefresh = (async () => {
      const requests: Array<[string, Promise<unknown>]> = [
        ['get_status', sendCloudController<LanStatus>('get_status', {}, true)],
        ['get_scores', sendCloudController<{ scores: ScoreSummary[] }>('get_scores', {}, true)],
        ['list_scenes', sendCloudController<{ scenes: SceneSummary[] }>('list_scenes', { scope: 'room' }, true)],
        ['list_audio_inputs', sendCloudController<ListAudioInputsResult>('list_audio_inputs', {}, true)],
      ];
      await Promise.allSettled(requests.map(async ([name, request]) => {
        try {
          const result = await request;
          if (selectedDeviceId() !== deviceId) return;
          // The epoch travels WITH the answer; which of its fields a moved epoch disqualifies is decided
          // where those fields are read, not here. This loop knows the four reads' names and nothing
          // about what they carry.
          applyControllerResult(name, result, requestedAtEpoch);
        } catch (error) {
          // A silently swallowed refresh is how a blank console hides a real failure. The operator sees
          // nothing yet (the screen simply has not filled in), but whoever is debugging sees the cause.
          reportError(`controller refresh (${name})`, error);
          throw error;
        }
      }));
    })();
    try {
      await controllerRefresh;
    } finally {
      if (controllerRefreshDevice === deviceId) {
        controllerRefresh = undefined;
        controllerRefreshDevice = '';
      }
    }
  }

  function scheduleGesture(slot: GestureSlot): void {
    if (slot.inFlight || slot.timer != null || (!slot.params && !slot.commits.length)) return;
    const delay = slot.commits.length ? 0 : Math.max(0, 100 - (Date.now() - slot.lastSentAt));
    slot.timer = globalThis.setTimeout(() => {
      slot.timer = undefined;
      void flushGesture(slot);
    }, delay) as unknown as number;
  }

  /**
   * Write one keep-last intent slot straight to the Realtime Database, or clear it.
   *
   * **This is the only place the console writes to the database at all**, and it is the only console
   * path with no cloud function in front of it. That has one consequence worth stating where it cannot
   * be missed: `database.rules.json` is the ENTIRE authorization for these writes. The relay's role
   * checks, its queue admission, its audit row and its refusal channel are all absent here — the rules'
   * uid match, venue-member role hop, schema literal, size ceiling and rate floor are what stand between
   * a signed-in browser and somebody else's room. A change to what this writes is a change to those
   * rules, in the same round, or it is a hole.
   *
   * Clearing is `set(ref, null)`, which is what a database delete IS — so there is exactly one write
   * call site rather than a set path and a remove path that could drift.
   *
   * Nothing waits on it. A drag sample that loses a race with the next sample is meant to lose: the slot
   * is keep-last and the newer value is the one the room should be at. A failure is reported to whoever
   * is debugging and to nobody else — an operator has no action to take, the room is being driven by the
   * release regardless, and the addendum's rule holds: no new on-screen words.
   */
  function writeControllerIntent(deviceId: string, controlKey: string, slot: ControllerIntent | null): void {
    const rigId = selectedRigId();
    if (!rigId) return;
    void databaseSet(databaseRef(realtime, `controllerIntent/${deviceId}/${rigId}/${controlKey}`), slot)
      .catch((error: unknown) => {
        console.warn('[nuance-venue] controller intent write refused', JSON.stringify({
          controlKey, cleared: slot === null, reason: String(error),
        }));
      });
  }

  /**
   * The name every party outside this tab knows one control by.
   *
   * It has to satisfy the database rules' `[A-Za-z0-9_-]{1,120}`, and the gesture's local key is not
   * that shape — it is `${command}:${targetKey}`, and a target key is whatever the screen that owns the
   * control made of it (`pixels:12:dv_tape:0:dv_tape:2`, an effect's parameter name, a fingerprint). So
   * it is DERIVED by hashing rather than filtered: a character filter collides two different targets
   * onto one name the first time a screen invents a new target vocabulary, and a collision here means
   * one control's drag overwriting another's. The command name is kept in front of the digest so a slot
   * is legible in the database console; every command this console sends is `[a-z_]+`.
   *
   * The SAME string is the intent slot's database key and the `coalesceKey` of every command the
   * gesture sends. That is not tidiness: the Pi files its per-control high-water — the `at` of the last
   * committed command it applied, which is how it drops a still-buffered older sample that would
   * otherwise land after the release — under the release's `coalesceKey`. Two strings meaning "which
   * control is this" could only drift, and a drift there makes that guard silently never match.
   */
  function intentControlKey(name: string, key: string): string {
    return `${name}_${fnv1a36(key)}`;
  }

  /** Put this drag sample in the room's intent slot, overwriting whatever the last sample left there. */
  function sendGestureIntent(slot: GestureSlot, params: Record<string, unknown>): void {
    const deviceId = selectedDeviceId();
    const uid = user()?.uid;
    if (!deviceId || !uid) return;
    // The database stamps `at` as it applies the write. Nothing here reads a clock.
    const intent = controllerIntentSlot(uid, slot.name, params, rigElementCounts(rig()), serverTimestamp());
    if (!intent) {
      if (!slot.oversizeReported) {
        slot.oversizeReported = true;
        console.warn('[nuance-venue] gesture samples cannot be encoded as an intent slot', JSON.stringify({
          command: slot.name, controlKey: slot.controlKey,
        }));
      }
      return;
    }
    slot.intentLive = true;
    writeControllerIntent(deviceId, slot.controlKey, intent);
  }

  /**
   * Put this drag sample on the room's open link (S17, D16), keep-last per control on the controller exactly
   * as the database slot is. True when the link took the sample — sent, or too large for the link's 4,096-byte
   * cap and skipped (the release still commits the value, and the skip is recorded once per gesture, as for an
   * oversized slot). False when there is no open link: the caller writes the database slot.
   */
  function sendLinkIntent(slot: GestureSlot, params: Record<string, unknown>): boolean {
    const link = roomLink();
    const rigId = selectedRigId();
    if (!link || link.state() !== 'open' || !rigId) return false;
    const masked = maskIntentParams(params, rigElementCounts(rig()));
    const sent = masked !== undefined && link.intent({ rigId, controlKey: slot.controlKey, command: slot.name,
      paramsJson: JSON.stringify(masked) });
    if (!sent && !slot.oversizeReported) {
      slot.oversizeReported = true;
      console.warn('[nuance-venue] gesture samples cannot be encoded as a link intent', JSON.stringify({
        command: slot.name, controlKey: slot.controlKey,
      }));
    }
    return true;
  }

  /**
   * Take this gesture's intent slot out of the room.
   *
   * Called BEFORE the release is sent, which is the whole ordering rule: with the slot gone there is no
   * sample left in the database that could be applied after the value the operator let go on.
   */
  function clearGestureIntent(slot: GestureSlot): void {
    if (!slot.intentLive) return;
    slot.intentLive = false;
    const deviceId = selectedDeviceId();
    if (deviceId) writeControllerIntent(deviceId, slot.controlKey, null);
  }

  async function flushGesture(slot: GestureSlot): Promise<void> {
    if (slot.inFlight || (!slot.params && !slot.commits.length)) return;
    const commit = slot.commits.shift();
    const params = commit?.params ?? slot.params!;
    const final = commit != null;
    const onApplied = commit?.onApplied;
    const onRejected = commit?.onRejected;
    const onUnanswered = commit?.onUnanswered;
    if (!commit) slot.params = undefined;
    slot.lastSentAt = Date.now();
    controlEpoch += 1;
    showControlFeedback(commandSent(slot.name));
    const selectedTransport = controlTransport();
    const attemptedCloud = selectedTransport === 'cloud';
    // A drag sample is a keep-last slot the console writes straight to the database and the Pi applies
    // every value of, so the room moves WHILE the operator slides rather than on release (owner ruling
    // 2026-09-17). It costs no cloud function, nothing serializes on it, and nothing waits for it: an
    // older sample losing a race with a newer one is the slot working as intended.
    if (!final && selectedTransport === 'cloud' && user() && selectedDeviceId()) {
      // With the room's link open the sample travels on it (D16); otherwise it is the database slot.
      if (!sendLinkIntent(slot, params)) sendGestureIntent(slot, params);
      if (slot.params || slot.commits.length) scheduleGesture(slot);
      return;
    }
    // Everything past here either commits this gesture or sends it down a wire that is not the cloud, and
    // in both cases the intent slot must come OUT first. Before the release, because with no sample left
    // in the database there is nothing that could be applied after the value the operator let go on —
    // that single ordering is what replaces the barrier the old editing path needed (each sample was an
    // awaited callable and a late one could overtake the final value; a keep-last slot has nothing to
    // wait for). And before a direct send, because a drag that began on the cloud and finishes over the
    // cable — the cloud dropping mid-gesture is exactly when that happens — would otherwise leave a live
    // sample in the room for the Pi to apply after the cable's own release. It is a no-op when this
    // gesture never wrote one.
    clearGestureIntent(slot);
    slot.inFlight = true;
    try {
      if (selectedTransport === 'lan' || selectedTransport === 'bluetooth') {
        const client = selectedTransport === 'lan' ? lanClient : bluetoothClient;
        const result = await client.call(slot.name, params);
        applyControllerResult(slot.name, result);
        announceCloudActivity(slot.name, params, slot.controlKey, final ? 'committed' : 'editing');
        if (final) {
          showControlFeedback(commandApplied(slot.name));
          onApplied?.(result);
        }
        return;
      }
      if (selectedTransport !== 'cloud' || !user() || !selectedDeviceId()) {
        throw new Error('Choose an online controller first.');
      }
      // A repeated final set_fixture_params value can legitimately produce a delta ack because the
      // non-blocking editing write already landed. Ask for the legacy touched-fixture echo on release so
      // the realtime command mirror still carries the authoritative final value to peer consoles.
      const sentParams = final && slot.name === 'set_fixture_params' && params.projection === 'delta-v1'
        ? Object.fromEntries(Object.entries(params).filter(([key]) => key !== 'projection'))
        : params;
      if (final && roomConfirms(slot.name)) {
        // The release of a level gesture waits for the ROOM, not for the relay. History is recorded on
        // the same terms as the screen keeps the level: a commit that the cloud has not reported on is
        // still a commit the operator made and the room applied, so `onApplied` runs either way and only
        // a refusal (which throws, below) withholds it.
        const outcome = await sendRoomConfirmed(slot.name, sentParams, slot.controlKey);
        if (outcome.confirmed) showControlFeedback(commandApplied(slot.name));
        else clearControlFeedback();
        onApplied?.(outcome);
        return;
      }
      const receipt = await sendCloudControllerReceipt(
        slot.name, sentParams, final, slot.controlKey, final ? 'committed' : 'editing',
      );
      if (final) {
        const result = applyCloudCommandAck(slot.name, sentParams, receipt);
        showControlFeedback(commandApplied(slot.name));
        onApplied?.(result);
      }
    } catch (error) {
      let failure = error;
      // Same rule as `controllerCall`'s: the retry follows the link the operator has, not the browser's
      // opinion of Firebase.
      const fallback = directControllerReady() ? directTransport() : undefined;
      if (attemptedCloud && fallback) {
        const client = fallback === 'lan' ? lanClient : bluetoothClient;
        try {
          const result = await client.call(slot.name, params);
          applyControllerResult(slot.name, result);
          announceCloudActivity(slot.name, params, slot.controlKey, final ? 'committed' : 'editing');
          if (final) {
            showControlFeedback(commandApplied(slot.name));
            onApplied?.(result);
          }
          return;
        } catch (fallbackError) {
          failure = fallbackError;
        }
      }
      if (relayVerdict(failure) !== 'refused') {
        // The cloud stopped reporting on a command the room has almost certainly already performed. Every
        // gesture caller's `onRejected` is a ROLLBACK — the effect panel snaps a knob back, the sync
        // switch flips itself off again, the audio stack drops the layer just added — so calling it here
        // would undo a change that landed, and the sentence beside it would be a false accusation against
        // the room. Say nothing instead: the screen keeps what the operator did, and the room's own
        // reported state is what corrects it.
        //
        // Note for anyone writing a guard against this: do NOT test for a particular sentence. `rollBack`
        // prints whatever `plainErrors` makes of the error, and a relay timeout maps to its own wording
        // ("did not answer in time") rather than to the panel's refusal sentence — so asserting that the
        // refusal wording is absent passes straight through the regression. The invariant is that there
        // is no sentence at all.
        //
        // Saying nothing is not the same as saying nothing HAPPENED, and that is what `onUnanswered` is
        // for. The gesture is over: no answer is coming, and a caller that took a hold on its control
        // when it sent this — the audio stack's panel is the one that does — would otherwise hold it for
        // the rest of the session and go on showing an edit the room was never confirmed to have made.
        // It is deliberately NOT `onRejected`: the caller must let the ROOM correct the control, never
        // take the operator's edit back itself.
        clearControlFeedback();
        onUnanswered?.(failure);
        return;
      }
      const step = commandFailed(slot.name, failure);
      onRejected?.(failure);
      showControlFeedback(step);
      toast(step.feedback.message, 'error');
    } finally {
      slot.inFlight = false;
      if (slot.params || slot.commits.length) scheduleGesture(slot);
      else gestureSlots.delete(slot.key);
    }
  }

  function controllerGesture(
    name: string,
    params: Record<string, unknown>,
    targetKey: string,
    final = false,
    onApplied?: (result: unknown) => void,
    onRejected?: (error: unknown) => void,
    onUnanswered?: (error: unknown) => void,
  ): void {
    if (!canControl()) {
      toast("Your venue role doesn't allow controls.", 'error');
      return;
    }
    if (!piControllerReady()) {
      onRejected?.(new Error('Change not applied.'));
      return;
    }
    const selectedTransport = controlTransport();
    if (selectedTransport === 'none'
        || (selectedTransport === 'cloud' && (!user() || !selectedDeviceId()))) {
      toast('Choose an online controller first.', 'error');
      return;
    }
    const key = `${name}:${targetKey}`.slice(0, 120);
    const slot = gestureSlots.get(key) ?? {
      name,
      key,
      controlKey: intentControlKey(name, key),
      commits: [],
      intentLive: false,
      oversizeReported: false,
      inFlight: false,
      lastSentAt: 0,
    };
    if (final) {
      // Editing samples are keep-latest, but every pointer/key release is a distinct committed
      // gesture. Queue those finals in order so a slow direct link cannot apply an older sample after
      // Undo, and so rapid consecutive gestures still produce one acknowledged history entry each.
      slot.params = undefined;
      slot.commits.push({ params, onApplied, onRejected, onUnanswered });
      if (slot.timer != null) {
        globalThis.clearTimeout(slot.timer);
        slot.timer = undefined;
      }
    } else {
      slot.params = params;
    }
    gestureSlots.set(key, slot);
    scheduleGesture(slot);
  }

  function sendPanelGesture(
    params: Record<string, unknown>, coalesceKey: string, final = false,
    onApplied?: (result: unknown) => void, onRejected?: (error: unknown) => void,
    onUnanswered?: (error: unknown) => void,
  ): void {
    if (!microRecordWrites()) {
      controllerGesture('set_fixture_params', params, coalesceKey, final, onApplied, onRejected, onUnanswered);
      return;
    }
    if (!controlAvailable()) {
      onRejected?.(new Error('Change not applied.'));
      return;
    }
    if (!final) return;
    void sendRecordControl('set_fixture_params', () => writePanelRecords(params)).then(() => {
      onApplied?.({ confirmed: true, expiresAt: Date.now() });
    }).catch((error) => onRejected?.(error));
  }

  async function connectLan(address = lanAddress()): Promise<void> {
    if (!address.trim()) throw new Error('Enter the controller address.');
    setLanAddress(address.trim());
    localStorage.setItem('nuance-venue:lan-address', address.trim());
    try {
      const hello = await lanClient.connect(address.trim());
      setLanHello(hello);
      applyDirectSelection(selectionAfterPairing(hello, consoleSelection()));
      if (hello.authenticated && controlTransport() === 'lan') await refreshLan();
    } catch (error) {
      if (lanState() !== 'reconnecting') setLanState('failed');
      setLanReason(reportError('lan', error, "Couldn't reach the controller.").message);
      throw error;
    }
  }

  async function pairLan(setupCode: string): Promise<void> {
    await lanClient.pair(setupCode);
    const hello = await lanClient.call<LanHello>('hello');
    setLanHello(hello);
    applyDirectSelection(selectionAfterPairing(hello, consoleSelection()));
    if (controlTransport() === 'lan') await refreshLan();
  }

  function disconnectLan(): void {
    const wasActive = controlTransport() === 'lan';
    lanClient.disconnect();
    setLanHello(undefined);
    if (wasActive) void refreshController().catch(() => undefined);
  }

  async function connectBluetooth(setupCode: string): Promise<void> {
    if (!setupCode.trim()) throw new Error('Enter the controller setup code.');
    setBluetoothReason('');
    const identity = await bluetoothClient.connect({
      setupCode,
      appDeviceId: lanClient.appDeviceId,
      displayName: `Web console · ${user()?.email ?? 'local operator'}`,
    });
    setBluetoothIdentity(identity);
    // A Bluetooth setup result names a controller and no room. Pairing with it therefore drives it
    // without claiming to be the remembered choice of whatever venue room happened to be on screen.
    applyDirectSelection(selectionAfterPairing(identity, consoleSelection()));
    if (controlTransport() === 'bluetooth') await refreshBluetooth();
  }

  async function disconnectBluetooth(): Promise<void> {
    const wasActive = controlTransport() === 'bluetooth';
    await bluetoothClient.disconnect();
    setBluetoothIdentity(undefined);
    setBluetoothReason('');
    if (wasActive) await refreshController();
  }

  async function setMaxIntensity(value: number): Promise<void> {
    const next = Math.max(0, Math.min(1, value));
    if (microRecordWrites()) {
      await writeRigRecord('output', [], { op: 'set', set: [{ path: 'ceiling', value: next }] });
      return;
    }
    const direct = controlTransport() !== 'cloud';
    const result = await controllerCall<{ maxIntensity: number }>('set_max_intensity', { value: next });
    setLanStatus((current) => ({ ...(current ?? {}), maxIntensity: result?.maxIntensity ?? next }));
    if (!direct && user() && selectedDeviceId()) {
      await cloudCall('setDeviceOutputConfig', { deviceId: selectedDeviceId(), maxIntensity: next });
      setDeviceDoc((current) => ({ ...(current ?? {}), maxIntensity: next }));
    }
    setLanStatus((current) => current ? {
      ...current,
      maxIntensity: result?.maxIntensity ?? next,
      engine: { ...(current.engine ?? {}), maxIntensity: result?.maxIntensity ?? next },
    } : current);
  }

  async function setRemoteLivePolicy(enabled: boolean): Promise<void> {
    const venueId = selectedVenueId();
    if (!venueId) throw new Error('Choose a venue first.');
    const result = await cloudCall<{
      remoteLive: RemoteLivePolicy;
    }>('setVenueRemoteLivePolicy', {
      venueId,
      remoteLive: { enabled },
    });
    setVenues((current) => current.map((item) => item.venueId === venueId ? {
      ...item,
      remoteLive: result.remoteLive,
    } : item));
    setRoomStatus((current) => current ? {
      ...current,
      remoteLive: result.remoteLive,
    } : current);
  }

  // ── the team ────────────────────────────────────────────────────────────────────────────────────
  // Four thin forwards onto the cloud callables. The rules about WHO may do each of these live entirely
  // in the platform (a manager runs the team but never touches the owner); the console reads the same
  // table through `can()` to decide what to show, and never re-implements the rule.
  function requireVenue(): string {
    const venueId = selectedVenueId();
    if (!venueId) throw new Error('Choose a venue first.');
    return venueId;
  }

  async function listVenueMembers(): Promise<VenueMember[]> {
    const result = await cloudCall<{ members: VenueMember[] }>('listVenueMembers', { venueId: requireVenue() });
    return result.members;
  }

  async function listVenueInvites(): Promise<VenueInvite[]> {
    const result = await cloudCall<{ invites: VenueInvite[] }>('listVenueInvites', { venueId: requireVenue() });
    return result.invites;
  }

  /**
   * One call covers both people: the platform materializes the membership when the email already has an
   * account, and writes a pending invite (with its Accept email) when it does not. `addVenueMember` — which
   * refused an address with no account — is not a console capability; there is nothing an operator could do
   * with the refusal except send the invite this sends.
   */
  async function inviteVenueMember(email: string, memberRole: AssignableRole): Promise<InviteOutcome> {
    return cloudCall<InviteOutcome>('inviteVenueMember', { venueId: requireVenue(), email, role: memberRole });
  }

  async function cancelVenueInvite(inviteId: string): Promise<void> {
    await cloudCall('cancelVenueInvite', { venueId: requireVenue(), inviteId });
  }

  // ── the room's named looks ──────────────────────────────────────────────────────────────────────
  // Four thin forwards, like the team's. The library itself arrives through the live listener above, so
  // none of these re-reads it; they exist to make the WRITE, which only the cloud is allowed to make.
  function requireRoom(): string {
    const roomId = selectedRoomId();
    if (!roomId) throw new Error('Choose a room first.');
    return roomId;
  }

  async function saveEffectPreset(
    packageId: string,
    name: string,
    values: Record<string, EffectPackageParameterValue>,
    audio?: { stack?: EffectStack },
  ): Promise<EffectPreset> {
    return cloudCall<EffectPreset>('saveEffectPreset', {
      roomId: requireRoom(), packageId, name, values, ...audio,
    });
  }

  /**
   * The cloud's answer is read the same way a stored preset is, rather than cast.
   *
   * This is the boundary where a cloud answer becomes an app value, and a look can now carry a layer
   * stack — an ordered list of objects, not a scalar. A recalled stack the console cannot read must cost
   * that recall its stack, never leave a malformed list to be sent back at the room as a stack edit.
   */
  async function applyEffectPreset(presetId: string): Promise<EffectPreset> {
    const answer = await cloudCall<unknown>('applyEffectPreset', { roomId: requireRoom(), presetId });
    return parseEffectPreset(answer) ?? { presetId, packageId: '', name: '', values: {} };
  }

  async function renameEffectPreset(presetId: string, name: string): Promise<EffectPreset> {
    return cloudCall<EffectPreset>('renameEffectPreset', {
      roomId: requireRoom(), presetId, name,
    });
  }

  async function deleteEffectPreset(presetId: string): Promise<void> {
    await cloudCall('deleteEffectPreset', { roomId: requireRoom(), presetId });
  }

  async function setMemberRole(uid: string, memberRole: AssignableRole): Promise<void> {
    await cloudCall('setMemberRole', { venueId: requireVenue(), uid, role: memberRole });
  }

  async function removeVenueMember(uid: string): Promise<void> {
    await cloudCall('removeVenueMember', { venueId: requireVenue(), uid });
  }

  async function endRemoteLive(): Promise<void> {
    // From a linked console, ending Remote Live is the controller's own `remote_live_end` on the link (D9).
    if (roomLinkState() === 'open') {
      await controllerCall('remote_live_end');
      return;
    }
    const targetRigId = selectedRigId();
    const sessionId = roomDoc()?.remoteLiveLocks?.[targetRigId]?.sessionId
      ?? remoteSession()?.id
      ?? roomStatus()?.remoteLiveLocks?.[targetRigId]?.sessionId;
    if (!sessionId) return;
    await cloudCall('endRemoteLiveSession', {
      venueId: selectedVenueId(),
      roomId: selectedRoomId(),
      targetRigId,
      sessionId,
      reason: 'ended-by-web-console',
    });
    await loadRoomDetails(selectedVenueId(), selectedRoomId());
  }

  async function takeControl(): Promise<void> {
    const ownership = controlOwnership();
    if (!ownership) return;
    if (ownership.kind === 'remote') {
      const targetRigId = selectedRigId();
      const sessionId = roomDoc()?.remoteLiveLocks?.[targetRigId]?.sessionId
        ?? remoteSession()?.id
        ?? roomStatus()?.remoteLiveLocks?.[targetRigId]?.sessionId;
      if (roomLinkState() === 'open' || (sessionId && user())) await endRemoteLive();
      else if (lanClient.connected || bluetoothClient.connected) await controllerCall('remote_live_end');
      else throw new Error('The Nuance stream session is unavailable. Try again when the room reconnects.');
    } else {
      // A Nuance LAN stream is the one exclusive source. End its sessions explicitly so a sender that
      // keeps transmitting cannot retake the room; the daemon restores PANEL in the same operation.
      await controllerCall('end_stream');
    }
    if (bluetoothClient.connected) await refreshBluetooth();
    else if (lanClient.connected) await refreshLan();
  }

  async function finishSignIn(current: User): Promise<void> {
    // `createAccount` owns this sequence for an account being created: it has to write the display name
    // onto the Auth record before `ensureAccount` copies it into `users/{uid}`, which happens exactly
    // once. Letting the auth-state listener race ahead would bank an empty name permanently.
    if (creatingAccount) return;
    setUser(current);
    setEmailVerified(current.emailVerified);
    setAuthError('');
    await dispatch.call('ensureAccount').catch(() => undefined);
    if (current.emailVerified) await claimInvites();
    await refreshCloud();
  }

  /**
   * Claim every pending invite addressed to this account's verified emails, and land in the venue the
   * person came for.
   *
   * Selecting the venue BEFORE `refreshCloud` matters: `refreshCloud` keeps the current selection if it
   * is in the list and otherwise takes the first venue, so setting it here is what makes someone who
   * followed an invite link open the venue that invited them rather than whichever venue sorts first.
   */
  async function claimInvites(): Promise<void> {
    const result = await dispatch
      .call<{ claimed?: ClaimedInvite[] }>('claimPendingInvites')
      .catch(() => undefined);
    if (!result) return;
    const venueId = venueToSelectAfterClaim(result.claimed ?? [], pendingInviteId());
    if (venueId) {
      setSelectedVenueId(venueId);
      localStorage.setItem('nuance-venue:venue', venueId);
    }
    clearPendingInvite();
  }

  /**
   * An account exists the moment it is created but is unusable until the link in the person's inbox is
   * opened — in another tab, or on their phone. Nothing tells this tab, and Firebase only refreshes
   * `emailVerified` when the user record is re-read, so while the console is holding at the verification
   * state it re-reads it and lets them in the moment it flips.
   */
  async function checkVerification(): Promise<void> {
    const current = auth.currentUser;
    if (!current || current.emailVerified) return;
    await current.reload().catch(() => undefined);
    const refreshed = auth.currentUser;
    if (!refreshed?.emailVerified) return;
    await finishSignIn(refreshed);
  }

  createEffect(() => {
    if (!awaitingVerification()) return;
    const timer = window.setInterval(() => void checkVerification(), VERIFICATION_POLL_MS);
    onCleanup(() => window.clearInterval(timer));
  });

  /**
   * Create an email/password account, name it, and send its verification email.
   *
   * The console does not let the new account in yet — `awaitingVerification` holds it at the sign-in
   * card until the address is proven, which is the same property the platform enforces server-side
   * (`claimPendingInvites` only ever claims invites for VERIFIED emails).
   */
  async function createAccount(name: string, email: string, password: string): Promise<void> {
    setAuthBusy(true);
    setAuthError('');
    creatingAccount = true;
    try {
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const displayName = name.trim();
      if (displayName) await updateProfile(credential.user, { displayName });
      await sendEmailVerification(credential.user, emailActionSettings());
      creatingAccount = false;
      await finishSignIn(credential.user);
    } catch (error) {
      const plain = plainAccountError(error, ACCOUNT_CREATE_FAILURE);
      reportError('create-account', error, plain.message);
      setAuthError(plain.message);
      throw error;
    } finally {
      creatingAccount = false;
      setAuthBusy(false);
    }
  }

  /** Send the verification email again — the first one bounced off a spam filter or was deleted. */
  async function resendVerification(): Promise<void> {
    const current = auth.currentUser;
    if (!current) return;
    setAuthBusy(true);
    setAuthError('');
    try {
      await sendEmailVerification(current, emailActionSettings());
    } catch (error) {
      const plain = plainAccountError(error, VERIFICATION_SEND_FAILURE);
      reportError('resend-verification', error, plain.message);
      setAuthError(plain.message);
      throw error;
    } finally {
      setAuthBusy(false);
    }
  }

  /**
   * Send a password reset email.
   *
   * With email enumeration protection on — it is, on both projects — an address with no account
   * resolves here exactly like one that has an account, and no email is sent. That is the point of the
   * setting, and it is why the confirmation this resolves into cannot promise an email arrived. With
   * the protection off, `auth/user-not-found` surfaces as a plain sentence instead.
   */
  async function sendPasswordReset(email: string): Promise<void> {
    setAuthBusy(true);
    setAuthError('');
    try {
      await sendPasswordResetEmail(auth, email.trim(), emailActionSettings());
    } catch (error) {
      const plain = plainAccountError(error, PASSWORD_RESET_FAILURE);
      reportError('password-reset', error, plain.message);
      setAuthError(plain.message);
      throw error;
    } finally {
      setAuthBusy(false);
    }
  }

  /** Drop a stale refusal when the sign-in card moves to a different question. */
  function clearAuthError(): void {
    setAuthError('');
  }

  async function signInEmail(email: string, password: string): Promise<void> {
    setAuthBusy(true);
    setAuthError('');
    try {
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      await finishSignIn(credential.user);
    } catch (error) {
      const message = /invalid-credential|wrong-password|user-not-found/i.test(String(error))
        ? 'The email or password is not correct.'
        : plainSignInError(error).message;
      setAuthError(message);
      throw error;
    } finally {
      setAuthBusy(false);
    }
  }

  async function signInGoogle(): Promise<void> {
    setAuthBusy(true);
    setAuthError('');
    try {
      const credential = await signInWithPopup(auth, new GoogleAuthProvider());
      await finishSignIn(credential.user);
    } catch (error) {
      const plain = plainSignInError(error);
      reportError('sign-in', error, plain.message);
      setAuthError(plain.message);
      throw error;
    } finally {
      setAuthBusy(false);
    }
  }

  async function signOut(): Promise<void> {
    clearPanelHistories();
    disconnectLan();
    clearSubscriptions();
    await firebaseSignOut(auth);
    setVenues([]);
    setRooms([]);
    setRoomStatus(undefined);
    resetAudioRoomState();
  }

  function setRoute(next: RouteName): void {
    if (route() === next) return;
    location.hash = `/${next}`;
  }

  onMount(() => {
    const legacyRoute = location.hash.replace(/^#\/?/, '').split(/[/?]/)[0];
    if (legacyRoute === 'panel' || legacyRoute === 'room') {
      history.replaceState(history.state, '', `${location.pathname}${location.search}#/scenes`);
      setRouteSignal('scenes');
    }
    const hashListener = () => setRouteSignal(routeFromHash());
    const onlineListener = () => setCloudReachable(true);
    const offlineListener = () => setCloudReachable(false);
    window.addEventListener('hashchange', hashListener);
    window.addEventListener('online', onlineListener);
    window.addEventListener('offline', offlineListener);
    if (localOrigin) setAuthReady(true);
    const unsubscribeAuth = onAuthStateChanged(auth, (current) => {
      setUser(current);
      setEmailVerified(current?.emailVerified === true);
      setAuthReady(true);
      if (current) {
        setCloudReachable(navigator.onLine);
        watchCloudConnectivity();
        void finishSignIn(current);
      } else {
        connectivityUnsubscribe?.();
        connectivityUnsubscribe = undefined;
        if (!localOrigin) {
          lanClient.disconnect('signed out');
          setLanHello(undefined);
        }
        setVenues([]);
        setRooms([]);
        resetAudioRoomState();
      }
    });
    lanClient.onNotification = (notification) => handleNotification(notification, 'lan');
    bluetoothClient.onNotification = (notification) => handleNotification(notification, 'bluetooth');
    bluetoothClient.onIdentity = setBluetoothIdentity;
    bluetoothClient.onState = (state, reason) => {
      setBluetoothState(state);
      setBluetoothReason(reason ?? '');
      if (state === 'connected' && bluetoothClient.connected && controlTransport() === 'bluetooth') void refreshBluetooth();
    };
    lanClient.onHello = (hello) => {
      setLanHello(hello);
      applyDirectSelection(selectionAfterHello(hello, consoleSelection()));
    };
    lanClient.onState = (state, reason) => {
      setLanState(state);
      setLanReason(reason ?? '');
      if (state === 'connected' && lanClient.connected && lanHello()?.authenticated && controlTransport() === 'lan') void refreshLan();
    };
    if (defaultAddress && (localOrigin || queryAddress)) {
      globalThis.setTimeout(() => void connectLan(defaultAddress).catch(() => undefined), 100);
    }
    // NO TIMERS. Reading the Pi through the cloud costs a ~240 KB whole-room marshal on the large rig.
    // A 1.7.20 cloud console therefore observes only its screen's complete authored-tree leaves; heartbeat,
    // collaboration and command acknowledgements remain exact children of controllerState. The 1.7.19
    // expand path gets one bounded join read. Venue-network and Bluetooth origins use state_changed.
    onCleanup(() => {
      window.removeEventListener('hashchange', hashListener);
      window.removeEventListener('online', onlineListener);
      window.removeEventListener('offline', offlineListener);
      unsubscribeAuth();
    });
  });

  createEffect(() => {
    const activeDevice = selectedDeviceId();
    if (!activeDevice || !selectedVenueId() || !selectedRoomId() || !user() || !cloudBindingReady()) return;
    subscribeDetails(selectedVenueId(), selectedRoomId(), activeDevice);
    watchControllerState(activeDevice);
    if (!controllerState()) void establishControllerState();
  });

  // Opening a screen is an explicit action, and the screens that show the room need its state.
  createEffect(() => {
    const screen = route();
    if (cloudStateMode === 'tree') void reconcileAuthoredSubscriptions();
    if (!user() || !selectedDeviceId() || controllerState()) return;
    if (['scenes', 'scores', 'effects', 'music', 'status'].includes(screen)) void establishControllerState();
  });

  /**
   * The session link's lifecycle (D1, B116): one link per (this tab, the open room's controller), opened when
   * the room is open on the cloud transport and its controller publishes `controllerRoomLink`, closed on a room
   * or controller change, sign-out, a direct connection taking over, or the capability going away. A controller
   * without the capability (the Micro, a 1.10.16 Pi) never gets a link: the console behaves exactly as before.
   */
  let roomLinkKey = '';
  createEffect(() => {
    const device = deviceDoc();
    const deviceId = selectedDeviceId();
    const venueId = selectedVenueId();
    const roomId = selectedRoomId();
    const eligible = Boolean(user()) && Boolean(venueId && roomId && deviceId) && controlTransport() === 'cloud'
      && device?.id === deviceId && roomLinkOffered(device);
    const key = eligible ? `${venueId}:${roomId}:${deviceId}:${String(device!.controllerRoomLink)}` : '';
    if (key === roomLinkKey) return;
    roomLinkKey = key;
    untrack(() => {
      closeRoomLink();
      if (key) openRoomLinkFor(venueId, roomId, deviceId, String(device!.controllerRoomLink));
    });
  });
  onMount(() => {
    // A closing page ends its link at once rather than leaving the controller to find it dead 30 s later.
    const pageHidden = (): void => { roomLinkKey = ''; closeRoomLink(); };
    window.addEventListener('pagehide', pageHidden);
    onCleanup(() => window.removeEventListener('pagehide', pageHidden));
  });

  // A warmed direct link becomes authoritative only on a real cloud-disconnect edge. Fetch once on that
  // edge; when cloud returns its already-open mirror automatically becomes preferred again.
  let previousTransport: ControlTransport = 'none';
  createEffect(() => {
    const next = controlTransport();
    if (next !== previousTransport && (next === 'lan' || next === 'bluetooth')) {
      void (next === 'lan' ? refreshLan() : refreshBluetooth());
    }
    previousTransport = next;
  });

  createEffect(() => {
    if ((!user() && !localOrigin) || cloudReachable() || lanClient.connected || ['connected', 'connecting', 'reconnecting'].includes(bluetoothState()) || !lanAddress() || !lanClient.hasStoredPairing(lanAddress())) return;
    void connectLan(lanAddress()).catch(() => undefined);
  });

  onCleanup(() => {
    resolveTakeoverConfirmation(false);
    releaseControllerState();
    clearSubscriptions();
    closeRoomLink();
    roomLinkMeter.stop();
    connectivityUnsubscribe?.();
    gestureSlots.forEach((slot) => {
      if (slot.timer != null) globalThis.clearTimeout(slot.timer);
      // A drag that never released — a lost pointer, a tab closing mid-gesture — leaves its slot in the
      // room. The Pi drops it two seconds later on its own `expiresAt`, and this takes it out now, so a
      // console going away does not leave the room holding somebody's last finger position at all.
      clearGestureIntent(slot);
    });
    lanClient.disconnect('page closed');
    void bluetoothClient.disconnect();
  });

  const base: Omit<VenueContextValue, 'outputView'> = {
    configured: firebaseConfigured || localOrigin,
    localOrigin,
    authReady,
    user,
    signedIn,
    awaitingVerification,
    authBusy,
    authError,
    venues,
    rooms,
    selectedVenue,
    selectedRoom,
    selectedRigId,
    recordRigSelected,
    rigUnassigned,
    piLineSelected,
    microRecordWrites,
    boundDevice,
    selectedController,
    selectedDeviceId,
    role,
    canControl,
    controlAvailable,
    controlsEnabled,
    cloudReachable,
    controlTransport,
    activeSource,
    activeSourceLabel,
    controlOwnership,
    takeoverConfirmation,
    confirmTakeover,
    resolveTakeoverConfirmation,
    activity,
    controlFeedback,
    cloudBusy,
    cloudError,
    clearCloudError: () => setCloudError(''),
    roomStatus,
    cloudRig,
    rigRecordState,
    rigAppliedRevision,
    effectRecord,
    writeEffectRecord,
    writeRigRecord,
    rigReloadRevision,
    cloudScores,
    roomDoc,
    effectPresets,
    autoVJLibrary,
    effectDocuments,
    settingsTreeView,
    deviceDoc,
    controllerHealthLive: healthLive,
    remoteSession,
    mirror,
    bluetoothClient,
    bluetoothState,
    bluetoothReason,
    bluetoothIdentity,
    lanClient,
    lanAddress,
    lanState,
    lanReason,
    lanHello,
    lanStatus,
    rig,
    placementTargets,
    roomLayout,
    roomModel,
    roomLayoutLoading,
    roomLayoutWarning,
    effectLayout,
    effectLayoutStatus,
    effectLayoutDetail,
    staff,
    controllerState,
    panelBaselineComplete,
    transport,
    scores,
    scoreAssignments,
    scenes: visibleScenes,
    sceneRecords: visibleSceneRecords,
    loadSceneRecord,
    audioInputs,
    roomMusicInput,
    roomMusicListening,
    selectedAudioInputIdentity,
    selectAudioInput,
    audioStatus,
    actionBusy,
    toasts,
    route,
    setRoute,
    signInEmail,
    signInGoogle,
    createAccount,
    resendVerification,
    sendPasswordReset,
    clearAuthError,
    signOut,
    selectVenue,
    selectRoom,
    selectController,
    refreshCloud,
    reloadRoomRig,
    refreshLan,
    refreshController,
    connectLan,
    pairLan,
    disconnectLan,
    connectBluetooth,
    disconnectBluetooth,
    controllerCall,
    controllerRead,
    controllerGesture,
    sendPanelEdit,
    sendPanelGesture,
    sendBlackout,
    cloudCall,
    renameDevice,
    deleteScore,
    runAction,
    setMaxIntensity,
    setRemoteLivePolicy,
    listVenueMembers,
    listVenueInvites,
    inviteVenueMember,
    cancelVenueInvite,
    saveEffectPreset,
    applyEffectPreset,
    renameEffectPreset,
    deleteEffectPreset,
    setMemberRole,
    removeVenueMember,
    endRemoteLive,
    takeControl,
    dismissToast,
    roomLink,
    roomLinkState,
  };
  // Created once, in the provider's scope: its cleanup runs only when the console itself goes away.
  const value: VenueContextValue = { ...base, outputView: createOutputViewSession(base) };

  return <VenueContext.Provider value={value}>{props.children}</VenueContext.Provider>;
}

export function useVenue(): VenueContextValue {
  const context = useContext(VenueContext);
  if (!context) throw new Error('useVenue must be used inside VenueProvider.');
  return context;
}
